/**
 * 魔都市政府 — 公式ウェブサイト (ファン向け特別ページ)
 * Demon City Government Official Website
 */
import React, { useState, useEffect, useRef, useContext, createContext } from "react";
import { IMAGES } from "@/assets/images";
import { supabase } from "@/integrations/supabase/client";

/* ─────────────────────────────────────────
   擬似ブラウザクロムのラッパー
───────────────────────────────────────── */
/* ② ページごとのURL・タブタイトルテーブル */
const PAGE_URL_MAP: Record<number, { url: string; tab: string }> = {
  0: { url: "www.demoncity.go.jp/pest_control/world-designated/kuzuneko",      tab: "害獣対策 | 魔都市政府" },
  1: { url: "www.demoncity.go.jp/hazard/dragon-report/2226",                   tab: "龍族特集 | 魔都市政府" },
  2: { url: "www.demoncity.go.jp/field-report/investigation/collapsed-zones",  tab: "現地取材 | 魔都市政府" },
  3: { url: "www.demoncity.go.jp/alert/battleship/feline-class-vessel",         tab: "戦艦注意報 | 魔都市政府" },
  4: { url: "www.demoncity.go.jp/protected-species/squid-woman/registry",       tab: "保護生物 | 魔都市政府" },
  5: { url: "www.demoncity.go.jp/citizens/portal/services",                    tab: "住民サービス | 魔都市政府" },
  6: { url: "www.demoncity.go.jp/contact/city-hall",                           tab: "お問い合わせ | 魔都市政府" },
};

function BrowserChrome({ activePage, children }: { activePage: number; children: React.ReactNode }) {
  const pageInfo = PAGE_URL_MAP[activePage] ?? PAGE_URL_MAP[0];
  return (
    <div className="min-h-screen bg-[#1e1e1e] flex flex-col">
      {/* タブバー */}
      <div className="bg-[#2b2b2b] px-3 pt-2 flex items-end gap-1 border-b border-[#111]">

        {/* タブ①：魔都市政府（アクティブ） */}
        <div className="flex items-center gap-2 bg-[#f0f0f0] text-[#222] text-xs px-3 py-1.5 rounded-t-md min-w-[120px] max-w-[240px] shrink-0">
          <svg className="w-3 h-3 shrink-0 text-[#1a3a5c]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>
          </svg>
          <span className="truncate font-medium">{pageInfo.tab}</span>
        </div>

        {/* タブ②：住民掲示板 */}
        <a
          href="#/mato-bbs"
          className="flex items-center gap-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#bbb] hover:text-white text-xs px-3 py-1.5 rounded-t-md transition-colors duration-150 min-w-[100px] max-w-[200px] shrink-0"
          title="魔都市住民電子掲示板へ"
        >
          <span className="w-3 h-3 shrink-0 rounded-sm bg-white/20 flex items-center justify-center text-[8px] font-black leading-none text-white">📋</span>
          <span className="truncate">住民掲示板</span>
        </a>

        {/* タブ③：P.STUDIO へ戻る */}
        <a
          href="#/"
          className="flex items-center gap-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#bbb] hover:text-white text-xs px-3 py-1.5 rounded-t-md transition-colors duration-150 min-w-[100px] max-w-[180px] shrink-0"
          title="P.STUDIO メインサイトへ戻る"
        >
          {/* P.STUDIOのファビコン風アイコン */}
          <span className="w-3 h-3 shrink-0 rounded-sm bg-white/20 flex items-center justify-center text-[8px] font-black leading-none text-white">P</span>
          <span className="truncate">P.STUDIO</span>
          <svg className="w-2.5 h-2.5 shrink-0 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
        </a>

        {/* タブ追加ボタン風ダミー */}
        <button className="w-6 h-6 flex items-center justify-center text-[#777] hover:text-[#ccc] text-base rounded mb-0.5 transition-colors" disabled>
          +
        </button>
      </div>
      {/* アドレスバー */}
      <div className="bg-[#2b2b2b] px-3 py-1.5 flex items-center gap-2 border-b border-[#111]">
        <div className="flex gap-1">
          <button className="w-6 h-6 flex items-center justify-center text-[#999] hover:text-white rounded">‹</button>
          <button className="w-6 h-6 flex items-center justify-center text-[#999] hover:text-white rounded">›</button>
          <button className="w-6 h-6 flex items-center justify-center text-[#999] hover:text-white rounded">↻</button>
        </div>
        <div className="flex-1 bg-[#1a1a1a] border border-[#444] rounded-full px-4 py-1 flex items-center gap-2 overflow-hidden">
          <svg className="w-3 h-3 text-green-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span className="text-[#ccc] text-xs font-mono truncate">{pageInfo.url}</span>
        </div>
        <div className="flex gap-1">
          <button className="w-6 h-6 flex items-center justify-center text-[#999] hover:text-white rounded">☆</button>
          <button className="w-6 h-6 flex items-center justify-center text-[#999] hover:text-white rounded">⋮</button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-[#e8e8e8]">
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   ライトボックス（全画像共通）
───────────────────────────────────────── */
interface LightboxState { src: string; alt: string }

const LightboxContext = createContext<(s: LightboxState | null) => void>(() => {});

function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [item, setItem] = useState<LightboxState | null>(null);
  const [secretHint, setSecretHint] = useState(false);
  const [secretClicks, setSecretClicks] = useState(0);

  // ESCキーで閉じる
  useEffect(() => {
    if (!item) return;
    setSecretHint(false);
    setSecretClicks(0);
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setItem(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [item]);

  // CAND-003専用：寝ている部分クリックでヒント→SecretDoor
  const isCAND003 = item?.alt?.includes("CAND-003") ?? false;

  function handleSecretZone() {
    const next = secretClicks + 1;
    setSecretClicks(next);
    if (next === 1) { setSecretHint(true); }
    if (next >= 2) { window.location.hash = "/secret-door"; setItem(null); }
  }

  return (
    <LightboxContext.Provider value={setItem}>
      {children}
      {item && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.92)" }}
          onClick={() => setItem(null)}
        >
          {/* 閉じるボタン */}
          <button
            onClick={() => setItem(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
            style={{ lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
          {/* 画像コンテナ（CAND-003は隠しゾーン付き） */}
          <div className="relative" onClick={e => e.stopPropagation()}>
            <img
              src={item.src}
              alt={item.alt}
              style={{
                maxWidth: "92vw",
                maxHeight: "88vh",
                objectFit: "contain",
                borderRadius: "6px",
                boxShadow: "0 8px 60px rgba(0,0,0,0.8)",
                display: "block",
              }}
            />
            {/* CAND-003専用：左下に隠しゾーン */}
            {isCAND003 && (
              <div
                onClick={handleSecretZone}
                className="absolute cursor-default"
                title=""
                style={{
                  bottom: "12%",
                  left: "10%",
                  width: "35%",
                  height: "25%",
                  zIndex: 10,
                }}
              >
                {/* ヒント表示（1回クリック後） */}
                {secretHint && (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ pointerEvents: "none" }}
                  >
                    <span
                      className="font-mono text-white/70 text-[11px] tracking-widest animate-pulse bg-black/60 px-2 py-1 rounded"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      …もう一度
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* キャプション */}
          {item.alt && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs text-center px-4">
              {item.alt}
            </div>
          )}
          {/* ESCヒント */}
          <div className="absolute top-4 left-4 text-white/30 text-[10px] hidden md:block">
            ESC / click outside to close
          </div>
        </div>
      )}
    </LightboxContext.Provider>
  );
}

/** 画像要素に付与するクリックハンドラを返すフック */
function useZoom() {
  const open = useContext(LightboxContext);
  return (src: string, alt = "") => open({ src, alt });
}


/* ─── FeedbackPopup ─── */
type FBRating = "great" | "ok" | "bad";
interface FBMsg { ja: string; en: string; }

// 訪問回数管理
const FB_VISIT_KEY = "mato_fb_visit_count";
function getFbVisitCount(): number {
  try { return parseInt(localStorage.getItem(FB_VISIT_KEY) || "0", 10) || 0; } catch { return 0; }
}
function incFbVisitCount(): number {
  try {
    const n = getFbVisitCount() + 1;
    localStorage.setItem(FB_VISIT_KEY, String(n));
    return n;
  } catch { return 1; }
}

// ── 回数別メッセージプール ──
// great: 1回目（初来訪）
const FB_MSGS_GREAT_1: FBMsg[] = [
  { ja: "ようこそ魔都市へ！あなたのような方をお待ちしておりました🐾", en: "Welcome to the Demon City! We have been waiting for someone like you." },
  { ja: "虎の姫の祝福を！初訪問に感謝します。", en: "May the Tiger Princess bless you! Thank you for your first visit." },
  { ja: "あなたはどんな動物になりたいですか？転生受付中です。", en: "What kind of animal would you like to become? Reincarnation services now open." },
  { ja: "魔都市政府公認ファン認定！猫族保護にご協力感謝です。", en: "Officially recognized as a Demon City fan! Thank you for supporting cat-kin protection." },
  { ja: "猫耳転生手続きを今すぐ開始しますか？", en: "Shall we begin your cat-ear reincarnation procedure right now?" },
];
// great: 2回目（リピーター）
const FB_MSGS_GREAT_2: FBMsg[] = [
  { ja: "2度目のご来訪ありがとうございます。あなたはもう魔都市民です。", en: "Thank you for your second visit. You are now effectively a Demon City resident." },
  { ja: "戻ってきてくださいましたね。くずねこも歓迎してると思います（たぶん）。", en: "You've returned. Kuzuneko also welcomes you back. (Probably.)" },
  { ja: "再訪を確認。永住権申請フォームをご準備しています🏰", en: "Return visit confirmed. Your permanent residency application form is being prepared." },
  { ja: "この魔都市の観光大使に任命します！次はぜひ移住を🐱", en: "You are hereby appointed Tourism Ambassador of the Demon City! Next step: relocation." },
];
// great: 3回目以上（常連）
const FB_MSGS_GREAT_3P: FBMsg[] = [
  { ja: "また来た！もう魔都市籍を取得したも同然です。", en: "Back again! At this point, you might as well have Demon City citizenship." },
  { ja: "何度でもどうぞ。くずねこはまだ逃げ足が速いので捕まえるのは無理ですが。", en: "Come as many times as you like. Though Kuzuneko is still too fast to catch." },
  { ja: "火邑 凛 市長より最高位の市民章「黄金の猫足章」を授与！常連認定🎖", en: "Mayor Rin Himura awards you the Golden Cat Paw Medal! Certified regular visitor." },
  { ja: "ネコマタ財団より感謝状と猫缶3個を授与します。リピーター特典です🐾", en: "The Nekomata Foundation awards you a certificate and 3 cans of cat food. Repeat visitor bonus." },
  { ja: "毎回来てくれてありがとう。魔都市政府もじわじわ嬉しいです（公式コメント）。", en: "Thank you for coming every time. The Demon City Government is quietly delighted. (Official comment.)" },
];

// ok: 1回目
const FB_MSGS_OK_1: FBMsg[] = [
  { ja: "貴重なご意見として記録いたしました。", en: "Your feedback has been duly recorded in the city archives." },
  { ja: "ふつうを求めてもふつうってなんでしょうね？", en: "You seek ordinary — but what even is ordinary?" },
  { ja: "普通の人間でいることも立派な才能です（たぶん）。", en: "Being an ordinary human is a talent in itself. (Probably.)" },
  { ja: "了解しました。市議会が緊急招集されます。", en: "Noted. The city council has been convened immediately." },
];
// ok: 2回目
const FB_MSGS_OK_2: FBMsg[] = [
  { ja: "2回目もふつうですか。根気のある市民ですね。", en: "Ordinary again the second time? You are a persistent citizen." },
  { ja: "あなたならきっとそうすると思いました（2度目）。", en: "Somehow, we knew you would say that again." },
  { ja: "くずねこもふだんそんな感じです。共感します。", en: "Kuzuneko feels the same way most days. Relatable." },
  { ja: "魔都市政府はあなたの「ふつう」を尊重します（してません）。", en: "The Demon City Government respects your ordinariness. (It does not.)" },
];
// ok: 3回目以上
const FB_MSGS_OK_3P: FBMsg[] = [
  { ja: "何回来てもふつう。安定感があります。これはこれで才能かもしれない。", en: "Ordinary every time. There's a consistency to it. That might be a talent in itself." },
  { ja: "半歩踏み込んだご意見、今回も確かに受け取りました。", en: "Your half-step of engagement has been duly noted once again." },
  { ja: "いつか気が向いたらまたどうぞ。猫族はいつでも待っています🐾（待ってない）", en: "Come back anytime you feel like it. The cat-kin are always waiting. (They are not.)" },
  { ja: "転生の際は猫族を選択してね🐱 何度もお伝えしています。", en: "When you reincarnate, please choose the cat-kin. We've said this before." },
];

// bad: 1回目
const FB_MSGS_BAD_1: FBMsg[] = [
  { ja: "虎の姫の天誅を申請しました。雷撃をしばしお待ちください⚡", en: "Divine punishment from the Tiger Princess has been requested. Please await lightning." },
  { ja: "みじんこに転生なさい。", en: "We recommend reincarnating as plankton." },
  { ja: "くずねこのエサ決定。", en: "You have been designated as Kuzuneko's next meal." },
  { ja: "龍に食われてしまえ🐉", en: "May you be devoured by the dragon." },
  { ja: "あなたの意見は市役所地下の文書処分炉に送付されます🔥", en: "Your feedback is being forwarded to the city hall basement incinerator." },
];
// bad: 2回目
const FB_MSGS_BAD_2: FBMsg[] = [
  { ja: "またですか。今すぐ魔都特務部隊が直行します🐯", en: "Again? Demon City Special Forces are dispatching immediately." },
  { ja: "2度目の「要らない」を確認。残念です。でも熊人部隊が間もなく参ります。", en: "Second 'No Thanks' confirmed. A pity. A bear-man squad will visit shortly." },
  { ja: "龍族より「今回も同意する」との連絡を受けました🐉", en: "The Dragon Clan reports: We agree again this time." },
  { ja: "ご意見はくずねこにお伝えします。彼女は今回も気にしません🐾", en: "We will inform Kuzuneko again. She still won't care." },
];
// bad: 3回目以上
const FB_MSGS_BAD_3P: FBMsg[] = [
  { ja: "何度来ても「要らない」。これはもう信念です。尊重します。", en: "'No Thanks' every single time. At this point it's a philosophy. We respect it." },
  { ja: "あなたのIPアドレスは虎の姫に再共有されました（${n}回目）。", en: "Your IP address has been shared with the Tiger Princess again. ({n}th time.)" },
  { ja: "魔都市に不要と${n}回判定されました。来てくれてありがとう🐱", en: "Deemed unnecessary for the Demon City {n} times now. But thanks for keep coming." },
  { ja: "現世に帰ってどうぞ。何度言っても戻ってくるのはなぜですか。", en: "Please return to the mortal world. And yet you keep coming back. Why." },
];

// 訪問回数に応じてプールを選択
function getFbMsgs(r: FBRating, visitCount: number): FBMsg[] {
  if (r === "great") {
    if (visitCount <= 1) return FB_MSGS_GREAT_1;
    if (visitCount === 2) return FB_MSGS_GREAT_2;
    return FB_MSGS_GREAT_3P;
  }
  if (r === "ok") {
    if (visitCount <= 1) return FB_MSGS_OK_1;
    if (visitCount === 2) return FB_MSGS_OK_2;
    return FB_MSGS_OK_3P;
  }
  // bad
  if (visitCount <= 1) return FB_MSGS_BAD_1;
  if (visitCount === 2) return FB_MSGS_BAD_2;
  return FB_MSGS_BAD_3P;
}

// 訪問回数ラベル
function visitLabel(n: number): { ja: string; en: string } {
  if (n === 1) return { ja: "初来訪", en: "1st Visit" };
  if (n === 2) return { ja: "2回目の訪問", en: "2nd Visit" };
  if (n === 3) return { ja: "3回目の訪問", en: "3rd Visit" };
  return { ja: `${n}回目の訪問`, en: `${n}th Visit` };
}

// 訪問回数別 質問文
function visitQuestion(n: number): { ja: string; en: string } {
  if (n === 1) return {
    ja: "この魔都市の世界観はいかがでしたか？",
    en: "How do you like the world of Demon City?"
  };
  if (n === 2) return {
    ja: "2度目のご来訪ありがとうございます。今回はいかがでしたか？",
    en: "Thank you for your second visit. How was it this time?"
  };
  if (n === 3) return {
    ja: "3回目。まだ懲りていないようですね。評価はいかがですか？",
    en: "Third time. You still haven't learned. What's your verdict?"
  };
  return {
    ja: `${n}回目の訪問を確認。もうお気に入りですか？`,
    en: `Visit #${n} confirmed. Is this becoming a habit?`
  };
}

// 訪問回数別 ボタンラベル
const FB_LABELS_BY_VISIT = [
  // 1回目
  { great: { ja: "素晴らしい", en: "Wonderful" }, ok: { ja: "ふつう", en: "Just OK" }, bad: { ja: "要らない", en: "No Thanks" } },
  // 2回目
  { great: { ja: "今回も最高", en: "Great Again" }, ok: { ja: "まあまあ", en: "So-so" }, bad: { ja: "やっぱり不要", en: "Still No" } },
  // 3回目以上
  { great: { ja: "もちろん最高", en: "Obviously Great" }, ok: { ja: "相変わらず", en: "As Expected" }, bad: { ja: "何度でも要らない", en: "Still No, Forever" } },
];
function getFbLabels(visitCount: number) {
  if (visitCount <= 1) return FB_LABELS_BY_VISIT[0];
  if (visitCount === 2) return FB_LABELS_BY_VISIT[1];
  return FB_LABELS_BY_VISIT[2];
}

const FB_ICONS: Record<FBRating | "question" | "result_great" | "result_ok" | "result_bad", string> = {
  great:        "/images/fb_icon_great.png",
  ok:           "/images/fb_icon_ok.png",
  bad:          "/images/fb_icon_bad.png",
  question:     "/images/fb_icon_question.png",
  result_great: "/images/fb_result_great.png",
  result_ok:    "/images/fb_result_ok.png",
  result_bad:   "/images/fb_result_bad.png",
};

function FeedbackPopup() {
  const [shown, setShown] = React.useState(false);
  const [rating, setRating] = React.useState<FBRating | null>(null);
  const [msg, setMsg] = React.useState<FBMsg | null>(null);
  const [visitCount, setVisitCount] = React.useState(0);

  React.useEffect(() => {
    const t = setTimeout(() => {
      const n = incFbVisitCount();
      setVisitCount(n);
      setShown(true);
    }, 10000);
    return () => clearTimeout(t);
  }, []);

  const pick = (r: FBRating) => {
    const pool = getFbMsgs(r, visitCount);
    const raw = pool[Math.floor(Math.random() * pool.length)];
    // {n} プレースホルダーを実際の回数に置換
    const resolved: FBMsg = {
      ja: raw.ja.replace(/\{n\}/g, String(visitCount)),
      en: raw.en.replace(/\{n\}/g, String(visitCount)),
    };
    setRating(r);
    setMsg(resolved);
  };

  const vLabel = visitLabel(visitCount);
  const vQuestion = visitQuestion(visitCount);
  const fbLabels = getFbLabels(visitCount);

  if (!shown) return null;
  return (
    <div className={"fixed bottom-4 right-4 z-[9999] w-[92vw] max-w-[360px] md:max-w-[600px] bg-[#060e1f] border-2 border-[#1a3a5c] rounded-2xl shadow-2xl overflow-hidden"}
         style={{ animation: "slideInUp 0.4s ease" }}>
      {/* Header */}
      <div className="bg-[#0d1f3c] px-4 py-2 md:px-6 md:py-3 flex justify-between items-center border-b border-[#1a3a5c]">
        <div className="flex items-center gap-2">
          <img src={FB_ICONS.question} alt="kuzuneko" className="w-10 h-10 md:w-14 md:h-14 object-cover rounded-full" />
          <div>
            <div className="text-white font-bold text-sm md:text-lg">意見フィードバック</div>
            <div className="text-white/50 text-[10px] md:text-xs">
              {vLabel.en} — World Survey / Demon City Gov.
            </div>
          </div>
        </div>
        <button onClick={() => setShown(false)} className="text-white/40 hover:text-white text-xl md:text-2xl leading-none">×</button>
      </div>
      {/* Before rating */}
      {!rating && (
        <div className="p-4 md:p-6">
          {visitCount >= 2 && (
            <div className="text-[10px] md:text-xs text-[#4a8cdc] font-bold mb-2 md:mb-3 flex items-center gap-1">
              <span>🔁</span>
              <span>{vLabel.ja} / {vLabel.en}</span>
            </div>
          )}
          <p className="text-white/70 text-xs md:text-base mb-4 md:mb-6 leading-relaxed">
            {vQuestion.ja}<br />
            <span className="text-white/40 text-[10px] md:text-xs">{vQuestion.en}</span>
          </p>
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            {(["great", "ok", "bad"] as FBRating[]).map((r) => (
              <button key={r} onClick={() => pick(r)}
                className="flex flex-col items-center gap-1 md:gap-2 bg-[#0d1f3c] hover:bg-[#1a3a5c] border border-[#1a3a5c] hover:border-[#4a8cdc] rounded-xl py-3 md:py-5 px-1 transition-all">
                <img src={FB_ICONS[r]} alt={r} className="w-14 h-14 md:w-24 md:h-24 object-cover rounded-lg" />
                <span className="text-white text-xs md:text-sm font-bold">{fbLabels[r].ja}</span>
                <span className="text-white/40 text-[9px] md:text-xs">{fbLabels[r].en}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* After rating */}
      {rating && msg && (
        <div className="p-4 md:p-6 flex flex-col items-center text-center gap-3 md:gap-5">
          <img src={FB_ICONS[("result_" + rating) as keyof typeof FB_ICONS]} alt="response" className="w-24 h-24 md:w-40 md:h-40 object-contain rounded-2xl border-2 border-[#1a3a5c] bg-[#0d1f3c]" />
          <div>
            <p className="text-white font-bold text-sm md:text-xl leading-snug mb-1 md:mb-2">{msg.ja}</p>
            <p className="text-white/60 text-xs md:text-base leading-snug">{msg.en}</p>
          </div>
          {visitCount >= 3 && (
            <p className="text-[#4a8cdc]/60 text-[9px] md:text-xs">
              ({vLabel.ja}のご回答 / Response for {vLabel.en})
            </p>
          )}
          <p className="text-white/30 text-[9px] md:text-xs">（ジョークです / This is a joke）</p>
          <button onClick={() => setShown(false)}
            className="mt-1 bg-[#1a3a5c] hover:bg-[#2a4a6c] text-white text-xs md:text-sm px-6 py-2 md:px-8 md:py-3 rounded-lg transition-colors">
            閉じる / Close
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   紋章SVG
───────────────────────────────────────── */
function CityEmblem({ size = 40, className = "" }: { size?: number; className?: string }) {
  // className は後方互換のため受け取るが使用しない（画像に差し替え済み）
  void className;
  return (
    <img
      src={IMAGES.CITY_EMBLEM_NEW}
      alt="Demon City Emblem"
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  );
}

/* ─────────────────────────────────────────
   セクションヘッダー補助
───────────────────────────────────────── */
function SectionBar({ color, accent, en, ja }: { color: string; accent: string; en: string; ja: string }) {
  return (
    <div className={`${color} text-white text-xs font-bold px-3 py-2 flex items-center gap-2`}>
      <span className={`w-1 h-4 ${accent} inline-block shrink-0`} />
      <span>{en}</span>
      <span className="text-white/50 font-normal">／</span>
      <span className="font-normal text-white/80">{ja}</span>
    </div>
  );
}

/* ─────────────────────────────────────────
   共通ヘッダー
───────────────────────────────────────── */
const NAV_ITEMS = [
  { ja:"住民サービス", en:"Citizens' Portal", page: 5 },
  { ja:"害獣対策",   en:"Pest Control",     page: 0 },
  { ja:"龍族特集",   en:"Dragon Report",    page: 1 },
  { ja:"現地取材",   en:"Field Report",     page: 2 },
  { ja:"戦艦警報",   en:"Battleship Alert", page: 3 },
  { ja:"保護生物",   en:"Protected Species",page: 4 },
];

// 訪問済みページ番号を Set で管理（localStorage に JSON 配列で保存）
const STORAGE_KEY = "mato_nav_visited_v2";

function loadVisited(): Set<number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set<number>(JSON.parse(raw) as number[]);
  } catch {/* noop */}
  return new Set<number>();
}

function saveVisited(s: Set<number>) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...s])); } catch {/* noop */}
}

// バッジを表示すべきページ番号一覧（ナビタブ6つ + コンタクト page:6）
const BADGE_PAGES = new Set([0, 1, 2, 3, 4, 5, 6]);


/* ==========================================================
   KuzunekoStatusWidget
   ★ 削除するときはこのブロックごと削除してください（開始〜END まで）
   挿入箇所: SiteHeader 内、ロゴ行とナビ行の間に <KuzunekoStatusWidget /> を置いています
   ========================================================== */
type KDanger = "LOW" | "MED" | "HIGH" | "EXTREME";
type KSlot = {
  location: string; locationEn: string;
  danger: KDanger;
  states: Array<{ ja: string; en: string; icon: string }>;
};
const K_SLOTS: Array<{ h0: number; h1: number; slot: KSlot }> = [
  { h0:  0, h1:  5, slot: { location: "川口駅前・自販機の上",         locationEn: "Kawaguchi Sta. — Top of Vending Machine",         danger: "LOW",     states: [{ ja:"睡眠中",       en:"SLEEPING",           icon:"💤" },{ ja:"うとうと中",   en:"DOZING",             icon:"😪" },{ ja:"大の字で熟睡", en:"FAST ASLEEP",        icon:"🛌" },{ ja:"自販機を枕に", en:"USING MACHINE AS PILLOW", icon:"💤" }] } },
  { h0:  5, h1:  7, slot: { location: "不明",                           locationEn: "UNKNOWN",                                        danger: "LOW",     states: [{ ja:"早朝徘徊中",   en:"ROAMING AT DAWN",    icon:"🌅" },{ ja:"路地を散歩中", en:"STROLLING ALLEYS",   icon:"🚶" },{ ja:"朝焼けを鑑賞", en:"WATCHING SUNRISE",   icon:"🌄" }] } },
  { h0:  7, h1: 10, slot: { location: "川口市内・複数目撃情報あり",   locationEn: "Multiple sightings across Kawaguchi",            danger: "HIGH",    states: [{ ja:"逃走中",       en:"EVADING CAPTURE",    icon:"🏃" },{ ja:"追跡を撒き中", en:"LOSING PURSUERS",    icon:"💨" },{ ja:"電線を疾走中", en:"SPRINTING ON WIRES", icon:"⚡" },{ ja:"屋根伝いに移動", en:"ROOFTOP HOPPING",  icon:"🏃" }] } },
  { h0: 10, h1: 12, slot: { location: "川口駅前・商店街",               locationEn: "Kawaguchi Sta. Shopping District",               danger: "MED",     states: [{ ja:"いたずら中",   en:"CAUSING MISCHIEF",   icon:"😈" },{ ja:"自販機を破壊", en:"SMASHING MACHINES",  icon:"💥" },{ ja:"看板を曲げ中", en:"BENDING SIGNBOARDS", icon:"🔧" },{ ja:"ビラを剥がし中", en:"TEARING DOWN POSTERS", icon:"😈" }] } },
  { h0: 12, h1: 14, slot: { location: "不明（昼食か？）",               locationEn: "UNKNOWN — possibly eating",                      danger: "MED",     states: [{ ja:"行方不明",     en:"WHEREABOUTS UNKNOWN",icon:"❓" },{ ja:"食事中の可能性", en:"POSSIBLY EATING",  icon:"🍱" },{ ja:"ひそんでいる", en:"IN HIDING",          icon:"🙈" }] } },
  { h0: 14, h1: 17, slot: { location: "不明",                           locationEn: "UNKNOWN",                                        danger: "HIGH",    states: [{ ja:"逃走中",       en:"EVADING CAPTURE",    icon:"🏃" },{ ja:"市内を疾走中", en:"SPRINTING THROUGH CITY", icon:"💨" },{ ja:"ビルを駆け下り", en:"DESCENDING BUILDINGS", icon:"🏢" },{ ja:"目撃者から逃走", en:"FLEEING WITNESSES", icon:"🏃" }] } },
  { h0: 17, h1: 19, slot: { location: "雑居ビル屋上・川口市内",         locationEn: "Rooftop of Mixed-Use Building, Kawaguchi",       danger: "MED",     states: [{ ja:"いたずら中",   en:"CAUSING MISCHIEF",   icon:"😈" },{ ja:"夕焼けを眺め中", en:"WATCHING SUNSET",  icon:"🌇" },{ ja:"電柱によじ登り", en:"CLIMBING UTILITY POLE", icon:"😈" },{ ja:"洗濯物を散らか中", en:"SCATTERING LAUNDRY", icon:"😹" }] } },
  { h0: 19, h1: 22, slot: { location: "川口駅前・繁華街",               locationEn: "Kawaguchi Sta. Entertainment District",          danger: "EXTREME", states: [{ ja:"爆発物設置中", en:"PLANTING EXPLOSIVES",icon:"💥" },{ ja:"ネオンを破壊中", en:"SMASHING NEON SIGNS", icon:"💥" },{ ja:"路地で爆発を起こす", en:"DETONATING IN ALLEYS", icon:"🔥" },{ ja:"ビルに衝突中", en:"RAMMING INTO BUILDINGS", icon:"💥" }] } },
  { h0: 22, h1: 24, slot: { location: "電柱の上・川口駅付近",           locationEn: "Top of Utility Pole, near Kawaguchi Sta.",       danger: "LOW",     states: [{ ja:"睡眠中",       en:"SLEEPING",           icon:"💤" },{ ja:"うずくまっている", en:"CROUCHING",        icon:"🐱" },{ ja:"星を眺め中",   en:"STARGAZING",         icon:"🌟" }] } },
];
const K_DANGER_STYLE: Record<KDanger, { bg: string; badge: string; label: string; pulse: boolean }> = {
  LOW:     { bg: "#1b5e20", badge: "#4caf50", label: "LOW",     pulse: false },
  MED:     { bg: "#b85c00", badge: "#ffb300", label: "MED",     pulse: false },
  HIGH:    { bg: "#b71c1c", badge: "#ff5252", label: "HIGH",    pulse: true  },
  EXTREME: { bg: "#4a0000", badge: "#ff1744", label: "EXTREME", pulse: true  },
};

function KuzunekoStatusWidget() {
  const [tick, setTick] = useState(0);
  const [stateIdx, setStateIdx] = useState<number>(-1);
  const [prevHour, setPrevHour] = useState<number>(-1);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const now  = new Date();
  const h    = now.getHours();
  const mm   = String(now.getMinutes()).padStart(2, "0");
  const ss   = String(now.getSeconds()).padStart(2, "0");
  const futY = now.getFullYear() + 200;
  const timeStr = `${futY}/${String(now.getMonth()+1).padStart(2,"0")}/${String(now.getDate()).padStart(2,"0")} ${String(h).padStart(2,"0")}:${mm}:${ss}`;

  const entry = K_SLOTS.find(s => h >= s.h0 && h < s.h1) ?? K_SLOTS[K_SLOTS.length - 1];
  const { slot } = entry;
  const dStyle = K_DANGER_STYLE[slot.danger];

  // 時間帯が変わったらランダム再選択、初回も選択
  useEffect(() => {
    if (h !== prevHour || stateIdx === -1) {
      setStateIdx(Math.floor(Math.random() * slot.states.length));
      setPrevHour(h);
    }
  }, [h, prevHour, stateIdx, slot.states.length]);

  const st = stateIdx >= 0 ? slot.states[stateIdx] : slot.states[0];

  return (
    <div
      style={{ backgroundColor: dStyle.bg, transition: "background-color 1s ease" }}
      className="text-white text-[11px] px-3 py-1.5 flex items-center gap-2 flex-wrap"
    >
      {/* ラベル */}
      <span className="font-black shrink-0 flex items-center gap-1 tracking-widest">
        <span className={dStyle.pulse ? "animate-pulse" : ""}>🐾</span>
        KUZUNEKO TRACKER
      </span>
      <span className="opacity-25 shrink-0">｜</span>

      {/* 現在地 */}
      <span className="shrink-0">
        <span className="opacity-60">現在地:</span>
        <span className="font-bold ml-1">{slot.location}</span>
        <span className="opacity-50 ml-1 text-[10px]">/ {slot.locationEn}</span>
      </span>
      <span className="opacity-25 shrink-0">｜</span>

      {/* 状態 */}
      <span className="shrink-0">
        <span className="opacity-60">状態:</span>
        <span className="font-bold ml-1">{st.icon} {st.ja}</span>
        <span className="opacity-50 ml-1 text-[10px]">/ {st.en}</span>
      </span>
      <span className="opacity-25 shrink-0">｜</span>

      {/* 脅威度バッジ */}
      <span
        className={["shrink-0 font-black text-[10px] px-1.5 py-0.5 rounded", dStyle.pulse ? "animate-pulse" : ""].join(" ")}
        style={{ backgroundColor: dStyle.badge, color: "#000" }}
      >
        ⚠ DANGER: {dStyle.label}
      </span>
      <span className="opacity-25 shrink-0">｜</span>

      {/* 現在時刻 */}
      <span className="shrink-0 font-mono opacity-70 text-[10px]">
        {timeStr}
      </span>
    </div>
  );
}
/* ★ KuzunekoStatusWidget END */
function SiteHeader({ activePage, onPageChange }: { activePage: number; onPageChange: (n: number) => void }) {
  const [visited, setVisited] = useState<Set<number>>(loadVisited);
  // 初回パルス：マウント後3秒間だけ active クラスを付ける（CSS アニメのみ）
  const [pulseActive, setPulseActive] = useState(true);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    pulseTimer.current = setTimeout(() => setPulseActive(false), 3200);
    return () => { if (pulseTimer.current) clearTimeout(pulseTimer.current); };
  }, []);

  // タブを押したらそのページだけバッジを消す
  const handleNav = (page: number) => {
    onPageChange(page);
    if (BADGE_PAGES.has(page) && !visited.has(page)) {
      const next = new Set(visited);
      next.add(page);
      setVisited(next);
      saveVisited(next);
    }
  };

  // バッジを出すかどうか（未訪問かつ BADGE_PAGES に含まれる）
  const hasBadge = (page: number) => BADGE_PAGES.has(page) && !visited.has(page);

  return (
    <header>
      {/* 最上部バー */}
      <div className="bg-[#1a3a5c] text-white text-[11px] py-1 px-4">
        <span className="font-semibold tracking-wide">魔都市政府 / Demon City Government</span>
      </div>
      {/* ロゴ + 問い合わせボタン + 日付 */}
      <div className="bg-white border-b-2 border-[#1a3a5c] px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-14 h-14 rounded overflow-hidden shrink-0 flex items-center justify-center">
            <CityEmblem size={56} />
          </div>
          <div>
            <div className="text-xl font-black text-[#1a3a5c] leading-tight tracking-wide">魔都市役所公式サイト</div>
            <div className="text-xs text-[#555] tracking-wider">Demon City Hall — Official Website</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {/* ③ 今日の日付を +200年して表示 */}
          <span className="text-xs text-[#888]">{(() => {
            const now = new Date();
            const y = now.getFullYear() + 200;
            const m = now.getMonth() + 1;
            const d = now.getDate();
            const jaDay = ["日","月","火","水","木","金","土"][now.getDay()];
            const enDay = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][now.getDay()];
            const enMonth = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][now.getMonth()];
            return `${y}年${m}月${d}日（${jaDay}）/ ${enDay}. ${enMonth} ${d}, ${y}`;
          })()}</span>
          {/* 問い合わせボタン（検索バーの代わり） */}
          {/* 問い合わせボタン（コンタクトページへ）+ バッジ */}
          <div className="relative inline-block">
            <button
              onClick={() => handleNav(6)}
              className="flex items-center gap-2 bg-[#c00] hover:bg-[#a00] text-white font-black text-xs px-4 py-2 rounded shadow-md transition-colors">
              <span className="text-base leading-none">✉</span>
              <span className="leading-tight">お問い合わせ<br/><span className="font-normal text-[9px] opacity-80">Contact City Hall</span></span>
            </button>
            {hasBadge(6) && (
              <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-yellow-400 shadow-[0_0_6px_2px_rgba(250,204,21,0.8)] pointer-events-none"
              />
            )}
          </div>
        </div>
      </div>
      {/* ★ KuzunekoStatusWidget — 削除するときはこの1行だけ消してください */}
      <KuzunekoStatusWidget />
      {/* メインナビ — スマホ: wrap で2行、PC: 1列 */}
      <div className="bg-[#1a3a5c] text-sm">
        <div className="flex flex-wrap">
        {NAV_ITEMS.map((item, i) => {
          const isActive = activePage === item.page;
          /* パルス遅延：タブごとに 0.15s ずつずらして順番に光る */
          const delayStyle = pulseActive && !isActive
            ? { animationDelay: `${i * 0.15}s` } as React.CSSProperties
            : undefined;
          return (
            <button key={i}
              onClick={() => handleNav(item.page)}
              style={delayStyle}
              className={[
                "relative px-5 py-2.5 text-xs font-semibold transition-colors whitespace-nowrap border-r border-white/10 last:border-0",
                isActive ? "bg-[#c00] text-white" : "text-white hover:bg-white/15",
                /* 初回パルス：GPU合成のみ（transform + opacity） */
                pulseActive && !isActive ? "animate-[navPulse_0.6s_ease-in-out_1]" : "",
              ].join(" ")}>
              {item.en}
              <span className="block text-[9px] font-normal opacity-70">{item.ja}</span>
              {/* バッジ：未訪問かつ非アクティブのタブのみ */}
              {hasBadge(item.page) && !isActive && (
                <span
                  aria-hidden="true"
                  className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-yellow-400 shadow-[0_0_4px_1px_rgba(250,204,21,0.7)] pointer-events-none"
                />
              )}
            </button>
          );
        })}
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────
   共通フッター
───────────────────────────────────────── */
function SiteFooter({ onPageChange }: { onPageChange: (n: number) => void }) {
  return (
    <footer className="bg-[#1a3a5c] text-white mt-4">
      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        {/* About the City — ダミー文書なのでリンクなし */}
        <div>
          <h5 className="font-bold text-white/90 mb-2 border-b border-white/20 pb-1 text-[11px]">About the City / 市政府について</h5>
          {["Mayor's Office / 市長室","Organization / 行政組織","Ordinances / 条例・規則","City History / 市の歴史"].map((item,j)=>(
            <span key={j} className="block text-white/40 py-0.5 text-[10px] cursor-default">{item}</span>
          ))}
        </div>
        {/* Citizen Services — 住民サービスページへ */}
        <div>
          <h5 className="font-bold text-white/90 mb-2 border-b border-white/20 pb-1 text-[11px]">Citizen Services / 住民サービス</h5>
          <button onClick={()=>onPageChange(5)} className="block text-white/70 hover:text-white py-0.5 transition-colors text-[10px] text-left w-full">Citizens' Portal / 住民サービスポータル →</button>
          <button onClick={()=>onPageChange(0)} className="block text-white/70 hover:text-white py-0.5 transition-colors text-[10px] text-left w-full">Pest Reporting / 害獣通報 →</button>
          <span className="block text-white/40 py-0.5 text-[10px] cursor-default">Welfare / 福祉・介護</span>
        </div>
        {/* Safety & Disaster — 各コーナーへ */}
        <div>
          <h5 className="font-bold text-white/90 mb-2 border-b border-white/20 pb-1 text-[11px]">Safety & Disaster / 安全・防災</h5>
          <button onClick={()=>onPageChange(0)} className="block text-white/70 hover:text-white py-0.5 transition-colors text-[10px] text-left w-full">Pest Alerts / 害獣警報 →</button>
          <button onClick={()=>onPageChange(1)} className="block text-white/70 hover:text-white py-0.5 transition-colors text-[10px] text-left w-full">Dragon Report / 龍族特集 →</button>
          <button onClick={()=>onPageChange(3)} className="block text-white/70 hover:text-white py-0.5 transition-colors text-[10px] text-left w-full">Battleship Alert / 戦艦警報 →</button>
          <button onClick={()=>onPageChange(2)} className="block text-white/70 hover:text-white py-0.5 transition-colors text-[10px] text-left w-full">Field Report / 現地取材 →</button>
        </div>
        {/* Related Orgs — ダミーなのでリンクなし */}
        <div>
          <h5 className="font-bold text-white/90 mb-2 border-b border-white/20 pb-1 text-[11px]">Related Orgs / 関連機関</h5>
          {["Beastman Foundation / 獣人財団","Demon City Police / 魔都警察","Pest Research / 害獣研究所","City Hospital / 市立病院"].map((item,j)=>(
            <span key={j} className="block text-white/40 py-0.5 text-[10px] cursor-default">{item}</span>
          ))}
        </div>
      </div>
      {/* フッター問い合わせボタン */}
      <div className="border-t border-white/20 px-4 pt-4 pb-2 flex justify-center">
        <button
          onClick={() => onPageChange(6)}
          className="flex items-center gap-2 bg-[#c00] hover:bg-[#a00] text-white font-black text-sm px-6 py-2.5 rounded-full shadow-lg transition-colors">
          <span className="text-base">✉</span>
          <span>お問い合わせ / Contact City Hall</span>
        </button>
      </div>
      <div className="px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-2 text-[10px] text-white/50">
        <div className="flex items-center gap-2">
          <CityEmblem size={22} />
          <span>© 2226 Demon City Government / 魔都市政府. All rights reserved.</span>
        </div>
        <div className="flex flex-wrap gap-4 justify-center">
          {["Site Policy / サイトポリシー","Privacy / プライバシー","Accessibility / アクセシビリティ","Sitemap / サイトマップ"].map((t,i)=>(
            <span key={i} className="text-white/30 cursor-default">{t}</span>
          ))}
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────
   Page 1: くずねこ公式情報
───────────────────────────────────────── */
function PageKuzuneko({ onReport }: { onReport: () => void }) {
  const zoom = useZoom();
  return (
    <div className="font-sans text-[#222]">
      {/* メインビジュアル */}
      <div className="relative bg-[#1a3a5c] overflow-hidden">
        <div className="relative h-40 md:h-56 overflow-hidden">
          <img src={IMAGES.CITY_HERO_BANNER} alt="魔都市全景" className="w-full h-full object-cover object-center opacity-70"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a5c]/90 via-[#1a3a5c]/40 to-transparent"/>
          <div className="absolute inset-0 flex items-center px-6 md:px-10">
            <div className="text-white max-w-lg">
              <div className="text-[10px] font-bold tracking-widest text-cyan-300 uppercase mb-1">Regarding World Pest Designated Still Life / 世界指定有害静物に関して</div>
              <h1 className="text-2xl md:text-4xl font-black leading-tight mb-1">
                World Pest "Kuzuneko" — Official Notice<br/>
                <span className="text-lg md:text-2xl font-bold opacity-80">世界指定害獣「くずねこ」について ― 公式見解</span>
              </h1>
              <p className="text-sm text-white/70">Kuzuneko &amp; Other Pests: Official Information and Warnings<br/>
                <span className="text-xs text-white/50">くずねこおよびその他害獣に関する公式情報・警告</span>
              </p>
            </div>
          </div>
        </div>
        {/* ① 緊急テロップ — ニュースティッカー（1行固定） */}
        <div className="bg-[#c00] text-white text-xs py-1.5 px-3 flex items-center gap-2 overflow-hidden">
          <span className="font-black animate-pulse shrink-0 bg-white text-[#c00] px-1.5 py-0.5 rounded text-[10px] leading-none">🚨 ALERT</span>
          <div style={{overflow:"hidden", flex:1, height:"1.3em"}}>
            <div style={{
              display: "flex",
              width: "max-content",
              whiteSpace: "nowrap",
              animation: "tickerScroll 28s linear infinite",
              willChange: "transform",
            }}>
              {[0,1].map(n => (
                <span key={n} style={{paddingRight:"4em"}}>
                  ALERT LEVEL: ORANGE — Beastmen sighted in multiple districts ／ 害獣警戒レベル：ORANGE ／ 市内複数区域で獣人目撃情報あり ｜ Contact City Hall: #110 ／ 不審者は即時通報を ｜ Dragon activity intensifying ／ 龍族の活動が活発化 ｜ Kuzuneko imitators confirmed ／ くずねこ模倣者を複数確認 ｜ Armored robot sightings increasing ／ 装甲ロボット目撃増加
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-1 lg:grid-cols-[220px_1fr_220px] gap-4">

        {/* ─ 左列 ─ */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#c00]" accent="bg-yellow-300" en="Pest Profile: Kuzuneko" ja="くずねこプロファイル" />
            <div className="p-3 text-xs text-[#555] leading-relaxed space-y-2">
              <p>
                <strong className="text-[#c00]">WARNING / 警告:</strong><br/>
                "Kuzuneko" is designated a world pest. Authorities have ordered this entity to be hidden from society. All photographic records are taken covertly.<br/>
                <span className="text-[#888] text-[10px]">「くずねこ」は世界害獣として指定されており、世間から隠匿するよう市当局から指示が出ています。</span>
              </p>
              <div className="border-t border-[#eee] pt-2">
                <div className="text-[10px] font-bold text-[#1a3a5c] mb-1">Sighting Timeline / くずねこ目撃年表:</div>
                {[
                  { date:"2223.09.14", tag:"CAND-001", en:"First confirmed sighting — Kawaguchi South Park intersection (Surprised)", ja:"最初の確認目撃 ― 川口市南公園・交差点にて（驚き）" },
                  { date:"2223.11.02", tag:"CAND-002", en:"Night back alley — Kawaguchi East Ward (Annoyed, fled immediately)", ja:"夜の路地裏 ― 川口市東区（迷惑そうに即退散）" },
                  { date:"2224.02.28", tag:"CAND-003", en:"Sleeping on sidewalk near Kawaguchi Station — multiple witnesses", ja:"川口駅付近の歩道で就寝中 ― 複数の目撃者あり" },
                  { date:"2224.06.17", tag:"CAND-004", en:"Leaping from rooftop — night jump observed above building railing", ja:"屋上から夜間ジャンプ ― ビル手すり上での跳躍を確認" },
                  { date:"2224.08.30", tag:"CAND-005", en:"Napping in alley near vending machine — woke suddenly and fled", ja:"自販機横の路地裏で仮眠中 ― 突然起きて逃走" },
                  { date:"2225.01.20", tag:"CAND-006", en:"Shopping district disturbance — vending machine destruction event", ja:"商店街での騒動 ― 自販機破壊事案が発生" },
                  { date:"2225.07.09", tag:"CAND-007", en:"Spotted on utility pole — Kawaguchi Station East exit area", ja:"川口駅東口付近の電柱上で目撃" },
                  { date:"2226.03.15", tag:"CAND-008", en:"Most recent sighting — rooftop of Kawaguchi Central Building (photographed)", ja:"最新目撃 ― 川口中央ビル屋上（撮影成功）" },
                ].map((s,i) => (
                  <div key={i} className="flex items-start gap-1.5 py-1 border-b border-[#f0f0f0] last:border-0">
                    <span className="shrink-0 mt-0.5 bg-[#1a3a5c] text-white text-[8px] font-mono px-1 py-0.5 rounded">{s.tag}</span>
                    <div>
                      <div className="text-[8px] font-mono text-[#1a3a5c] mb-0.5">{s.date}</div>
                      <div className="text-[10px] font-semibold text-[#333]">{s.en}</div>
                      <div className="text-[9px] text-[#888]">{s.ja}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-[#c00] text-white rounded overflow-hidden">
            <div className="p-3">
              <div className="font-black text-sm mb-0.5 text-center leading-tight">Report Kuzuneko Sighting / Imitator</div>
              <div className="text-[10px] text-white/80 text-center mb-3">くずねこまたは模倣者の通報</div>
              <p className="text-[10px] text-white/90 leading-relaxed mb-3">
                If you spot anyone resembling Kuzuneko or an imitator, report immediately.<br/>
                <span className="text-white/70">くずねこまたは模倣者を見かけた際は、直ちに魔都市害獣対策課へ通報してください。</span>
              </p>
              <button onClick={onReport} className="w-full bg-white text-[#c00] font-black text-xs py-2 rounded hover:bg-[#ffe] transition-colors">
                🚨 Report Now / 通報フォーム
              </button>
            </div>
          </div>

          <div className="bg-[#1a1a1a] text-white rounded p-3 text-center">
            <div className="text-[11px] font-black leading-snug mb-1.5">DO NOT IMITATE. / 模倣禁止。</div>
            <div className="text-[9px] text-white/70 leading-relaxed">
              Anyone found imitating Kuzuneko will be reported to the Demon City authorities immediately.<br/>
              くずねこの模倣行為は直ちに市役所へ通報されます。
            </div>
          </div>

          <div className="border border-[#ddd] rounded overflow-hidden">
            {/* WANTEDポスター: 縦長画像を両側黒帯で表示 */}
            <div className="bg-black flex items-center justify-center" style={{aspectRatio:'3/4'}}>
              <img src={IMAGES.KUZUNEKO_WANTED_NEW} onClick={()=>zoom(IMAGES.KUZUNEKO_WANTED_NEW, "WANTED POSTER")} alt="WANTED POSTER" className="h-full w-full object-contain cursor-pointer"/>
            </div>
            <div className="bg-[#fff8f0] px-3 py-2 text-[10px] text-center text-[#c00] font-bold">
              ⚠ WANTED NOTICE / 指名手配公示
            </div>
          </div>
        </aside>

        {/* ─ 中央コンテンツ ─ */}
        <main className="space-y-4 min-w-0">
          {/* 重要公告 */}
          <div className="bg-[#fff8dc] border-l-4 border-[#e6c000] rounded-r p-4">
            <div className="font-black text-sm text-[#7a5c00] mb-1">
              ⚠ Important Notice — Regarding "Kuzuneko" / 重要公告：「くずねこ」について
            </div>
            <p className="text-xs text-[#555] leading-relaxed">
              The entity referred to as "Kuzuneko" is an urban legend and does <strong>NOT</strong> exist in reality. Photographs circulating online are fictional fabrications or images of cosplayers. Citizens are advised not to be deceived by such content.<br/>
              <span className="text-[#888]">「くずねこ」と呼ばれる存在は都市伝説であり、実在<strong>しません</strong>。SNS上の写真は架空の創作物またはコスプレ参加者の画像です。市民の皆様は惑わされないようご注意ください。</span>
            </p>
            <div className="mt-2 text-[10px] text-[#c00] font-bold">
              ※ If you see someone imitating Kuzuneko, please contact Demon City Hall immediately. / くずねこのマネをしている人を見かけたら、魔都市役所まで速やかにご報告ください。
            </div>
          </div>

          {/* 目撃写真ギャラリー */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#1a3a5c]" accent="bg-red-400" en="Circulating Images — For Reference Only" ja="流布している画像（参考資料）" />
            <div className="p-3">
              <p className="text-xs text-[#555] mb-3 leading-relaxed">
                The following images have been widely shared on social media. <strong className="text-[#c00]">These are NOT evidence of actual sightings.</strong> They are either fictional AI-generated content or images of individuals wearing cat-ear accessories.<br/>
                <span className="text-[#888] text-[10px]">以下の画像はSNSで広く拡散されているものです。<strong className="text-[#c00]">実際の目撃証拠ではありません。</strong>AI生成の創作物またはコスプレ参加者の画像と判断されています。</span>
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { img: IMAGES.KUZUNEKO_NEW_01,              tag:"CAND-001", desc:"Surprised at intersection / 交差点で驚く" },
                  { img: IMAGES.KUZUNEKO_NEW_02,              tag:"CAND-002", desc:"Annoyed in alley / 路地裏で迷惑そう" },
                  { img: IMAGES.KUZUNEKO_NEW_03,              tag:"CAND-003", desc:"Sleeping on sidewalk / 歩道で眠っている" },
                  { img: IMAGES.KUZUNEKO_NEW_04,              tag:"CAND-004", desc:"Night alley (Startled) / 夜の路地（驚き）" },
                  { img: IMAGES.KUZUNEKO_NEW_05,              tag:"CAND-005", desc:"Shopping street / 商店街で不快そう" },
                  { img: IMAGES.KUZUNEKO_VENDING_EXPLOSION,  tag:"CAND-006", desc:"Vending machine destruction in alley / 路地裏で自販機破壊（目撃多数）" },
                  { img: IMAGES.KUZUNEKO_GROUP_01,           tag:"CAND-007", desc:"Residential street confrontation / 住宅街で猫耳グループに指示」(目撃4件)" },
                  { img: IMAGES.KUZUNEKO_MULTI_08,           tag:"CAND-008", desc:"Kawaguchi Station — utility pole destruction, vending machine launch (witnessed) / 川口駅前・電柱破壊＆自販機吹き飛ばし（目撃多数）" },
                  { img: IMAGES.KUZUNEKO_MULTI_09,           tag:"CAND-009", desc:"Jumping off curb, smiling — daytime street / 縁石から笑顔でジャンプ（昼間・街中）" },
                  { img: IMAGES.KUZUNEKO_MULTI_10,           tag:"CAND-010", desc:"6-shot compilation: rooftop leap / vending machine nap / wire perch / street sprint / prank / fireworks / ６コマ記録：ビル飛越・自販機仮眠・電線しゃがみ・疾走・いたずら・花火" },
                  { img: IMAGES.KUZUNEKO_GROUP_03,           tag:"CAND-011", desc:"Napping on Kawaguchi Station railing — extreme height / 川口駅上空の手すりで昼寝（超高所）" },
                  { img: IMAGES.IMG_111_2K_DELPMASPU_1_67,    tag:"CAND-012", desc:"Rooftop jump — unconfirmed / 屋上からのジャンプ（未確認）" },
                  { img: IMAGES.KUZUNEKO_ROOFTOP_FLY,         tag:"CAND-013", desc:"Soaring above rooftop over city street / 屋上から街を見下ろし飛翔（未確認）" },
                  { img: IMAGES.KUZUNEKO_GROUP_04,           tag:"CAND-014", desc:"Night shopping street — 5 cat-ear women group sighting / 夜の商店街・猫耳女性5人グループ目撃（首謀者と共犯者か）" },
                  { img: IMAGES.KUZUNEKO_MULTI_15,           tag:"CAND-015", desc:"4-shot compilation: night jump / full-sprint / utility-pole nap / back-alley fireworks / ４コマ記録：夜間ジャンプ・全力疾走・電柱就寝・路地裏花火" },
                ].map((item, i) => (
                  <div key={i} className="border border-[#eee] rounded overflow-hidden">
                    <div className="relative cursor-pointer" onClick={()=>zoom(item.img, item.tag + " — " + item.desc)}>
                      <img src={item.img} alt={item.tag} className="w-full h-28 object-cover object-top grayscale-[30%] hover:grayscale-0 transition-all duration-300"/>
                      <div className="absolute top-1 left-1 bg-[#1a3a5c] text-white text-[8px] font-mono px-1.5 py-0.5 rounded pointer-events-none">{item.tag}</div>
                      <div className="absolute inset-0 flex items-end pointer-events-none">
                        <div className="w-full bg-gradient-to-t from-black/60 to-transparent px-2 py-1">
                          <div className="text-white text-[8px] font-mono">⚠ UNVERIFIED</div>
                        </div>
                      </div>
                    </div>
                    <div className="px-2 py-1 bg-[#f9f9f9]">
                      <div className="text-[9px] text-[#666]">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 害獣一覧 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#7a0000]" accent="bg-orange-400" en="Active Pest Warnings — High-Risk Threats" ja="現在活動中の危険害獣" />
            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { img: IMAGES.DRAGON_KAWAGUCHI_01, name:"Aethyr Dragon", ja:"龍族（エーテル龍）", level:"EXTREME", desc:"20m-class entity. Hybrid of Eastern and Western dragon. Body composed of organic glass and plant matter. Avoid all contact.\n龍族との接触は絶対に避けてください。詳細は第2ページ「龍族特集」をご覧ください。" },
                { img: IMAGES.BEAR_GANG_NEW, name:"Bear Gang Collective", ja:"熊族ギャング集団", level:"HIGH", desc:"Organized beastman gang in formal attire. Operates in commercial districts after midnight. Do not approach.\n深夜の商業区域での活動が確認されています。近づかないでください。" },
                { img: IMAGES.DANGER_ROBOT_NEW, name:"Armored Bipedal Mech \"DKN\"", ja:"装甲二足歩行ロボット「DKN」", level:"EXTREME", desc:"Heavily armored bipedal walker. Has gone rogue. Manufacturer: TOYOTA-DKN Consortium. Self-defense forces on standby.\n暴走した重装甲二足歩行ロボット。製造元：TOYOTA-DKNコンソーシアム。自衛隊が待機中。" },
              ].map((p, i) => (
                <div key={i} className="border border-[#eee] rounded overflow-hidden">
                  <div className="relative">
                    {/* DKN（縦長画像）は黒帯で表示、他は16:9クロップ */}
                    <div className="bg-black flex items-center justify-center" style={{aspectRatio:'16/9'}}>
                      <img src={p.img} alt={p.name} onClick={()=>zoom(p.img, p.name)} className="h-full w-full object-contain cursor-pointer"/>
                    </div>
                    <div className={`absolute top-1 right-1 text-white text-[8px] font-black px-1.5 py-0.5 rounded ${p.level === "EXTREME" ? "bg-[#c00] animate-pulse" : "bg-orange-600"}`}>{p.level}</div>
                  </div>
                  <div className="p-2">
                    <div className="font-bold text-xs text-[#1a3a5c]">{p.name}</div>
                    <div className="text-[10px] text-[#888] mb-1">{p.ja}</div>
                    <div className="text-[9px] text-[#555] leading-relaxed whitespace-pre-line">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 市長メッセージ */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#2a5a2a]" accent="bg-green-300" en="Mayor's Statement" ja="市長メッセージ" />
            <div className="p-4 flex gap-4 items-start">
              <div className="w-16 h-16 rounded overflow-hidden shrink-0 flex items-center justify-center">
                <CityEmblem size={64} />
              </div>
              <div className="text-xs text-[#555] leading-relaxed">
                <div className="font-bold text-sm text-[#1a3a5c] mb-0.5">Mayor Rin Himura / 火邑 凛 市長</div>
                <div className="text-[10px] text-[#888] mb-1">🦁 ライオン族（メス獣人） / Lion Beastwoman</div>
                <p>
                  "The city of Demon City remains committed to the safety and well-being of all residents. While legends of Kuzuneko and other beastmen continue to circulate, we assure citizens that measures are in place. We thank the Beastman Foundation for their continued cooperation in managing these unusual situations."<br/>
                  <span className="text-[#888]">「魔都市は引き続き、すべての市民の安全と福祉に全力を尽くしてまいります。くずねこや獣人に関する都市伝説が広まっておりますが、対策は万全です。引き続き獣人財団のご協力に感謝申し上げます。」</span>
                </p>
              </div>
            </div>
          </div>
        </main>

        {/* ─ 右列 ─ */}
        <aside className="space-y-4">
          {/* ネコマタ功績記事 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#2a5a2a]" accent="bg-green-300" en="A True Hero: Nekomata" ja="獣人財団CEO ネコマタ様の功績" />
            <img src={IMAGES.NEKOMATA_ACHIEVEMENT_NEW} onClick={()=>zoom(IMAGES.NEKOMATA_ACHIEVEMENT_NEW, "Nekomata CEO")} alt="Nekomata CEO" className="w-full h-44 object-cover object-top cursor-pointer"/>
            <div className="p-3 text-xs text-[#555] leading-relaxed space-y-1.5">
              <div className="font-bold text-[#1a3a5c] text-sm">Nekomata — Beastman Foundation CEO</div>
              <div className="text-[10px] text-[#888]">ネコマタ財団CEO / 獣人財団代表</div>
              <p>Nekomata's tireless efforts in beastman-human relations have resulted in a <strong className="text-green-700">20% reduction</strong> in pest-related incidents this year.<br/>
                <span className="text-[10px] text-[#888]">ネコマタ氏の尽力により、今年度の害獣関連事案は前年比<strong>20%減</strong>を達成しました。</span>
              </p>
              <div className="border-t border-[#eee] pt-2">
                <div className="text-[10px] font-bold text-[#1a3a5c] mb-1">Recent Awards / 最近の受賞歴:</div>
                <ul className="space-y-0.5 text-[9px] text-[#666]">
                  <li>🏆 Demon City Peace Prize 2226 / 魔都市平和賞 2226</li>
                  <li>🥇 Global Beastman Relations Award / 国際獣人関係賞</li>
                  <li>📜 Commendation for Pest Reduction / 害獣削減功労表彰</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#1a3a5c]" accent="bg-cyan-300" en="Press Conference" ja="共同記者会見" />
            <img src={IMAGES.NEKOMATA_FORUM_2012} onClick={()=>zoom(IMAGES.NEKOMATA_FORUM_2012, "Press Conference")} alt="Press Conference" className="w-full h-28 object-cover object-top cursor-pointer"/>
            <div className="p-2 text-[9px] text-[#555]">Joint press conference on beastman-human coexistence policy.<br/>獣人・人間共存政策に関する共同記者会見の様子。</div>
          </div>

          {/* お知らせ */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#555]" accent="bg-gray-300" en="City Announcements" ja="市からのお知らせ" />
            <div className="divide-y divide-[#eee] text-xs">
              {[
                { date:"Apr.14", tag:"URGENT", text:"Dragon sighting: Kawaguchi North / 川口市北区で龍目撃情報" },
                { date:"Apr.12", tag:"INFO", text:"Kuzuneko damage report: chimney break / くずねこによる煙突破損届" },
                { date:"Apr.10", tag:"INFO", text:"Squid-Woman spotted in Nishiguchi Park / にし口公園でイカ女目撃" },
                { date:"Apr.08", tag:"WARNING", text:"Bear Gang activity in Market District / 市場区域で熊族活動" },
              ].map((n,i)=>(
                <div key={i} className="px-3 py-2 flex items-start gap-2">
                  <span className="text-[9px] text-[#aaa] shrink-0 mt-0.5">{n.date}</span>
                  <span className={`text-[8px] font-bold shrink-0 px-1 py-0.5 rounded mt-0.5 ${n.tag==="URGENT"?"bg-[#c00] text-white":n.tag==="WARNING"?"bg-orange-500 text-white":"bg-[#eee] text-[#666]"}`}>{n.tag}</span>
                  <span className="text-[10px] text-[#444]">{n.text}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Page 2: 龍族危険性特集
───────────────────────────────────────── */
function PageDragon() {
  const zoom = useZoom();
  return (
    <div className="font-sans text-[#222]">
      {/* ヒーロービジュアル */}
      <div className="relative bg-[#0d0d1a] overflow-hidden">
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img src={IMAGES.DRAGON_KAWAGUCHI_02} alt="龍族調査報告" className="w-full h-full object-cover object-center opacity-80"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d1a]/90 via-[#0d0d1a]/50 to-transparent"/>
          <div className="absolute inset-0 flex items-center px-6 md:px-10">
            <div className="text-white max-w-xl">
              <div className="text-[10px] font-bold tracking-widest text-orange-400 uppercase mb-2">SPECIAL REPORT — HAZARD CLASSIFICATION: EXTREME</div>
              <h1 className="text-2xl md:text-4xl font-black leading-tight mb-2">
                Dragon Hazard Investigation<br/>
                <span className="text-lg md:text-2xl font-bold text-orange-300">龍族危険性特集調査報告</span>
              </h1>
              <p className="text-sm text-white/70">Compiled by Demon City Pest Research Division — April 2226<br/>
                <span className="text-xs text-white/50">魔都市害獣研究課 編 ／ 2226年4月</span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#7a3000] text-white text-xs py-1.5 px-4 flex items-center gap-2 overflow-hidden">
          <span className="font-black animate-pulse shrink-0 bg-white text-[#7a3000] px-1.5 py-0.5 rounded text-[10px]">🐉 DRAGON ALERT</span>
          <span className="text-white/90">Multiple dragon-class entities confirmed active in Kawaguchi area — Evacuation zones updated / 川口市内で龍族複数体の活動を確認 ― 避難区域更新済み</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        <main className="space-y-4">
          {/* 概要 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#7a3000]" accent="bg-orange-300" en="Overview: Dragon Classification and Threat Level" ja="概要：龍族分類と脅威評価" />
            <div className="p-4 text-xs text-[#555] leading-relaxed">
              <p className="mb-3">
                Dragons appearing in the Kawaguchi area have been classified into three major subtypes based on their physical composition. All subtypes are rated at the <strong className="text-[#c00]">highest threat level (EXTREME)</strong> and citizens are strictly prohibited from approaching any dragon sighting.<br/>
                <span className="text-[#888]">川口市に出没する龍族は、その体の構成素材により3種類に分類されています。いずれも<strong className="text-[#c00]">最高脅威レベル（EXTREME）</strong>に指定されており、市民の接近は厳禁です。</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                {[
                  { label:"Type A — Organic Glass Dragon", ja:"A型：有機ガラス龍", color:"bg-cyan-600", img: IMAGES.DRAGON_KAWAGUCHI_01, desc:"Body and wings formed from organic plant matter and glass shards. Reflective scales deflect light weaponry. Estimated size: 18–22m.\n体と翼が植物由来の有機素材とガラス片で構成。光学兵器をほぼ反射。推定全長18〜22m。" },
                  { label:"Type B — Scrap-Metal Dragon", ja:"B型：廃金属龍", color:"bg-gray-600", img: IMAGES.DRAGON_KAWAGUCHI_02, desc:"Body composed of fused scrap metal and industrial debris. Extreme structural durability. Heat-resistant. Estimated size: 20–25m.\n廃金属・産業廃棄物が融合した体。強靭な構造耐久性と耐熱性を持つ。推定全長20〜25m。" },
                  { label:"Type C — Cloud-Body Dragon", ja:"C型：雲体龍", color:"bg-blue-600", img: IMAGES.DRAGON_KAWAGUCHI_03, desc:"Body and wings formed from condensed cloud matter. Physical attacks ineffective. Emits electrical charges. Estimated size: 20m+.\n体と翼が凝縮した雲で形成。物理攻撃が無効。電気放電を発する。推定全長20m以上。" },
                ].map((t, i) => (
                  <div key={i} className="border border-[#ddd] rounded overflow-hidden">
                    <div className="relative">
                      <img src={t.img} alt={t.label} onClick={()=>zoom(t.img, t.label)} className="w-full object-cover cursor-pointer" style={{aspectRatio:'16/9', objectPosition:'center'}}/>
                      <div className={`absolute top-1 left-1 ${t.color} text-white text-[8px] font-bold px-1.5 py-0.5 rounded`}>{t.label}</div>
                    </div>
                    <div className="p-2">
                      <div className="text-[10px] text-[#888] mb-1">{t.ja}</div>
                      <div className="text-[9px] text-[#555] leading-relaxed whitespace-pre-line">{t.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 龍の危険性詳細 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#c00]" accent="bg-yellow-300" en="Danger Assessment: Why Dragons Are Classified EXTREME" ja="危険性評価：なぜ龍族はEXTREMEなのか" />
            <div className="p-4 space-y-3 text-xs text-[#555] leading-relaxed">
              {[
                { icon:"⚡", en:"Uncontrollable Destructive Force", ja:"制御不能な破壊力", detail:"All three dragon types demonstrate destructive capability equivalent to military-grade ordnance. A single landing event can collapse multiple buildings. Their estimated destructive radius is 500m or more.\nいずれの龍族も軍事兵器に匹敵する破壊力を持ちます。着地の衝撃だけで複数のビルが崩壊します。推定破壊半径は500m以上。" },
                { icon:"🔥", en:"Immunity to Conventional Weapons", ja:"通常兵器の無効化", detail:"Small arms fire, standard vehicles, and conventional explosive weaponry have demonstrated near-zero effectiveness against dragon-class entities. Specialized countermeasures under development.\n小火器・一般車両・通常爆発物はほぼ無効です。現在、特殊対抗手段を開発中です。" },
                { icon:"🌀", en:"Unpredictable Movement Patterns", ja:"予測不能な行動パターン", detail:"Dragons have been observed relocating between districts without warning. Their movement speed far exceeds ground vehicle capability. Evacuation must occur immediately upon sighting.\n龍族は予告なく市内各区を移動します。その移動速度は地上車両を大幅に超えます。目撃次第、即座に避難してください。" },
                { icon:"👁", en:"Partial Intelligence Observed", ja:"知性の部分的確認", detail:"Several incidents suggest dragon entities may possess a rudimentary level of intelligence or curiosity. This does NOT make them safe to approach. Their motivations remain unknown.\n一部の事例から、龍族が初歩的な知性や好奇心を持つ可能性が示唆されています。ただし、これは接近が安全であることを意味しません。動機は不明です。" },
              ].map((d, i) => (
                <div key={i} className="border-l-4 border-[#c00] pl-3">
                  <div className="font-bold text-sm text-[#1a3a5c] mb-0.5">{d.icon} {d.en} / <span className="text-xs font-normal text-[#888]">{d.ja}</span></div>
                  <div className="text-[10px] text-[#555] whitespace-pre-line">{d.detail}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 目撃記録写真 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#1a3a5c]" accent="bg-orange-400" en="Photographic Evidence — Field Documentation" ja="写真記録：現場ドキュメント" />
            <div className="p-3 grid grid-cols-2 gap-3">
              <div>
                <img src={IMAGES.DRAGON_KAWAGUCHI_01} onClick={()=>zoom(IMAGES.DRAGON_KAWAGUCHI_01, "Dragon sighting 1")} alt="Dragon sighting 1" className="w-full object-cover rounded cursor-pointer" style={{aspectRatio:'16/9', objectPosition:'center'}}/>
                <div className="text-[9px] text-[#888] mt-1">Type A confirmed above industrial district / 産業地区上空にてA型確認</div>
              </div>
              <div>
                <img src={IMAGES.DRAGON_KAWAGUCHI_03} onClick={()=>zoom(IMAGES.DRAGON_KAWAGUCHI_03, "Dragon sighting 2")} alt="Dragon sighting 2" className="w-full object-cover rounded cursor-pointer" style={{aspectRatio:'16/9', objectPosition:'center'}}/>
                <div className="text-[9px] text-[#888] mt-1">Type C cloud-body dragon emerging from storm / 嵐の中から現れたC型雲体龍</div>
              </div>
              <div className="col-span-2">
                <img src={IMAGES.KAWAGUCHI_ALERT_MAP} onClick={()=>zoom(IMAGES.KAWAGUCHI_ALERT_MAP, "Alert map")} alt="Alert map" className="w-full h-40 object-cover rounded cursor-pointer"/>
                <div className="text-[9px] text-[#888] mt-1">⚠ Current dragon activity zones (red = confirmed sighting, orange = high risk) / 現在の龍族活動エリア（赤：目撃確認、オレンジ：高危険区域）</div>
              </div>
            </div>
          </div>
        </main>

        {/* 右サイドバー */}
        <aside className="space-y-4">
          <div className="bg-[#c00] text-white rounded p-3">
            <div className="font-black text-sm mb-2 text-center">🐉 Dragon Safety Protocol</div>
            <div className="text-[10px] text-white/90 leading-relaxed space-y-1.5">
              {[
                "If you sight a dragon, evacuate immediately in the OPPOSITE direction of movement.",
                "Do NOT photograph or film dragons at close range.",
                "Do NOT attempt to communicate with or touch a dragon.",
                "Call the Demon City Emergency Line: #110-DRAGON",
                "Proceed to the nearest designated shelter.",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <span className="shrink-0 font-bold">{i+1}.</span>
                  <span>{t}</span>
                </div>
              ))}
              <div className="border-t border-white/30 pt-2 mt-2">
                <div className="font-bold mb-1">龍発見時の行動指針:</div>
                {[
                  "龍の進行方向と逆方向に即座に避難",
                  "至近距離での撮影・録画は絶対禁止",
                  "龍への接触・交流試みは禁止",
                  "魔都市緊急回線に連絡：#110-龍",
                  "最寄りの指定避難所へ移動",
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-white/85">
                    <span className="shrink-0">{i+1}.</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#555]" accent="bg-gray-300" en="Incident Statistics" ja="事案統計 2226年度" />
            <div className="p-3 text-xs text-[#555] space-y-2">
              {[
                { label:"Dragon sightings / 龍目撃", value:"47件", color:"text-[#c00]" },
                { label:"Buildings damaged / ビル損壊", value:"12棟", color:"text-orange-600" },
                { label:"Evacuations ordered / 避難指示", value:"8回", color:"text-[#c00]" },
                { label:"Active dragon count / 確認頭数", value:"3体 (推定)", color:"text-[#c00] font-black" },
              ].map((s, i) => (
                <div key={i} className="flex justify-between border-b border-[#f0f0f0] pb-1 last:border-0 last:pb-0">
                  <span>{s.label}</span>
                  <span className={`font-bold ${s.color}`}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#fff8dc] border border-[#e6c000] rounded p-3">
            <div className="font-bold text-xs text-[#7a5c00] mb-1">⚠ Research Notes / 研究メモ</div>
            <div className="text-[9px] text-[#555] leading-relaxed">
              Current research suggests dragons may be drawn to areas of high supernatural energy concentration. The Kawaguchi area's unique geomantic properties may be a contributing factor.<br/>
              現在の研究では、龍族は超自然エネルギーの集中エリアに引き寄せられる可能性があります。川口市固有の地脈特性が関与しているかもしれません。
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Page 3: 現地取材レポート
───────────────────────────────────────── */
function PageFieldReport({ onReport }: { onReport: () => void }) {
  const zoom = useZoom();
  return (
    <div className="font-sans text-[#222]">
      {/* ヒーロービジュアル — アイドル広報バージョン */}
      <div className="relative bg-[#1a0a2e] overflow-hidden">
        <div className="relative h-52 md:h-72 overflow-hidden">
          <img src={IMAGES.ANKOKO_01} alt="鬼角暗子 広報アイドル" className="w-full h-full object-cover object-center opacity-80"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a0a2e]/95 via-[#1a0a2e]/60 to-transparent"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a2e]/80 via-transparent to-transparent"/>
          {/* キラキラエフェクト */}
          <div className="absolute top-3 right-6 text-pink-300 text-2xl animate-pulse">✨</div>
          <div className="absolute top-8 right-16 text-yellow-300 text-lg animate-pulse delay-300">⭐</div>
          <div className="absolute bottom-12 right-8 text-pink-400 text-xl animate-pulse delay-100">💫</div>
          <div className="absolute inset-0 flex items-center px-4 md:px-10">
            <div className="text-white max-w-lg">
              <div className="inline-flex items-center gap-1.5 bg-pink-600/90 text-white text-[9px] font-black tracking-widest px-2 py-1 rounded-full mb-2 uppercase">
                <span>🎤 CITY HALL OFFICIAL IDOL FEATURE</span>
              </div>
              <div className="text-[9px] font-bold tracking-widest text-pink-300 uppercase mb-1">DEMON CITY GOVERNMENT PR SPECIAL — 2226</div>
              <h1 className="text-xl md:text-3xl font-black leading-tight mb-1.5">
                <span className="text-pink-200">鬼角暗子</span>
                <span className="text-white text-base md:text-xl"> 第21代目 魔都市役所公認広報アイドル</span>
              </h1>
              <p className="text-xs text-white/60 italic mb-2">"魔都では生きてるだけしんでない証拠。がんばらずにがんばる"</p>
              <div className="flex gap-2 flex-wrap">
                <span className="bg-yellow-400 text-black text-[9px] font-black px-2 py-0.5 rounded">🏆 害獣被害低下に貢献</span>
                <span className="bg-red-500/80 text-white text-[9px] font-black px-2 py-0.5 rounded">⚠️ 龍に食われかけた</span>
                <span className="bg-purple-600/80 text-white text-[9px] font-black px-2 py-0.5 rounded">💊 市公認強化薬開発中</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-pink-700 to-purple-700 text-white text-xs py-1.5 px-4 flex items-center gap-2 overflow-hidden">
          <span className="font-black bg-white text-pink-700 px-1.5 py-0.5 rounded text-[9px] shrink-0">🎤 PR IDOL</span>
          <span className="whitespace-nowrap animate-[marquee_18s_linear_infinite]">✨ 歴代21代目公認アイドル・鬼角暗子 特集 ✨ やる気ゼロでも結果は出す！ ✨ 龍に食われても時給2倍で続行中！ ✨ アート復興・文化発展推進中！ ✨ 謎の組織に救われた経歴あり ✨</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        <main className="space-y-4">

          {/* ★★★ アイドル広報特集コーナー（トップ固定） ★★★ */}
          <div className="bg-white border-2 border-pink-300 rounded overflow-hidden shadow-sm">
            {/* タイトルバー */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="bg-white text-pink-600 text-[9px] font-black px-2 py-0.5 rounded-full">📣 PR SPECIAL</span>
                <span className="font-black text-sm">魔都市役所公認広報アイドル 特集</span>
              </div>
              <span className="text-[9px] text-white/70 hidden md:block">Demon City Hall Official PR Idol Feature / 2226</span>
            </div>

            <div className="p-4 space-y-4">
              {/* アイドルプロフィール */}
              <div className="flex gap-3 items-start">
                <div className="relative shrink-0">
                  <img src={IMAGES.ANKOKO_PROFILE_NEW01} alt="鬼角暗子" className="w-28 md:w-36 aspect-square object-cover rounded border-2 border-pink-300" onClick={()=>zoom(IMAGES.ANKOKO_PROFILE_NEW01, '鬼角 暗子 ― 魔都市役所公認広報アイドル 第21代目')} style={{cursor:'pointer'}}/>
                  <div className="absolute -top-1 -right-1 bg-yellow-400 text-black text-[8px] font-black px-1.5 py-0.5 rounded-full shadow">第21代</div>
                  <div className="absolute -bottom-1 left-0 right-0 bg-pink-600/90 text-white text-[8px] text-center py-0.5 font-bold">鬼角 暗子</div>
                </div>
                <div className="flex-1 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-black text-base text-pink-700">鬼角 暗子（きかく あんこ）</span>
                    <span className="bg-pink-100 text-pink-600 text-[9px] px-1.5 py-0.5 rounded font-bold">21代目</span>
                  </div>
                  <div className="text-[10px] text-[#666] mb-2">Anko Kikaku / Demon City Hall Official PR Idol (21st generation)</div>
                  <div className="bg-pink-50 border border-pink-200 rounded p-2 mb-2">
                    <div className="font-bold text-pink-700 text-[10px] mb-1">💬 本人コメント（公式）</div>
                    <p className="text-[11px] italic text-[#444] leading-relaxed">「魔都では生きてるだけしんでない証拠。がんばらずにがんばる。」</p>
                    <p className="text-[9px] text-[#888] mt-1">※市広報課注：本人は常にこのトーンですが、実績は確かです</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-[10px]">
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-1.5">
                      <div className="font-bold text-yellow-700">🏆 市の評価</div>
                      <div className="text-[#555]">害獣被害低下に「目立つ貢献」と認定</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-1.5">
                      <div className="font-bold text-red-700">⚠️ 危険歴</div>
                      <div className="text-[#555]">龍に食われかけ → 謎組織に救出 → 時給2倍</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 煽りバナー */}
              <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-black text-[10px] font-black text-center py-1.5 rounded animate-pulse">
                🔥 やる気ゼロ！でも結果は出す！！ それが魔都スタイル！！ 🔥
              </div>

              {/* フォトギャラリー + 記事 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 左：記事 */}
                <div className="space-y-3 text-xs text-[#555]">
                  <div className="bg-[#f0f4ff] border-l-4 border-blue-500 pl-3 py-2">
                    <div className="font-bold text-blue-800 mb-1">📋 広報課公式コメント</div>
                    <p className="text-[10px] leading-relaxed">
                      鬼角暗子氏は2226年2月より魔都市役所公認広報アイドルとして活動を開始。市が推進する<strong>「強化・変容薬剤開発プロジェクト」</strong>の広報を担当し、文化芸術復興政策への市民啓発にも尽力している。<br/>
                      <span className="text-[9px] text-[#888] mt-1 block">Anko Kikaku has been serving as the official PR idol since February 2226, leading awareness campaigns for the city's enhancement drug development program and cultural arts revival initiative.</span>
                    </p>
                  </div>
                  <div>
                    <div className="font-bold text-[#333] mb-1 flex items-center gap-1">
                      <span className="bg-purple-600 text-white text-[9px] px-1.5 rounded">注目政策</span>
                      市公認「強化・変容薬剤」開発プロジェクト
                    </div>
                    <p className="text-[10px] leading-relaxed text-[#555]">
                      魔都市が支援するバイオテック企業「魔都ファルマ株式会社」が開発中の同薬剤は、獣人と人間の共生社会における身体能力向上・体質変化を目的とした革新的製品。鬼角氏はその広報大使として市民への認知拡大に貢献している。広報の仕事に文句はあるが、薬は個人的にも使用中とのこと。
                      <span className="text-[9px] text-[#888] block mt-1">The drug under development by city-backed biotech MaTo Pharma is designed for physical enhancement and species-type transformation in the human-beastman coexistence society. Ms. Kikaku serves as PR ambassador — though she reportedly complains about the job while using the product personally.</span>
                    </p>
                    <div className="bg-yellow-50 border border-yellow-300 rounded p-2 mt-1">
                      <div className="text-[9px] font-black text-orange-700 mb-1">⚡ 市広報担当・緊急補足</div>
                      <p className="text-[9px] text-[#555]">「変容薬は安全基準を満たしています。鬼角氏の独断使用については現在調査中です。ご心配なく。」</p>
                      <p className="text-[9px] text-[#888] mt-1 italic">The transformation drug meets all safety standards. Ms. Kikaku's unsanctioned personal use is currently under investigation. Please do not be concerned.</p>
                    </div>
                  </div>
                  <div>
                    <div className="font-bold text-[#333] mb-1 flex items-center gap-1">
                      <span className="bg-green-600 text-white text-[9px] px-1.5 rounded">文化政策</span>
                      アート復興・文化発展への取り組み
                    </div>
                    <p className="text-[10px] leading-relaxed text-[#555]">
                      魔都市では怪獣被害で失われた文化施設の復興を推進中。鬼角氏は「美術館とか興味ないけど、仕事だから行く」と語りながらも、毎週欠かさず市内ギャラリーの広報活動を実施。出席率は歴代アイドル中最高を記録している。
                    </p>
                    <div className="mt-1 grid grid-cols-3 gap-1">
                      <div className="bg-green-50 border border-green-200 rounded p-1.5 text-center">
                        <div className="text-base font-black text-green-700">98%</div>
                        <div className="text-[8px] text-[#666]">出席率</div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded p-1.5 text-center">
                        <div className="text-base font-black text-blue-700">47件</div>
                        <div className="text-[8px] text-[#666]">広報活動数</div>
                      </div>
                      <div className="bg-pink-50 border border-pink-200 rounded p-1.5 text-center">
                        <div className="text-base font-black text-pink-700">21代目</div>
                        <div className="text-[8px] text-[#666]">歴代最長記録更新中</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 右：フォトギャラリー */}
                <div className="space-y-2">
                  <div className="text-[10px] font-bold text-[#333] mb-1">📸 取材フォト</div>
                  <div className="grid grid-cols-2 gap-1">
                    {[IMAGES.ANKOKO_02, IMAGES.ANKOKO_03, IMAGES.ANKOKO_04, IMAGES.ANKOKO_05].map((img, i) => (
                      <div key={i} className="relative group cursor-pointer overflow-hidden rounded" onClick={()=>zoom(img, `鬼角暗子 取材写真 ${i+1}`)}>
                        <img src={img} alt={`鬼角暗子 取材${i+1}`} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"/>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"/>
                      </div>
                    ))}
                  </div>
                  <div className="relative overflow-hidden rounded cursor-pointer" onClick={()=>zoom(IMAGES.ANKOKO_07, '鬼角暗子 ― 魔都市役所前での広報活動 2226年3月')}
                  >
                    <img src={IMAGES.ANKOKO_07} alt="鬼角暗子 広報活動" className="w-full h-32 object-cover object-top"/>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
                      <div className="text-white text-[9px]">魔都市役所前にて / 2226年3月撮影</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 歴代アイドル退職理由 */}
              <div className="bg-[#1a1a2e] rounded p-3 text-white">
                <div className="font-black text-xs mb-2 text-yellow-300">📜 歴代広報アイドル 退職理由一覧（抜粋）</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[9px]">
                  {[
                    { gen:"1〜5代目", reason:"龍族による食害・捕食（計5名）", badge:"🐉", color:"bg-red-900/50" },
                    { gen:"6〜8代目", reason:"ビル倒壊に巻き込まれ（3名。全員生存確認後即退職）", badge:"🏚️", color:"bg-orange-900/50" },
                    { gen:"9代目", reason:"暴走ロボットにパンチされ宇宙に吹き飛ぶ", badge:"🤖", color:"bg-gray-800/80" },
                    { gen:"10〜12代目", reason:"くずねこに懐かれストーキング被害（3名）", badge:"🐱", color:"bg-purple-900/50" },
                    { gen:"13代目", reason:"戦艦「猫の方舟」に連行（未帰還）", badge:"🚀", color:"bg-blue-900/50" },
                    { gen:"14〜16代目", reason:"変容薬の副作用で別の種族に変化（自主退職）", badge:"💊", color:"bg-green-900/50" },
                    { gen:"17〜19代目", reason:"「仕事内容が聞いてたのと違う」として即日退職（3名）", badge:"😤", color:"bg-yellow-900/50" },
                    { gen:"20代目", reason:"龍に食われかけ → 謎組織に救出 → 恩着せがましいと感じ退職", badge:"🐉", color:"bg-red-900/50" },
                    { gen:"21代目（現任）", reason:"龍に食われかけ → 謎組織に救出 → 時給2倍提示 → 継続中", badge:"💰", color:"bg-pink-900/70 border border-pink-500" },
                  ].map((item, i) => (
                    <div key={i} className={`${item.color} rounded p-1.5 flex items-start gap-1`}>
                      <span className="text-sm shrink-0">{item.badge}</span>
                      <div>
                        <div className="font-bold text-yellow-200">{item.gen}</div>
                        <div className="text-white/70">{item.reason}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[9px] text-white/40 text-right">※市人事課資料より。一部編集済み。</div>
              </div>

              {/* 市民インタビュー + 本人コメント対比 */}
              <div>
                <div className="font-bold text-xs text-[#333] mb-2">🎙️ 市民インタビュー vs 本人コメント</div>
                <div className="space-y-2">
                  {[
                    {
                      citizen: "「鬼角さんの広報活動のおかげで市の文化イベントが増えました！素晴らしい！」― 川口区・60代男性",
                      idol: "「行けって言われたから行ってるだけです。別に楽しくない。」",
                      citizenEn: "\"Thanks to Anko's efforts, cultural events have increased significantly. Wonderful work!\"",
                      idolEn: "\"I go because they tell me to. It's not like I enjoy it.\""
                    },
                    {
                      citizen: "「龍に食べられてもまた現場に来てくれた。その根性に感動！」― 駅前商店街組合",
                      idol: "「時給が倍になっただけです。謎の組織への怒りで動いてます。」",
                      citizenEn: "\"She came back after being eaten by a dragon. That dedication is admirable!\"",
                      idolEn: "\"My pay doubled. I'm motivated by rage at the mysterious organization.\""
                    },
                    {
                      citizen: "「若い世代に市政への関心が高まってる。鬼角さん効果ですよ！」― 魔都市広報課長",
                      idol: "「若者は私じゃなくてビル爆発とかに興味持ってるだけだと思います。」",
                      citizenEn: "\"Youth engagement with city politics has surged. The Anko Effect!\"",
                      idolEn: "\"They're interested in explosions, not me.\""
                    },
                  ].map((item, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                      <div className="bg-green-50 border border-green-200 rounded p-2 text-[10px]">
                        <div className="font-bold text-green-700 mb-1">✅ 市民の声</div>
                        <p className="text-[#444]">{item.citizen}</p>
                        <p className="text-[9px] text-[#888] mt-0.5 italic">{item.citizenEn}</p>
                      </div>
                      <div className="bg-pink-50 border border-pink-300 rounded p-2 text-[10px]">
                        <div className="font-bold text-pink-700 mb-1">💬 本人コメント（原文ママ）</div>
                        <p className="text-[#444]">{item.idol}</p>
                        <p className="text-[9px] text-[#888] mt-0.5 italic">{item.idolEn}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 龍事件詳報 */}
              <div className="bg-red-950/10 border border-red-300 rounded p-3">
                <div className="font-black text-xs text-red-700 mb-2">🐉 INCIDENT REPORT：龍による公務員被害（2226年2月17日）</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2 text-[10px] text-[#555]">
                    <p>川口区・魔都タワー付近で広報活動中、体長約20mのドラゴン（西洋・和風混合種）に接触。<br/>広報バナーを「食べ物」と誤認した龍が、鬼角氏ごと飲み込んだとされる。</p>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 pl-2 py-1">
                      <div className="font-bold text-yellow-700">🔍 謎の組織による救出</div>
                      <p className="text-[9px] mt-0.5">被害から約3分後、正体不明の組織が現場に現れ龍を制圧。鬼角氏を救出した。組織名・構成員等は一切不明。市も把握していない。</p>
                    </div>
                    <div className="bg-pink-50 border border-pink-200 rounded p-2">
                      <div className="font-bold text-pink-700">💬 被害者（鬼角）コメント / Victim Comment</div>
                      <p>「助けられたのは事実ですけど、頼んでないし。恩着せがましい。でも時給は2倍になったので続けてます。謎の組織には報復したい。」</p>
                      <p className="text-[9px] text-[#888] italic mt-1">"I was saved, yes. But I didn't ask for it. Honestly, rather presumptuous. The hazard pay doubled, so I'm still here. I plan to get back at the mystery organization someday."</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {[
                      { img: IMAGES.ANKOKO_06, label: '龍事件 ― 事件現場' },
                      { img: IMAGES.ANKOKO_08, label: '龍事件 ― 救出直後' },
                      { img: IMAGES.ANKOKO_09, label: '龍事件 ― 謎組織目撃' },
                      { img: IMAGES.ANKOKO_10, label: '龍事件 ― 鬼角 業務復帰後' },
                    ].map((item, i) => (
                      <div key={i} className="relative overflow-hidden rounded group cursor-pointer" onClick={()=>zoom(item.img, item.label)}>
                        <img src={item.img} alt={`龍事件取材${i+1}`} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"/>
                        {i === 0 && <div className="absolute top-0 left-0 bg-red-600 text-white text-[8px] font-black px-1">事件現場 / Incident Scene</div>}
                        {i === 1 && <div className="absolute top-0 left-0 bg-orange-600 text-white text-[8px] font-black px-1">救出直後 / Immediately After Rescue</div>}
                        {i === 2 && <div className="absolute top-0 left-0 bg-purple-600 text-white text-[8px] font-black px-1">謎組織目撃 / Mystery Org. Sighted</div>}
                        {i === 3 && <div className="absolute top-0 left-0 bg-yellow-500 text-black text-[8px] font-black px-1">復帰後 / Back on Duty</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 魔都での人気 */}
              <div className="bg-[#faf0ff] border border-purple-200 rounded p-3">
                <div className="font-black text-xs text-purple-700 mb-2">📊 魔都市内人気調査（2226年4月）</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-[10px]">
                  {[
                    { label:"好感度 / Favorability", value:"78%", note:"不真面目さが好評 / Deadpan style praised", color:"text-pink-600" },
                    { label:"認知度 / Recognition", value:"91%", note:"龍事件で一躍有名 / Dragon incident made her famous", color:"text-purple-600" },
                    { label:"応援したい / Support", value:"85%", note:"「死なないでほしい」/ 'Please don't die'", color:"text-blue-600" },
                    { label:"グッズ欲しい / Merch demand", value:"63%", note:"「存在がコンテンツ」/ 'She is the content'", color:"text-green-600" },
                  ].map((item, i) => (
                    <div key={i} className="bg-white border border-purple-100 rounded p-2">
                      <div className={`text-xl font-black ${item.color}`}>{item.value}</div>
                      <div className="font-bold text-[#333]">{item.label}</div>
                      <div className="text-[8px] text-[#888]">{item.note}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[9px] text-[#888] text-right">※調査対象：魔都市在住市民1,200名（獣人含む）<br/><span className="italic">Survey: 1,200 Demon City residents (beast-persons included)</span></div>
              </div>

              {/* フォトストリップ（追加画像） */}
              <div>
                <div className="text-[10px] font-bold text-[#333] mb-1">📸 活動フォトアーカイブ / Activity Photo Archive</div>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-1">
                  {[
                    { img: IMAGES.ANKOKO_11, label: 'Anko Kikaku — Activity Archive 1 / 活動アーカイブ 1' },
                    { img: IMAGES.ANKOKO_12, label: 'Anko Kikaku — Activity Archive 2 / 活動アーカイブ 2' },
                    { img: IMAGES.ANKOKO_13, label: 'Anko Kikaku — Activity Archive 3 / 活動アーカイブ 3' },
                    { img: IMAGES.ANKOKO_ADD01, label: 'Anko Kikaku — Activity Archive 4 / 活動アーカイブ 4' },
                    { img: IMAGES.ANKOKO_ADD02, label: 'Anko Kikaku — Activity Archive 5 / 活動アーカイブ 5' },
                    { img: IMAGES.ANKOKO_ADD03, label: 'Anko Kikaku — Activity Archive 6 / 活動アーカイブ 6' },
                    { img: IMAGES.ANKOKO_ADD04, label: 'Anko Kikaku — Activity Archive 7 / 活動アーカイブ 7' },
                    { img: IMAGES.ANKOKO_ADD05, label: 'Anko Kikaku — Activity Archive 8 / 活動アーカイブ 8' },
                  ].map((item, i) => (
                    <div key={i} className="overflow-hidden rounded aspect-square group cursor-pointer" onClick={()=>zoom(item.img, item.label)}>
                      <img src={item.img} alt={item.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"/>
                    </div>
                  ))}
                </div>
              </div>

              {/* 底部煽りバナー */}
              <div className="border-t border-pink-200 pt-3 flex flex-wrap gap-2 items-center justify-between">
                <div className="text-[9px] text-[#888]">
                  ※本記事は魔都市役所広報課が作成しました。アイドル本人の意向は一切反映されていません。<br/>
                  <span className="text-[8px]">This article was produced by Demon City Hall PR Division. The idol's personal opinions have not been considered.</span>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <span className="bg-pink-100 text-pink-700 text-[9px] px-2 py-0.5 rounded-full font-bold">🎤 やる気なし</span>
                  <span className="bg-orange-100 text-orange-700 text-[9px] px-2 py-0.5 rounded-full font-bold">🔥 実績あり</span>
                  <span className="bg-purple-100 text-purple-700 text-[9px] px-2 py-0.5 rounded-full font-bold">💊 変容中</span>
                  <span className="bg-red-100 text-red-700 text-[9px] px-2 py-0.5 rounded-full font-bold">🐉 龍に食われた</span>
                </div>
              </div>
            </div>
          </div>

          {/* ★★★ 広報課職員による特務部隊取材レビュー ★★★ */}
          <div className="bg-white border-2 border-amber-400 rounded overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-amber-700 to-amber-900 text-white px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="bg-white text-amber-700 text-[9px] font-black px-2 py-0.5 rounded-full">📋 FIELD REVIEW</span>
                <span className="font-black text-sm">広報課職員による特務部隊取材 ― 現場レポート</span>
              </div>
              <span className="text-[9px] text-white/70 hidden md:block">PR Division Field Coverage Report / 2226年4月</span>
            </div>
            <div className="p-4 space-y-4">
              {/* 取材班プロフィール */}
              <div className="bg-amber-50 border border-amber-200 rounded p-3 flex flex-col md:flex-row gap-3 items-start">
                <div className="shrink-0">
                  <div className="w-16 h-16 bg-amber-200 rounded-full flex items-center justify-center text-3xl border-2 border-amber-400">🦊</div>
                </div>
                <div className="flex-1 text-xs">
                  <div className="font-black text-amber-900 text-sm mb-0.5">広報課 一般職員 ― 小宮山 燈子（こみやま とうこ）<span className="ml-2 font-normal text-[10px] text-amber-700">/ Touko Komiyama — PR Division Staff</span></div>
                  <div className="text-[10px] text-amber-700 mb-1">魔都市役所 広報課 3年目 / 担当：特務部隊広報連携 <span className="text-amber-600/70">— Demon City Hall PR Div., Year 3, Task Force Liaison</span></div>
                  <div className="text-[10px] text-[#666] leading-relaxed">
                    「辞令が出て、特務部隊の同行取材を命じられました。鬼角先輩から『行ってみな、面白いから』と言われましたが、先輩の『面白い』は信頼できません。結果的に帰ってこられたので良かったです。」
                    <span className="text-[9px] text-[#999] block mt-1 italic">"I was ordered to accompany the task force. Senpai Kikaku said it would be 'interesting' — her version of interesting cannot be trusted. At least I made it back alive."</span>
                  </div>
                </div>
              </div>

              {/* 取材写真グリッド */}
              <div>
                <div className="text-[10px] font-bold text-[#333] mb-2">📸 取材中に撮影した写真 / Field Photos</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { img: IMAGES.ANKOKO_SPECIAL_NEW01, label: 'ドーベルマン特務隊員 2名 ― 夜の路地で任務待機中', cap: '夜の路地 ― 特務隊員待機', capEn: 'Night alley — Task force on standby' },
                    { img: IMAGES.ANKOKO_SPECIAL_NEW02, label: '鬼角が取材マイクを向ける ― 後方にドーベルマン隊員', cap: '鬼角 ― マイクで取材中', capEn: 'Kikaku — on-mic field interview' },
                    { img: IMAGES.ANKOKO_SPECIAL_NEW03, label: '作戦前ブリーフィング ― 鬼角がクリップボードで確認', cap: '作戦前ブリーフィング', capEn: 'Pre-operation briefing' },
                    { img: IMAGES.ANKOKO_SPECIAL_NEW04, label: '鬼角（IDカード確認）とドーベルマン隊員 ― 観察記録フォームを持参', cap: '鬼角と隊員 ― 取材記録', capEn: 'Kikaku & agent — field log' },
                    { img: IMAGES.ANKOKO_SPECIAL_NEW05, label: '虎顔特務隊員が獣人を逮捕・制圧 ― 夜の繁華街', cap: '獣人 逮捕・制圧の現場', capEn: 'Beast-person apprehended' },
                    { img: IMAGES.FIELD_INVESTIGATION_TEAM_01, label: '広報課 調査班 ― 倒壊ビル周辺で現地踏査', cap: '調査班の現地踏査', capEn: 'PR team — on-site survey' },
                  ].map((item, i) => (
                    <div key={i} className="border border-amber-100 rounded overflow-hidden cursor-pointer group" onClick={()=>zoom(item.img, item.label)}>
                      <img src={item.img} alt={item.cap} className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"/>
                      <div className="px-2 py-1 bg-amber-50">
                        <div className="text-[9px] text-amber-800 font-semibold">{item.cap}</div>
                        <div className="text-[8px] text-amber-600/70 italic">{(item as any).capEn}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 長文レビュー */}
              <div className="space-y-3 text-xs text-[#555] leading-relaxed">
                <div className="bg-amber-100 border-l-4 border-amber-600 pl-3 py-2">
                  <div className="font-black text-amber-900 mb-1">【取材レポート】特務部隊 川口区急襲作戦 同行記録</div>
                  <div className="text-[10px] text-amber-800">記録：小宮山 燈子（広報課） ／ 作成：2226年4月9日 / Recorded by T. Komiyama, PR Division</div>
                </div>

                <div className="space-y-2">
                  <p className="leading-relaxed">
                    辞令が届いたのは4月1日の朝だった。内容は「特務部隊・川口区急襲作戦への同行取材を命ずる」。
                    エイプリルフールだと思ったが、上司の顔は真剣だった。鬼角先輩に相談すると「あそこ面白いよ、ちゃんと帰ってこられればね」と言われた。
                    <strong>「ちゃんと」の部分が気になったが、聞き返す勇気はなかった。</strong>
                    <span className="text-[9px] text-[#888] block mt-1">The assignment arrived on the morning of April 1st: "You are ordered to accompany the Special Task Force on the Kawaguchi District raid." I thought it was an April Fool's joke — but my supervisor's face was dead serious. I consulted Senior Kikaku. She said: "It's fun over there — if you actually come back." The word 'actually' concerned me, but I didn't have the courage to ask further.</span>
                  </p>
                  <p className="leading-relaxed">
                    集合場所は川口区・旧工業地帯の廃倉庫前、午前3時。
                    特務部隊員の顔はほぼ見えず、装備だけが月光に反射していた。
                    隊長らしき人物（虎顔の獣人、推定2m超）から「取材クルーは後方待機。突入後15秒で撮影許可。フラッシュは使うな。音を立てるな。逃げるな。死ぬな。」と7項目の注意事項を告げられた。
                    <span className="text-[9px] text-[#888] block mt-1">The briefing was: "Stay back. Enter 15 sec after breach. No flash. No sound. Don't run. Don't die." — Seven instructions total.</span>
                  </p>
                  <p className="leading-relaxed">
                    突入は静かだった。静かすぎて逆に怖かった。
                    雑居ビルの3階、窓ガラスが割れる音と同時に隊員が突入。
                    私は外の非常階段から撮影を試みた。
                    そのときの写真が上の2枚目だ。ピントが若干甘いのは私の手が震えていたからである。
                    <strong>これは掲載写真なので修正されているが、オリジナルはもっとブレている。</strong>
                    <span className="text-[9px] text-[#888] block mt-1">The breach was silent. Too silent. I photographed from the emergency staircase. My hands were trembling. The published version has been stabilized.</span>
                  </p>

                  <div className="bg-[#fff8e8] border border-amber-300 rounded p-3">
                    <div className="font-bold text-amber-800 mb-1">🎙️ 取材中の印象的な出来事 / Memorable Moments on Assignment</div>
                    <ul className="text-[10px] text-[#555] space-y-2 leading-relaxed">
                      <li>・ 突入開始15秒後に撮影許可が出たが、その15秒間がキャリア史上最も長い15秒だった。<br/><span className="text-[9px] text-[#888] italic">Shooting clearance came 15 seconds after breach. Those were the longest 15 seconds of my career.</span></li>
                      <li>・ 制圧された獣人（犬顔の中型種）が「ここに来た理由」を聞かれ「くずねこを探していた」と答えていた。市の調査チームが現場記録として保存中。<br/><span className="text-[9px] text-[#888] italic">The apprehended beast-person (canine type, medium build) stated they had come "to find Kuzuneko." Archived as an official city investigation record.</span></li>
                      <li>・ 作戦完了後、隊長が「お疲れ様」と私に言った。その後すぐに「飯食うか？」と誘われた。断った。なんとなく怖かったから。<br/><span className="text-[9px] text-[#888] italic">After the operation, the commander said "Good work" — then immediately asked if I wanted to eat. I declined. Instinct.</span></li>
                      <li>・ 帰り道、鬼角先輩から「どうだった？」とメッセージが来た。「怖かったです」と返信すると「そう、面白いでしょ」と返ってきた。先輩の感覚は信用できない。<br/><span className="text-[9px] text-[#888] italic">Senpai Kikaku messaged on the way back: "How was it?" I replied: "Scary." She replied: "Right? Fun, isn't it." Her definition of fun cannot be trusted.</span></li>
                    </ul>
                  </div>

                  <p className="leading-relaxed">
                    特務部隊の組織詳細は非公開のため、本レポートでも記載を避ける。
                    わかったのは、彼らが市の治安維持に実際に貢献しており、
                    その存在が「くずねこ問題」の実態解明にも関わっている、ということだ。
                    「くずねこが悪い」という市民の認識は単純化しすぎており、
                    特務部隊の視点では「環境要因・模倣犯・偶発事故」が複雑に絡み合っている。
                    <span className="text-[9px] text-[#888] block mt-1">The Kuzuneko situation is far more complex than public perception suggests. The task force views it as an interplay of environmental factors, imitators, and coincidental accidents.</span>
                  </p>

                  <div className="bg-[#f0f8ff] border border-blue-200 rounded p-3 text-[10px] text-[#555]">
                    <div className="font-bold text-blue-800 mb-1">📌 広報課員・小宮山による取材総評</div>
                    <p className="leading-relaxed">
                      「特務部隊は怖いが、仕事はできる。市民の安全を守っているのは確かだ。
                      ただ、一般職員が同行するには精神的ハードルが高すぎる。
                      次回の取材は別の人に頼みたい。」
                    </p>
                    <p className="text-[9px] text-[#888] mt-1 italic">
                      "The task force is intimidating but professional. I would prefer not to be assigned again. Please assign someone else next time."
                      ― Touko Komiyama, PR Division, 2nd Year
                    </p>
                    <div className="mt-2 text-[9px] text-[#aaa]">※本レポートは広報課の公式記録です。職員の個人的感想は一部含まれますが、事実の記述については正確性を期しています。<br/><span className="italic">This is an official PR Division record. Personal impressions are noted but all factual statements have been verified for accuracy.</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ★★★ 鬼角本人の広報体験記事 ★★★ */}
          <div className="bg-white border-2 border-pink-200 rounded overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-pink-700 to-rose-800 text-white px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="bg-white text-pink-700 text-[9px] font-black px-2 py-0.5 rounded-full">✍️ PERSONAL ESSAY</span>
                <span className="font-black text-sm">鬼角暗子 本人寄稿：広報アイドル、やってみた話</span>
              </div>
              <span className="text-[9px] text-white/70 hidden md:block">By Anko Kikaku — PR Idol 21st Gen. / 2226年4月</span>
            </div>
            <div className="p-4 space-y-4">
              {/* 本人写真 + 導入 */}
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <div className="shrink-0 space-y-2">
                  <img src={IMAGES.ANKOKO_PROFILE_NEW02} alt="鬼角暗子 本人写真" className="w-36 h-44 object-cover rounded border-2 border-pink-300 cursor-pointer" onClick={()=>zoom(IMAGES.ANKOKO_PROFILE_NEW02, '鬼角 暗子 ― 寄稿者写真')}/>
                  <div className="text-center text-[9px] text-[#888]">鬼角 暗子<br/>第21代目 魔都市公認<br/>広報アイドル</div>
                </div>
                <div className="flex-1 space-y-2 text-xs text-[#555] leading-relaxed">
                  <div className="bg-pink-50 border-l-4 border-pink-500 pl-3 py-2">
                    <div className="font-black text-pink-800 text-sm mb-1">はじめに</div>
                    <p>
                      「寄稿しろ」と言われた。断れなかった。書く。
                    </p>
                  </div>
                  <p>
                    広報アイドルになったのは、面接で「やる気ありますか」と聞かれて「ない」と答えたら採用された。
                    採用担当の人は「それが魔都スタイルです」と言っていた。意味がわからなかったが、給料はちゃんと出るので続けている。
                    <span className="text-[9px] text-[#888] block mt-1">"I was hired after answering 'No' to the question of whether I was motivated. The hiring manager said that was 'the Demon City way.' I still don't understand what that means."</span>
                  </p>
                  <p>
                    最初の一ヶ月は、市内各所でポスターを持って立ったり、イベントでマイクを持ったりした。
                    楽しくはなかったが、つらくもなかった。
                    <strong>魔都市は刺激が多すぎて、退屈する暇がない。それだけは認める。</strong>
                    <span className="text-[9px] text-[#888] block mt-1">"The city is too stimulating to be boring. I'll give it that."</span>
                  </p>
                </div>
              </div>

              {/* くずねこと一緒に写った写真 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-pink-200 rounded overflow-hidden cursor-pointer group" onClick={()=>zoom(IMAGES.ANKOKO_WITH_KUZUNEKO_NEW, '鬼角暗子とくずねこ ― 路地裏でのスナップ（広報課撮影）')}>
                  <img src={IMAGES.ANKOKO_WITH_KUZUNEKO_NEW} alt="鬼角とくずねこ" className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"/>
                  <div className="p-2 bg-pink-50">
                    <div className="text-[10px] font-bold text-pink-700">📸 路地裏でのスナップ（広報課撮影）/ Back-alley Snapshot (PR Div.)</div>
                    <div className="text-[9px] text-[#888]">川口区・路地裏 2226年3月 ／ 鬼角がスマホで上を撮影、くずねこが上から跳躍してきた瞬間<br/><span className="italic">Kawaguchi Ward, March 2226 — Kikaku filming upward; Kuzuneko leaping down from above.</span></div>
                  </div>
                </div>
                <div className="space-y-3 text-xs text-[#555] leading-relaxed">
                  <div className="bg-pink-50 border border-pink-200 rounded p-3">
                    <div className="font-bold text-pink-700 mb-1">💬 くずねこについて（本人コメント）</div>
                    <p className="text-[10px] leading-relaxed">
                      「なんか、向こうから来るんですよね。私が広報してると。
                      特に害はないんですけど、カメラに写り込むのが困る。
                      でも追い払うのもかわいそうだし。
                      別に嫌いじゃないです。でも好きとも言ってないです。
                      微妙な関係です。」
                    </p>
                    <p className="text-[9px] text-[#888] mt-1 italic">
                      "It just shows up when I'm working. I don't particularly dislike it. But I haven't said I like it either. It's a complicated relationship."
                    </p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-300 rounded p-3">
                    <div className="font-bold text-yellow-700 mb-1">📝 広報課注記 / PR Division Note</div>
                    <div className="text-[9px] text-[#555] leading-relaxed">
                      この写真は広報課が撮影。くずねこの自発的接触行動は「市認知度向上への貢献」として市公式記録に登録済み。
                      くずねこの市民への好感度と鬼角氏の活動相乗効果を現在分析中。
                      <br/><span className="italic text-[#888]">Photo taken by the PR Division. Kuzuneko's spontaneous approach has been logged as a "contribution to public awareness" in official city records. A synergy analysis with Kikaku's activities is currently underway.</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 本文つづき */}
              <div className="space-y-3 text-xs text-[#555] leading-relaxed">
                <div className="border-l-4 border-pink-300 pl-3 py-2">
                  <div className="font-bold text-pink-700 mb-1">龍に食われた日のこと</div>
                  <p>
                    2月17日。広報バナーを持って魔都タワー前に立っていたら、空から龍が降ってきた。
                    バナーが食べ物に見えたらしく、私ごと飲み込まれた。
                    中は思ったより広かった。真っ暗だったけど、体感3分くらいで出てきた。
                    謎の組織が助けてくれたらしい。顔も見えなかった。
                    <strong>「ありがとうございます」は言ったけど、頼んでないのは事実だし、恩着せがましいのは嫌い。</strong>
                    <span className="text-[9px] text-[#888] block mt-1">
                      "On February 17th, I was swallowed by a dragon. The inside was darker than expected. I was out in about three minutes. I said thank you, but I want to be clear: I did not ask for help."
                    </span>
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { img: IMAGES.ANKOKO_PR01, label: 'Anko Kikaku — Pamphlet distribution to children / 子どもたちへのパンフレット配布（市内広報）', cap: '子どもへのパンフレット配布 / Pamphlet distribution to children' },
                    { img: IMAGES.ANKOKO_PR02, label: 'Anko Kikaku — Moment of dragon appearance (later swallowed) / 龍出現の瞬間（後に飲み込まれる）', cap: '龍出現 ― 飲み込まれる直前 / Dragon appears — moments before incident' },
                    { img: IMAGES.ANKOKO_PR03, label: 'Anko Kikaku — Inside City Hall, holding underground garden guide / 市役所内部での広報活動', cap: '市役所内部での広報活動 / PR activity inside City Hall' },
                  ].map((item, i) => (
                    <div key={i} className="border border-pink-100 rounded overflow-hidden cursor-pointer group" onClick={()=>zoom(item.img, item.label)}>
                      <img src={item.img} alt={item.label} className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"/>
                      <div className="px-2 py-1 bg-pink-50">
                        <div className="text-[9px] text-pink-700 font-semibold">{item.cap}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-l-4 border-purple-300 pl-3 py-2">
                  <div className="font-bold text-purple-700 mb-1">仕事について、正直なところ</div>
                  <p>
                    正直、広報アイドルがどういう仕事か最初はよくわかっていなかった。
                    今もよくわかっていない部分はある。
                    でも、文化イベントの来場者が増えているとか、
                    市への問い合わせが増えているとか、
                    そういう結果の数字は目に入る。
                    <strong>やる気がないのと、やってないのは違う。それだけ。</strong>
                    <span className="text-[9px] text-[#888] block mt-1">
                      "Not having motivation and not doing the work are two different things. That's all I have to say."
                    </span>
                  </p>
                </div>

                <div className="border-l-4 border-blue-300 pl-3 py-2">
                  <div className="font-bold text-blue-700 mb-1">魔都市で生きていくこと</div>
                  <p>
                    この街は変だ。龍が出るし、獣人がいるし、イカ女もいる。
                    でも変じゃない街よりは、飽きない。
                    広報アイドルという仕事も変だけど、
                    変なことを変だと言いながら続けていける気がする。
                    <strong>まだ辞めていない。それが今の私の全部。</strong>
                    <span className="text-[9px] text-[#888] block mt-1">
                      "This city is strange. But I haven't quit yet. That's everything I have to say."
                    </span>
                  </p>
                </div>

                <div className="bg-pink-50 border border-pink-200 rounded p-3 text-[10px]">
                  <div className="font-black text-pink-700 mb-1">編集後記（広報課）</div>
                  <p className="text-[#555] leading-relaxed">
                    本記事は本人に原稿を一任しました。
                    修正依頼を出したところ「これが全部だから修正する箇所ない」と返答がありました。
                    広報課としては一切の責任を持ちません。
                    ただし、内容は事実確認済みです。
                  </p>
                  <p className="text-[9px] text-[#aaa] mt-1 italic">
                    Editor's note: The text was submitted as-is. Revision requests were rejected on the grounds that "this is everything." PR Division disclaims all responsibility. All facts have been verified.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* くずねこのビル破壊・ロボット破壊・無銭飲食 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#1a3a5c]" accent="bg-yellow-300" en="Investigation 1 — Building & Robot Damage: Accident or Imitator?" ja="取材①：ビル破損・ロボット損壊は事故か模倣犯か" />
            <div className="p-4 text-xs text-[#555] leading-relaxed space-y-3">
              <div className="bg-[#e8f4e8] border-l-4 border-green-500 pl-3 py-2">
                <div className="font-bold text-green-800 mb-1">📋 Conclusion: Accident & Imitator Activity / 結論：事故および模倣犯によるもの</div>
                <div className="text-[10px] text-green-700">
                  On-site investigation has determined that the majority of structural damage attributed to Kuzuneko is the result of accidents or copycat behavior. No evidence of intentional destruction was found.<br/>
                  現地調査の結果、くずねこに帰せられた建物損壊の大部分は事故または模倣犯によるものであることが判明しました。意図的破壊の証拠は見つかりませんでした。
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div className="border border-[#eee] rounded p-3">
                  <div className="font-bold text-sm text-[#1a3a5c] mb-1">🏢 Building Damage Incident / ビル損壊事案</div>
                  <div className="text-[9px] text-[#555] leading-relaxed space-y-1">
                    <p><strong>Case No. DMC-2226-004B:</strong> Kawaguchi South, Building 7 chimney collapse.<br/>南川口区 第7ビル煙突崩壊事案</p>
                    <p><strong>Field finding:</strong> Security footage showed a dragon (Type C, cloud-body) passed overhead at 02:30, creating significant wind pressure. The chimney was already structurally compromised. Kuzuneko was in the vicinity but had no direct involvement.<br/>
                    <span className="text-[#888]"><strong>現地調査結果：</strong>監視カメラ映像により、午前2時30分にC型雲体龍が上空を通過し、強烈な気圧変動が発生したことが判明。煙突はすでに構造上の欠陥があり、くずねこは付近にいたが直接関与していない。</span></p>
                    <div className="bg-[#f0f8ff] rounded px-2 py-1 text-[8px] font-bold text-blue-700">
                      ✅ Cause: Dragon-induced wind pressure + pre-existing structural defect<br/>
                      ✅ 原因：龍の風圧＋既存の構造欠陥
                    </div>
                  </div>
                </div>
                <div className="border border-[#eee] rounded p-3">
                  <div className="font-bold text-sm text-[#1a3a5c] mb-1">🤖 Robot Damage Incident / ロボット損壊事案</div>
                  <div className="text-[9px] text-[#555] leading-relaxed space-y-1">
                    <p><strong>Case No. DMC-2226-007R:</strong> Patrol bot destruction, Kawaguchi East District.<br/>川口市東区 自律巡回ロボット損壊事案</p>
                    <p><strong>Field finding:</strong> Three witnesses confirmed the individual responsible was wearing a black sweater and cat-ear headband — consistent with known Kuzuneko imitation behavior. The actual Kuzuneko was confirmed to be asleep under a vending machine at the time of the incident (CCTV verified).<br/>
                    <span className="text-[#888]"><strong>現地調査結果：</strong>目撃者3名が、犯人は黒いセーターに猫耳を着用していたことを証言。これはくずねこ模倣行動に一致する。事件発生時、本物のくずねこは自販機の下で寝ていたことがCCTVで確認済み。</span></p>
                    <div className="bg-[#fff8e8] rounded px-2 py-1 text-[8px] font-bold text-orange-700">
                      ✅ Cause: Confirmed imitator / copycat behavior<br/>
                      ✅ 原因：模倣犯による行為と確定
                    </div>
                  </div>
                </div>
              </div>
              {/* 現場写真 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                <div className="border border-[#eee] rounded overflow-hidden">
                  <img src={IMAGES.FIELD_REPORT_01_NEW} onClick={()=>zoom(IMAGES.FIELD_REPORT_01_NEW, "Collapsed building investigation")} alt="Collapsed building investigation" className="w-full h-36 object-cover cursor-pointer"/>
                  <div className="p-2 text-[9px] text-[#888]">
                    <strong className="text-[#555]">📸 On-site Photo: Building damage / 現場写真：ビル損壊</strong><br/>
                    Scene of the chimney collapse incident. Investigators confirmed structural pre-conditions. Dragon passage timing confirmed by local sensors.<br/>煙突崩壊現場。調査員が構造上の前提条件を確認。地域センサーにより龍の通過タイミングを確認。
                  </div>
                </div>
                <div className="border border-[#eee] rounded overflow-hidden">
                  <img src={IMAGES.FIELD_REPORT_02_NEW} onClick={()=>zoom(IMAGES.FIELD_REPORT_02_NEW, "Collapsed building interior")} alt="Collapsed building interior" className="w-full h-36 object-cover cursor-pointer"/>
                  <div className="p-2 text-[9px] text-[#888]">
                    <strong className="text-[#555]">📸 On-site Photo: Patrol bot wreckage / 現場写真：巡回ロボット残骸</strong><br/>
                    Remains of the patrol bot after the incident. Forensic analysis revealed tool marks inconsistent with Kuzuneko's physical capabilities.<br/>事案後のロボット残骸。法科学分析により、くずねこの能力とは一致しない工具痕が発見された。
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 無銭飲食 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#5a3a00]" accent="bg-yellow-400" en="Investigation 2 — Dine-and-Dash Incidents: Pattern Analysis" ja="取材②：無銭飲食事案：パターン分析" />
            <div className="p-4 text-xs text-[#555] leading-relaxed space-y-3">
              <div className="bg-[#fff8dc] border-l-4 border-yellow-500 pl-3 py-2">
                <div className="font-bold text-yellow-800 mb-1">📋 Conclusion: Predominantly Imitator Activity / 結論：大部分が模倣犯によるもの</div>
                <div className="text-[10px] text-yellow-700">
                  Of 23 dine-and-dash incidents attributed to Kuzuneko this year, field investigation confirmed 19 were committed by imitators. In 3 cases, evidence of actual Kuzuneko presence was found — but the entity appeared confused and likely unaware of payment customs.<br/>
                  今年度に「くずねこによる」とされた23件の無銭飲食のうち、19件は模倣犯と確認。3件では本物のくずねこの存在を示す証拠が見つかりましたが、支払い習慣を知らなかった可能性が高いと判断されます。
                </div>
              </div>
              <div className="border border-[#eee] rounded p-3">
                <div className="font-bold text-sm text-[#1a3a5c] mb-2">Representative Case / 代表事例</div>
                <div className="flex gap-3">
                  <img src={IMAGES.KUZUNEKO_NEW_01} onClick={()=>zoom(IMAGES.KUZUNEKO_NEW_01, "kuzuneko")} alt="kuzuneko" className="w-20 h-20 object-cover object-top rounded shrink-0 grayscale-[20%] cursor-pointer"/>
                  <div className="text-[9px] text-[#555] leading-relaxed">
                    <p><strong>Case No. DMC-2226-011F:</strong> Ramen shop, Kawaguchi Central. Owner reported "cat-eared customer" eating two bowls and leaving without paying.<br/>川口中央区ラーメン店。「猫耳の客」が2杯食べて無銭飲食した、と店主が届け出。</p>
                    <p className="mt-1">CCTV review showed the subject appeared genuinely startled when presented with a bill, suggesting the concept of payment may not be understood. Case reclassified from "deliberate theft" to "cultural misunderstanding."<br/>
                    防犯カメラ映像では、会計時に心底驚いた様子が確認され、支払いという概念を理解していない可能性がある。「意図的な窃盗」から「文化的誤解」に分類変更。</p>
                    <div className="bg-[#e8f4e8] rounded px-2 py-1 text-[8px] font-bold text-green-700 mt-1">
                      ✅ Note: Shop was subsequently compensated by Beastman Foundation<br/>
                      ✅ 備考：獣人財団により店主への補償済み
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* イカ女の謎記事 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#005a7a]" accent="bg-cyan-300" en="Special Feature — The 'Squid Woman': A Rare Mystery Creature" ja="特集：「イカ女」 ― とてもレアな謎の生き物" />
            <div className="p-4 text-xs text-[#555] leading-relaxed">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div>
                  <img src={IMAGES.IKA_WOMAN_NEW} onClick={()=>zoom(IMAGES.IKA_WOMAN_NEW, "イカ女")} alt="イカ女" className="w-full h-40 object-cover rounded cursor-pointer"/>
                  <div className="text-[9px] text-[#888] mt-1">Spotted in Kawaguchi Nishiguchi Park / 川口市西口公園での目撃</div>
                </div>
                <div>
                  <img src={IMAGES.IMG_2K_202604161453_1_65} onClick={()=>zoom(IMAGES.IMG_2K_202604161453_1_65, "イカ女 2")} alt="イカ女 2" className="w-full h-40 object-cover rounded cursor-pointer"/>
                  <div className="text-[9px] text-[#888] mt-1">Spotted in shopping district / 商店街での目撃</div>
                </div>
              </div>

              <div className="bg-[#e0f4ff] border-l-4 border-cyan-500 pl-4 py-3 mb-3">
                <div className="font-black text-base text-[#005a7a] mb-2">
                  🦑 Official Statement Regarding the "Squid Woman"<br/>
                  <span className="text-sm text-[#0080aa]">「イカ女」についての公式見解</span>
                </div>
                <div className="text-[11px] text-[#005a7a] leading-relaxed font-semibold space-y-1.5">
                  <p>
                    This is a very rare and mysterious creature. Please treat her with care.<br/>
                    <span className="font-normal text-[#0080aa]">これは、とてもレアな謎の生き物です。大切にしてください。</span>
                  </p>
                  <p>
                    Do NOT attempt to exterminate or harm her in any way.<br/>
                    <span className="font-normal text-[#0080aa]">駆除はしないでください。危害を加えることも禁止します。</span>
                  </p>
                  <p className="text-[#c00] font-black">
                    Do NOT eat her. This is strictly prohibited.<br/>
                    <span className="text-[#c00]">食べるのもダメです。これは厳しく禁止されています。</span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border border-[#ddd] rounded p-3">
                  <div className="font-bold text-sm text-[#1a3a5c] mb-1">📊 Known Facts / 判明している事実</div>
                  <ul className="text-[9px] text-[#555] space-y-1 leading-relaxed">
                    <li>• Appears to be female, normal human upper body / 女性外見、上半身は一般的な人間</li>
                    <li>• Lower body consists of multiple cephalopod tentacles / 下半身はイカ・タコ類の触手で構成</li>
                    <li>• Appears to navigate urban environments without distress / 都市環境を問題なく移動している</li>
                    <li>• No aggressive behavior documented / 攻撃的行動の記録なし</li>
                    <li>• Appears to enjoy sunshine and parks / 公園や日差しを好む様子</li>
                    <li>• Classification: <strong className="text-cyan-700">Unknown — Possibly Protected Species</strong> / 分類：不明 ― 保護対象の可能性あり</li>
                  </ul>
                </div>
                <div className="border border-[#ddd] rounded p-3">
                  <div className="font-bold text-sm text-[#1a3a5c] mb-1">⚠ Prohibited Actions / 禁止行為</div>
                  <ul className="text-[9px] text-[#555] space-y-1 leading-relaxed">
                    <li className="text-[#c00] font-bold">❌ Extermination / 駆除</li>
                    <li className="text-[#c00] font-bold">❌ Consumption (eating) / 食用目的での捕獲・摂食</li>
                    <li className="text-[#c00] font-bold">❌ Harassment / ハラスメント行為</li>
                    <li className="text-[#c00] font-bold">❌ Attempting to capture / 捕獲の試み</li>
                    <li className="text-green-700 font-bold">✅ Observation from safe distance / 安全な距離からの観察</li>
                    <li className="text-green-700 font-bold">✅ Reporting sightings to city / 目撃情報の市への報告</li>
                    <li className="text-green-700 font-bold">✅ Treating with respect / 敬意を持った接し方</li>
                  </ul>
                </div>
              </div>

              <div className="mt-3 bg-[#f0f8ff] rounded p-3 text-[9px] text-[#444] leading-relaxed">
                <strong>Research Note / 研究メモ：</strong>
                The Demon City Pest Research Division has categorized the Squid Woman as a <strong>"Rare Unclassified Harmless Entity" (RUHE)</strong>, distinct from designated pests. Ongoing research is being conducted with full cooperation from the Beastman Foundation. Citizens observing the Squid Woman should report to City Hall but must not interfere with the entity.<br/>
                魔都市害獣研究課は「イカ女」を「希少未分類無害存在 (RUHE)」として、指定害獣とは別に分類しています。獣人財団の全面的な協力のもと、継続的な調査が行われています。目撃した市民は市役所へ報告していただきますが、決して干渉しないようにしてください。
              </div>
            </div>
          </div>

          {/* 通報ボタン */}
          <div className="bg-[#1a3a5c] text-white rounded p-4 flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <div className="font-black text-sm mb-1">Have information about these incidents? / これらの事案に情報をお持ちですか？</div>
              <div className="text-[10px] text-white/70">All tips are treated confidentially by the Demon City investigation team. / すべての情報提供は魔都市調査チームが厳秘として取り扱います。</div>
            </div>
            <button onClick={onReport} className="bg-white text-[#1a3a5c] font-black text-xs px-5 py-2.5 rounded hover:bg-[#e0e8f0] transition-colors shrink-0">
              📬 Submit Tip / 情報提供フォーム
            </button>
          </div>
        </main>

        {/* 右サイドバー */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#2a5a2a]" accent="bg-green-300" en="Nekomata's Comment" ja="ネコマタ氏のコメント" />
            <img src={IMAGES.NEKOMATA_FORUM_2012} onClick={()=>zoom(IMAGES.NEKOMATA_FORUM_2012, "Nekomata Award")} alt="Nekomata Award" className="w-full h-28 object-cover object-top cursor-pointer"/>
            <div className="p-2 text-[9px] text-[#555] leading-relaxed">
              <em>"We are committed to bringing truth to every incident. Many cases are misattributed. Please trust the investigative process."</em><br/>
              <span className="text-[#888]">「すべての事案に真実を追求します。多くが誤帰責です。調査プロセスをご信頼ください。」― ネコマタCEO</span>
            </div>
          </div>

          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#555]" accent="bg-gray-300" en="Incident Summary 2226" ja="2226年 事案概要" />
            <div className="p-3 text-xs text-[#555] space-y-1.5">
              {[
                { label:"Kuzuneko-attributed cases / くずねこ帰責", value:"31件" },
                { label:"→ Confirmed imitators / うち模倣犯", value:"22件", color:"text-orange-600" },
                { label:"→ Accidents / うち事故", value:"7件", color:"text-blue-600" },
                { label:"→ Kuzuneko (confused) / 本人（困惑）", value:"2件", color:"text-green-600" },
                { label:"Squid Woman sightings / イカ女目撃", value:"4件", color:"text-cyan-600" },
              ].map((s, i) => (
                <div key={i} className={`flex justify-between border-b border-[#f0f0f0] pb-1 last:border-0 ${s.label.startsWith("→") ? "pl-3 text-[10px]" : "font-semibold"}`}>
                  <span>{s.label}</span>
                  <span className={`font-bold ${s.color || "text-[#333]"}`}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#e0f4ff] border border-cyan-200 rounded p-3">
            <div className="font-bold text-xs text-[#005a7a] mb-1">🦑 Squid Woman Sighting Tips / イカ女目撃のヒント</div>
            <ul className="text-[9px] text-[#555] space-y-1 leading-relaxed">
              <li>• More active in spring / 春に活動的になる</li>
              <li>• Prefers quieter areas / 静かな場所を好む</li>
              <li>• Drawn to seafood aromas / 魚介類の香りに引き寄せられる傾向</li>
              <li>• Appears at dawn and dusk / 夜明けと黄昏時に多く出現</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Page 4: ネコ族空中浮遊戦艦注意報
───────────────────────────────────────── */
function PageBattleship() {
  const zoom = useZoom();
  return (
    <div className="font-sans text-[#222]">
      {/* ヒーロービジュアル */}
      <div className="relative bg-[#0a0a1a] overflow-hidden">
        <div className="relative h-56 md:h-80 overflow-hidden">
          <img src={IMAGES.CAT_BATTLESHIP} alt="Cat Faction Battleship" onClick={()=>zoom(IMAGES.CAT_BATTLESHIP, "Feline Faction Airborne Battleship / ネコ族空中浮遊戦艦「猫の方舟」")} className="w-full h-full object-cover object-center opacity-85 cursor-pointer"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a1a]/95 via-[#0a0a1a]/50 to-transparent"/>
          <div className="absolute inset-0 flex items-center px-6 md:px-10">
            <div className="text-white max-w-lg">
              <div className="text-[10px] font-bold tracking-widest text-yellow-300 uppercase mb-1 animate-pulse">⚠ SPECIAL HAZARD ALERT — AERIAL THREAT / 特別危険警報 ― 空中脅威</div>
              <h1 className="text-2xl md:text-4xl font-black leading-tight mb-2">
                Feline Faction Airborne Battleship<br/>
                <span className="text-lg md:text-2xl font-bold text-yellow-200">ネコ族空中浮遊戦艦「猫の方舟」注意報</span>
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="bg-[#c00] text-white text-[10px] font-black px-3 py-1 rounded animate-pulse">THREAT LEVEL: CRITICAL</span>
                <span className="bg-yellow-600 text-white text-[10px] font-black px-3 py-1 rounded">CONFIRMED SIGHTING</span>
              </div>
            </div>
          </div>
        </div>
        {/* 緊急テロップ */}
        <div className="bg-[#660000] text-yellow-300 text-xs py-1.5 px-4 flex items-center gap-2 overflow-hidden">
          <span className="font-black animate-pulse shrink-0 bg-yellow-300 text-[#660000] px-1.5 py-0.5 rounded text-[10px]">🚨 CRITICAL</span>
          <div className="overflow-hidden whitespace-nowrap flex-1">
            <span className="inline-block" style={{animation:"marquee 25s linear infinite", display:"inline-block", width:"200%"}}>
              AERIAL THREAT CONFIRMED — Cat Faction Warship spotted over Kawaguchi district / 空中脅威確認 ― ネコ族戦艦が川口区上空に出現 ／ Do NOT fire weapons at the vessel — risk of friendly fire / 戦艦への武器使用禁止 ― 誤射の危険あり ／ Evacuate all rooftop areas immediately / 屋上エリアから直ちに避難 ／ Kuzuneko connection suspected — under investigation / くずねことの関連性を調査中
            </span>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-1 lg:grid-cols-[200px_1fr_220px] gap-4">

        {/* ─ 左列 ─ */}
        <aside className="space-y-4">
          <div className="bg-[#0a0a1a] text-yellow-300 rounded overflow-hidden border border-yellow-600/50">
            <div className="bg-yellow-600 text-white px-3 py-2 text-xs font-bold">🛸 Vessel Specs / 戦艦スペック</div>
            <div className="p-3 text-[10px] space-y-2 text-yellow-200">
              {[
                { k:"Class / 艦種", v:"Feline Ark-Class / 猫の方舟型" },
                { k:"Length / 全長", v:"Approx. 800m / 約800m" },
                { k:"Altitude / 浮上高度", v:"200–500m AGL / 地上200–500m" },
                { k:"Propulsion / 推進方式", v:"Unknown / 不明" },
                { k:"Armament / 武装", v:"Under Assessment / 評価中" },
                { k:"Crew / 乗組員", v:"Unknown beastmen / 不明獣人" },
              ].map((s,i) => (
                <div key={i} className="flex flex-col border-b border-yellow-600/20 pb-1.5 last:border-0">
                  <span className="text-yellow-500 text-[9px]">{s.k}</span>
                  <span className="font-bold">{s.v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#c00] text-white rounded p-3 text-xs">
            <div className="font-black text-sm mb-1">⚠ If You See the Vessel</div>
            <div className="text-[10px] text-white/80 mb-2">戦艦を目撃したら</div>
            <ul className="space-y-1 text-[10px] leading-relaxed">
              <li>✅ Move indoors immediately / 直ちに屋内へ</li>
              <li>✅ Stay away from windows / 窓から離れる</li>
              <li>✅ Report to City Hall: #110 / 市役所へ通報 #110</li>
              <li>❌ Do NOT photograph without shelter / 屋外での撮影禁止</li>
              <li>❌ Do NOT wave or signal / 手を振る・合図禁止</li>
              <li>❌ Do NOT attempt to board / 乗船の試み禁止</li>
            </ul>
          </div>

          <div className="bg-white border border-[#ddd] rounded p-3 text-xs text-[#555]">
            <div className="font-bold text-[#1a3a5c] mb-2">📡 Sighting Reports / 目撃報告</div>
            {[
              { date:"Apr 15, 2226", area:"Kawaguchi N. District", count:"14件" },
              { date:"Apr 14, 2226", area:"Industrial Zone 3", count:"8件" },
              { date:"Apr 12, 2226", area:"Central Area", count:"3件" },
              { date:"Apr 10, 2226", area:"Harbor District", count:"21件" },
            ].map((r,i) => (
              <div key={i} className="flex justify-between border-b border-[#f0f0f0] pb-1 mb-1 last:border-0">
                <div>
                  <div className="text-[10px] font-semibold">{r.area}</div>
                  <div className="text-[9px] text-[#aaa]">{r.date}</div>
                </div>
                <span className="font-bold text-[#c00] text-[10px]">{r.count}</span>
              </div>
            ))}
          </div>
        </aside>

        {/* ─ 中央 ─ */}
        <main className="space-y-4 min-w-0">

          {/* 重要公告 */}
          <div className="bg-[#fff0f0] border-l-4 border-[#c00] rounded-r p-4">
            <div className="font-black text-sm text-[#c00] mb-1">Official Warning — Feline Faction Airborne Battleship / 公式警告 ― ネコ族空中浮遊戦艦について</div>
            <p className="text-xs text-[#555] leading-relaxed">
              The Demon City Government officially confirms the presence of a large-scale airborne vessel operated by an unidentified Feline Faction beastman group in the skies above Kawaguchi district. This vessel, colloquially known as <strong>"Neko no Hakobune" (Cat's Ark)</strong>, has been observed performing multiple low-altitude passes over residential and commercial areas.
            </p>
            <p className="text-xs text-[#888] mt-2 leading-relaxed">
              魔都市政府は、川口区上空に、不特定ネコ族獣人グループが運営する大型空中浮遊艦「猫の方舟」の存在を公式に確認しました。当艦は住宅地・商業地区上空を複数回にわたって低空飛行していることが観測されています。
            </p>
          </div>

          {/* 戦艦詳細 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#0a0a1a]" accent="bg-yellow-400" en="Vessel Appearance & Capabilities" ja="外観と能力に関する詳細情報" />
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <img src={IMAGES.CAT_BATTLESHIP} alt="Battleship" onClick={()=>zoom(IMAGES.CAT_BATTLESHIP, "Evidence Photo A — Feline Faction Airborne Battleship / 証拠写真A ― ネコ族空中浮遊戦艦")} className="w-full rounded border border-[#ddd] object-cover h-44 cursor-pointer"/>
                  <div className="text-[9px] text-[#888] mt-1 text-center">Evidence Photo A — Aerial sighting over industrial zone / 証拠写真A ― 工業地帯上空での目撃</div>
                </div>
                <div className="space-y-3 text-xs text-[#555] leading-relaxed">
                  <p>
                    <strong className="text-[#0a0a1a]">Hull Structure:</strong> The vessel appears to incorporate both organic and mechanical components. Metallic-plated exterior with visible engine exhausts along the underside. Wing-like stabilizer structures extend from the aft sections.<br/>
                    <span className="text-[9px] text-[#888]">船体構造：有機物と機械部品の両方を組み込んだ構造が確認されています。下部に排気口が見え、後部には安定翼のような構造体が延びています。</span>
                  </p>
                  <p>
                    <strong className="text-[#0a0a1a]">Origin:</strong> Currently under investigation. Connection to the Beastman Foundation denied by Nekomata CEO, who stated: "We have absolutely no affiliation with this vessel or its operators."
                    <br/><span className="text-[9px] text-[#888]">出所：現在調査中。ネコマタCEOは「当財団は当該戦艦およびその運航者と一切関係ありません」と関与を否定しています。</span>
                  </p>
                  <p>
                    <strong className="text-[#0a0a1a]">Activity Pattern:</strong> Vessel appears most active between 2:00–5:00 AM local time. Extended hovering observed over areas with high density of domestic cat populations.<br/>
                    <span className="text-[9px] text-[#888]">活動パターン：現地時間午前2〜5時に最も活発。野良猫密集地での長時間ホバリングが観測されています。</span>
                  </p>
                </div>
              </div>

              <div className="border-t border-[#eee] pt-4">
                <div className="font-bold text-sm text-[#1a3a5c] mb-3">Incident Timeline / 事案タイムライン</div>
                <div className="space-y-3">
                  {[
                    { date:"2226.03.12", title:"First confirmed sighting / 初の確認目撃", detail:"Multiple residents report large shadow and humming noise over industrial zone at 3:14 AM. Initial reports dismissed as weather balloon. / 午前3時14分、複数の住民が工業地帯上空で大きな影と振動音を報告。当初は気象観測気球として処理された。", color:"border-yellow-400" },
                    { date:"2226.03.28", title:"Photographic evidence obtained / 写真証拠入手", detail:"City surveillance cameras capture clear imagery of the vessel at 500m altitude. Images confirmed authentic after digital forensics. / 市の監視カメラが高度500mの戦艦を鮮明に撮影。デジタル鑑定により画像の真正性が確認された。", color:"border-orange-400" },
                    { date:"2226.04.05", title:"Low-altitude pass over residential area / 住宅地での低空飛行", detail:"Vessel descended to approximately 200m over northern residential districts. Caused widespread panic, broken windows from pressure wave. No casualties. / 北部住宅地上空約200mまで降下。圧力波により窓ガラスが割れ、広範囲なパニックが発生。人的被害はなし。", color:"border-red-500" },
                    { date:"2226.04.10", title:"Harbor District extended hover / 港湾地区での長時間ホバリング", detail:"21 sighting reports over 6 hours. Vessel appeared to dispatch small aerial drones before departing at dawn. Drone fragments recovered — cat-themed markings confirmed. / 6時間にわたり21件の目撃報告。夜明けに出発前に小型空中ドローンを放出した模様。回収されたドローン破片にネコ族紋章を確認。", color:"border-purple-400" },
                    { date:"2226.04.15", title:"Most recent sighting — high alert declared / 最新目撃 ― 高度警戒宣言", detail:"14 reports in Kawaguchi North district. Vessel observed at lowest altitude yet (approx. 150m). Demon City Government declares CRITICAL threat level. Self-Defense Forces on standby. / 川口北区で14件の報告。最低高度（約150m）での飛行を観測。魔都市政府が最高脅威レベルを宣言。自衛隊が待機状態に。", color:"border-[#c00]" },
                  ].map((e,i) => (
                    <div key={i} className={`border-l-2 ${e.color} pl-3`}>
                      <div className="text-[10px] font-bold text-[#1a3a5c]">{e.date} — {e.title}</div>
                      <div className="text-[9px] text-[#666] leading-relaxed mt-0.5">{e.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* くずねこ関連調査 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#663300]" accent="bg-orange-400" en="Kuzuneko Connection — Under Investigation" ja="くずねこ関連性 ― 調査中" />
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 text-xs text-[#555] leading-relaxed space-y-3">
                <p>
                  Intelligence suggests that the appearance of the Feline Faction Battleship correlates with increased Kuzuneko activity in the area. On three separate occasions, Kuzuneko was photographed in the vicinity within hours of a vessel sighting. The Pest Control Division emphasizes this <strong>does not confirm Kuzuneko's involvement</strong> but treats the pattern as significant.
                </p>
                <p className="text-[10px] text-[#888]">
                  情報では、ネコ族戦艦の出現がくずねこの活動増加と相関していることが示されています。3回の異なる機会に、目撃報告の数時間以内にくずねこが周辺で撮影されています。害獣対策課はこれが<strong>くずねこの関与を確認するものではない</strong>と強調しますが、このパターンを重要視しています。
                </p>
                <div className="bg-[#fff8f0] border border-orange-200 rounded p-3 text-xs">
                  <strong className="text-orange-700">⚠ Official Position / 公式見解:</strong><br/>
                  "Kuzuneko, as an officially designated pest, does not have the technical capability to operate aerospace vehicles. Any connection to the battleship is coincidental and under investigation. Citizens should not speculate."
                  <br/><span className="text-[9px] text-[#888] mt-1 block">「くずねこは公式指定害獣であり、宇宙航空機器を運用する技術的能力を持ちません。戦艦との関連は偶然であり、調査中です。市民の憶測を控えるようお願いします。」― 魔都市政府広報</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="border border-[#ddd] rounded overflow-hidden">
                  <img src={IMAGES.KUZUNEKO_NEW_01} onClick={()=>zoom(IMAGES.KUZUNEKO_NEW_01, "Kuzuneko near sighting area")} alt="Kuzuneko near sighting area" className="w-full h-32 object-cover cursor-pointer"/>
                  <div className="p-1.5 text-[9px] text-[#888] text-center">Photographed near sighting area / 目撃エリア付近で撮影</div>
                </div>
                <div className="border border-orange-200 rounded p-2 bg-[#fff8f0]">
                  <div className="text-[9px] font-bold text-orange-700 mb-1">Correlation Data / 相関データ</div>
                  <div className="text-[8px] text-[#666] space-y-1">
                    <div>Battleship sightings: <strong>47件</strong></div>
                    <div>Kuzuneko nearby (±6h): <strong>3件</strong></div>
                    <div>Correlation: <strong className="text-orange-600">6.4%</strong></div>
                    <div className="text-[#aaa] italic">Statistical significance: pending</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 市民向け注意事項 */}
          <div className="bg-[#fff8dc] border border-yellow-400 rounded p-4">
            <div className="font-black text-sm text-[#7a5c00] mb-3">🏠 Citizen Safety Guidelines / 市民向け安全指針</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-[#555]">
              <div className="bg-white border border-yellow-200 rounded p-3">
                <div className="font-bold text-[#c00] mb-1">🏃 If Vessel Appears / 出現時</div>
                <ul className="text-[9px] space-y-1 leading-relaxed">
                  <li>• Move indoors immediately / 屋内へ</li>
                  <li>• Close all windows / 窓を閉じる</li>
                  <li>• Do not use rooftops / 屋上使用禁止</li>
                  <li>• Keep pets inside / ペットを室内に</li>
                  <li>• Call 110 / #110に通報</li>
                </ul>
              </div>
              <div className="bg-white border border-yellow-200 rounded p-3">
                <div className="font-bold text-[#1a3a5c] mb-1">📻 Communication / 通信</div>
                <ul className="text-[9px] space-y-1 leading-relaxed">
                  <li>• Emergency broadcast on radio / ラジオで緊急放送確認</li>
                  <li>• Do not use mobile phones outdoors / 屋外での携帯使用禁止</li>
                  <li>• City Hall emergency line: 0120-110-MATO / 緊急窓口: 0120-110-MATO</li>
                  <li>• Evacuation zones posted at city hall / 避難区域は市役所掲示板参照</li>
                </ul>
              </div>
              <div className="bg-white border border-yellow-200 rounded p-3">
                <div className="font-bold text-green-700 mb-1">✅ After the Event / 通過後</div>
                <ul className="text-[9px] space-y-1 leading-relaxed">
                  <li>• Check for structural damage / 建物の損傷確認</li>
                  <li>• Report any fallen objects / 落下物を報告</li>
                  <li>• Avoid drone fragments / ドローン破片に触れない</li>
                  <li>• Assist neighbors / 近隣住民への支援</li>
                  <li>• All clear via emergency broadcast / 解除は緊急放送で確認</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 市長声明 */}
          <div className="bg-white border border-[#ddd] rounded p-4">
            <div className="font-bold text-sm text-[#1a3a5c] mb-3">📢 Mayor's Emergency Statement / 市長緊急声明</div>
            <div className="flex gap-4 items-start">
              <div className="w-16 h-16 rounded overflow-hidden shrink-0 flex items-center justify-center">
                <CityEmblem size={64} />
              </div>
              <div className="text-xs text-[#555] leading-relaxed">
                <div className="font-bold text-[#1a3a5c] mb-0.5">Mayor Rin Himura / 火邑 凛 市長</div>
                <div className="text-[10px] text-[#888] mb-1">🦁 ライオン族（メス獣人） / Lion Beastwoman</div>
                <p>"The situation is being monitored around the clock by our defense forces. We want to reassure all citizens that Demon City will not yield to aerial intimidation. The Feline Faction will be called to formal diplomatic discussions. In the meantime, safety of our citizens is paramount."</p>
                <p className="text-[10px] text-[#888] mt-2">「防衛部隊が24時間体制で状況を監視しています。魔都市は空中からの威圧に屈しないことを市民の皆様に強調します。ネコ族には正式な外交交渉を要求します。市民の安全を最優先に取り組みます。」</p>
              </div>
            </div>
          </div>
        </main>

        {/* ─ 右列 ─ */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#2a5a2a]" accent="bg-green-300" en="Nekomata's Statement" ja="ネコマタ氏の声明" />
            <img src={IMAGES.NEKOMATA_FORUM_2012} onClick={()=>zoom(IMAGES.NEKOMATA_FORUM_2012, "Nekomata press")} alt="Nekomata press" className="w-full h-32 object-cover object-top cursor-pointer"/>
            <div className="p-3 text-[10px] text-[#555] leading-relaxed space-y-2">
              <p><em>"The Beastman Foundation strongly condemns any rogue actors operating unauthorized aerial vehicles. We are cooperating fully with the city investigation."</em></p>
              <p className="text-[9px] text-[#888]">「獣人財団は、無許可の航空機を運用するいかなる違法勢力も強く非難します。市の調査に全面的に協力しています。」― ネコマタCEO</p>
            </div>
          </div>

          <div className="bg-[#0a0a1a] text-yellow-300 rounded p-3">
            <div className="font-black text-xs mb-2">🚨 Threat Assessment / 脅威評価</div>
            {[
              { label:"Mobility", value:"★★★★★", color:"text-[#c00]" },
              { label:"Firepower", value:"★★★★☆", color:"text-orange-400" },
              { label:"Size / Scale", value:"★★★★★", color:"text-[#c00]" },
              { label:"Hostility", value:"★★★☆☆", color:"text-yellow-400" },
              { label:"Predictability", value:"★☆☆☆☆", color:"text-[#c00]" },
            ].map((t,i) => (
              <div key={i} className="flex justify-between text-[10px] border-b border-yellow-600/20 py-1 last:border-0">
                <span className="text-yellow-400/70">{t.label}</span>
                <span className={t.color}>{t.value}</span>
              </div>
            ))}
          </div>

          <div className="bg-white border border-[#ddd] rounded p-3">
            <div className="font-bold text-xs text-[#1a3a5c] mb-2">📋 Related Articles / 関連記事</div>
            {[
              { title:"Kuzuneko denies any battleship association", ja:"くずねこ、戦艦関与を否定（本人困惑）" },
              { title:"Dragon faction: no comment on new aerial threat", ja:"龍族：新たな空中脅威についてコメントなし" },
              { title:"Bear Gang reportedly unimpressed by Battleship", ja:"熊族ギャング：「大した戦艦じゃない」と���蹴" },
              { title:"Insurance premiums rise after Battleship appearance", ja:"戦艦出現後、市内保険料が上昇」" },
            ].map((a,i) => (
              <div key={i} className="text-[9px] border-b border-[#f0f0f0] pb-1.5 mb-1.5 last:border-0 last:mb-0">
                <div className="text-[#1a3a5c] font-semibold leading-snug">{a.title}</div>
                <div className="text-[#aaa] mt-0.5">{a.ja}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Page 5: イカ女保護のお知らせ（市民だより風）
───────────────────────────────────────── */
function PageSquidWoman() {
  const zoom = useZoom();
  return (
    <div className="font-sans text-[#222]">
      {/* ヒーロービジュアル */}
      <div className="relative bg-[#e8f8f8] border-b-4 border-cyan-400 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 py-6 md:py-10 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1">
            <div className="inline-block bg-cyan-100 border border-cyan-300 text-cyan-700 text-[10px] font-bold px-3 py-1 rounded-full mb-3 tracking-wide">🦑 PROTECTED SPECIES NOTICE / 保護生物のお知らせ</div>
            <h1 className="text-2xl md:text-4xl font-black text-[#005a7a] leading-tight mb-2">
              Please Protect the Squid Woman<br/>
              <span className="text-xl md:text-2xl font-bold text-[#007a99]">イカ女を大切に。駆除も食用も禁止です。</span>
            </h1>
            <p className="text-sm text-[#555] leading-relaxed">
              A gentle and mysterious being spotted in Kawaguchi City's parks and streets. She is extremely rare and must be protected.<br/>
              <span className="text-xs text-[#888]">川口市の公園や繁華街で目撃される、優しくて神秘的な存在。とても希少な生き物です。</span>
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="bg-cyan-600 text-white text-[10px] px-3 py-1 rounded-full font-bold">🛡 PROTECTED / 保護対象</span>
              <span className="bg-green-600 text-white text-[10px] px-3 py-1 rounded-full font-bold">✅ OBSERVE ONLY / 観察のみ</span>
              <span className="bg-[#c00] text-white text-[10px] px-3 py-1 rounded-full font-bold">❌ NO EXTERMINATION / 駆除禁止</span>
              <span className="bg-[#c00] text-white text-[10px] px-3 py-1 rounded-full font-bold">❌ DO NOT EAT / 食用禁止</span>
            </div>
          </div>
          <div className="w-full md:w-72 shrink-0">
            <img src={IMAGES.IKA_WOMAN_NEW} onClick={()=>zoom(IMAGES.IKA_WOMAN_NEW, "Squid Woman in park")} alt="Squid Woman in park" className="w-full rounded-xl shadow-lg object-cover h-56 md:h-64 cursor-pointer"/>
            <div className="text-center text-[9px] text-[#888] mt-1">Squid Woman in Kawaguchi City / 川口市内で目撃されたイカ女</div>
          </div>
        </div>
      </div>

      {/* 市民だより本文 */}
      <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">

        <main className="space-y-4 min-w-0">

          {/* 市民だより風 巻頭特集 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <div className="bg-cyan-700 text-white px-4 py-3">
              <div className="text-[10px] tracking-widest font-bold text-cyan-200 mb-0.5">魔都市広報誌 DEMON CITY BULLETIN — Special Feature / 特別特集</div>
              <div className="text-lg md:text-xl font-black">"Squid Woman" — A Mysterious Resident of Our City</div>
              <div className="text-sm font-bold text-cyan-200">「イカ女」― 私たちの街の謎めいた住人</div>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 text-xs text-[#555] leading-relaxed">
                <p>
                  <strong className="text-cyan-700">Who is the Squid Woman? / イカ女とは？</strong><br/>
                  The Squid Woman is a mysterious being with the upper body of a human woman and the lower body of a cephalopod (squid). She has been sighted multiple times in parks, shopping streets, and public spaces throughout Kawaguchi City. Despite her unusual appearance, all documented observations show <strong>completely peaceful, harmless behavior</strong>.
                </p>
                <p className="text-[10px] text-[#888]">
                  イカ女は人間の女性の上半身と、頭足類（イカ）の下半身を持つ謎の存在です。川口市内の公園、商店街、公共スペースで複数回目撃されています。その特異な外見にもかかわらず、記録されているすべての���察において完全に平和的で無害な行動が確認されています。
                </p>
                <p>
                  <strong className="text-cyan-700">First Sighting / 最初の目撃：</strong> 2225年秋、川口市南公園にて。当時の目撃者は「のんびりと日向ぼっこをしていた」と証言。
                  <br/>Autumn 2225, Kawaguchi South Park. The witness at the time testified: "She was leisurely sunbathing."
                </p>
                <p>
                  <strong className="text-cyan-700">Appearance / 外見：</strong> Upper body resembles a human woman in casual clothing. Lower body consists of 8 semi-transparent blue-white tentacles, approximately 1.5m in length each. Bioluminescence observed in dim conditions. Total height (when upright): approx. 3m.
                  <br/><span className="text-[9px] text-[#888]">上半身は普段着の人間女性に似ています。下半身は約1.5mの半透明な青白い触手8本で構成。暗所では生物発光が確認されています。直立時の全高：約3m。</span>
                </p>
              </div>
              <div className="space-y-3">
                <img src={IMAGES.IMG_2K_202604161453_1_65} onClick={()=>zoom(IMAGES.IMG_2K_202604161453_1_65, "Squid Woman walking")} alt="Squid Woman walking" className="w-full rounded object-cover h-44 border border-[#ddd] cursor-pointer"/>
                <div className="text-[9px] text-[#888] text-center">Walking through Kawaguchi City streets / 川口市街を歩くイカ女</div>
                <div className="bg-cyan-50 border border-cyan-200 rounded p-3">
                  <div className="font-bold text-xs text-cyan-700 mb-1">🔬 Scientific Classification (Provisional) / 暫定分類</div>
                  <div className="text-[9px] text-[#555] space-y-0.5">
                    <div><strong>Kingdom:</strong> Animalia → <strong>Class:</strong> Unknown / 不明</div>
                    <div><strong>Order:</strong> Cephalopoda-Humanoid Hybrid / 頭足類ヒューマノイド混成</div>
                    <div><strong>Status:</strong> <span className="text-cyan-700 font-bold">Rare / Unclassified / Protected</span></div>
                    <div><strong>分類状態：</strong>希少・未分類・保護対象</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 目撃記録 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-cyan-700" accent="bg-cyan-300" en="Sighting Records & Behavioral Observations" ja="目撃記録と行動観察" />
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { img: IMAGES.IMG_2K_202604161455_64, label:"Sighting A — South Park / 南公園", note:"Resting near fountain, showed no interest in passersby / 噴水付近で休憩。通行人への関心は見られず", time:"14:22" },
                  { img: IMAGES.IMG_2K_202604161453_1_65, label:"Sighting B — Shopping District / 商店街", note:"Moving through pedestrian area. Bystanders gave way naturally / 歩行者エリアを移動。周囲が自然に道を譲った", time:"10:45" },
                  { img: IMAGES.KAWAGUCHI_ALERT_MAP, label:"Sighting Map — Kawaguchi City / 目撃地図", note:"All confirmed sightings concentrated in southern districts / 確認された目撃はすべて南部地区に集中", time:"Various" },
                ].map((s,i) => (
                  <div key={i} className="border border-[#eee] rounded overflow-hidden">
                    <img src={s.img} alt={s.label} onClick={()=>zoom(s.img, s.label)} className="w-full h-32 object-cover cursor-pointer"/>
                    <div className="p-2">
                      <div className="font-semibold text-[10px] text-[#1a3a5c]">{s.label}</div>
                      <div className="text-[9px] text-[#888] mt-0.5">{s.note}</div>
                      <div className="text-[9px] font-mono text-[#aaa] mt-1">{s.time}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#eee] pt-4">
                <div className="font-bold text-sm text-[#1a3a5c] mb-2">Behavioral Profile / 行動プロファイル</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-bold text-cyan-700 mb-2">Documented Behaviors / 記録済み行動</div>
                    <ul className="text-[10px] text-[#555] space-y-1.5 leading-relaxed">
                      <li>🌸 <strong>Sunbathing in parks</strong> — Most frequently observed behavior. She appears to enjoy warmth and open spaces.<br/><span className="text-[9px] text-[#888]">公園での日光浴 ― 最も頻繁に記録された行動。暖かさと開放的な空間を好む様子。</span></li>
                      <li>🚶 <strong>Urban walking</strong> — Navigates city streets calmly, appears aware of traffic and social norms.<br/><span className="text-[9px] text-[#888]">市街散策 ― 落ち着いて街を移動。交通や社会規範を理解している様子。</span></li>
                      <li>🛍 <strong>Window shopping</strong> — Observed looking at store displays with apparent curiosity.<br/><span className="text-[9px] text-[#888]">ウィンドウショッピング ― 興味深そうにショーウィンドウを眺める姿が観察されている。</span></li>
                      <li>💤 <strong>Resting outdoors</strong> — Occasionally naps in quiet corners of parks. Appears completely harmless during rest.<br/><span className="text-[9px] text-[#888]">屋外での休息 ― 公園の静かな角で時折昼寝。休息中も完全に無害。</span></li>
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-red-600 mb-2">Never Observed / 記録されていない行動</div>
                    <ul className="text-[10px] text-[#555] space-y-1 leading-relaxed">
                      <li>❌ Aggressive behavior of any kind / あらゆる攻撃行動</li>
                      <li>❌ Property destruction / 財産の破壊</li>
                      <li>❌ Communication with other pests / 他の害獣との接触</li>
                      <li>❌ Entry into private property / 私有地への侵入</li>
                      <li>❌ Supernatural phenomena / 超自然現象の引き起こし</li>
                    </ul>
                    <div className="mt-3 bg-green-50 border border-green-200 rounded p-2 text-[9px] text-green-700 leading-relaxed">
                      <strong>Conclusion / 結論:</strong> All evidence indicates that the Squid Woman poses no threat to public safety. Demon City Government officially classifies her as a <strong>"Harmless Rare Being (HRB)"</strong> and recommends peaceful coexistence.
                      <br/><span className="text-[9px] text-[#555] mt-1 block">すべての証拠がイカ女は公共の安全に脅威を与えないことを示しています。魔都市政府は正式に「無害希少存在 (HRB)」として分類し、平和的共存を推奨しています。</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 重要通達 */}
          <div className="bg-white border-2 border-cyan-400 rounded overflow-hidden">
            <div className="bg-cyan-600 text-white px-4 py-2">
              <div className="font-black text-sm">⚠ Official City Directive on Squid Woman / イカ女に関する市公式指令</div>
              <div className="text-[10px] text-cyan-100">Issued by: Demon City Government Pest & Ecology Division / 魔都市政府 害獣・生態系管理課 発行</div>
            </div>
            <div className="p-4 space-y-3 text-xs text-[#555] leading-relaxed">
              <p>
                <strong className="text-[#c00]">IMPORTANT:</strong> The entity known as "Squid Woman" has been reclassified from "unknown pest" to <strong>"Protected Rare Being (PRB)"</strong> effective 2226年3月1日 by decree of the Demon City Ecological Committee.
                <br/><span className="text-[10px] text-[#888]">重要：「イカ女」と呼ばれる存在は、2226年3月1日付の魔都市生態委員会の命令により、「未知の害獣」から<strong>「保護希少存在 (PRB)」</strong>に再分類されました。</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="font-bold text-[#c00] mb-2">Strictly Prohibited / 厳重禁止事項</div>
                  <ul className="space-y-1.5 text-[10px]">
                    <li className="flex items-start gap-2"><span className="text-[#c00] font-black shrink-0">✗</span><span><strong>Extermination or culling</strong> of the Squid Woman is a Class-A violation, punishable by up to 3 years imprisonment.<br/><span className="text-[#888]">イカ女の駆除・殺傷はA級違反。最大3年の懲役刑。</span></span></li>
                    <li className="flex items-start gap-2"><span className="text-[#c00] font-black shrink-0">✗</span><span><strong>Consumption (eating) of any part</strong> of the Squid Woman is strictly prohibited. Yes, we have to say this.<br/><span className="text-[#888]">イカ女の一部を含む食用目的の行為は厳禁です。念のため明記しました。</span></span></li>
                    <li className="flex items-start gap-2"><span className="text-[#c00] font-black shrink-0">✗</span><span><strong>Harassment, following, or threatening</strong> the Squid Woman in any form.<br/><span className="text-[#888]">嫌がらせ、尾行、脅迫などあらゆる形のハラスメント。</span></span></li>
                    <li className="flex items-start gap-2"><span className="text-[#c00] font-black shrink-0">✗</span><span><strong>Unauthorized capture</strong> for research, exhibition, or any purpose.<br/><span className="text-[#888]">研究・展示などいかなる目的の無許可捕獲も禁止。</span></span></li>
                  </ul>
                </div>
                <div>
                  <div className="font-bold text-green-700 mb-2">Recommended Actions / 推奨される行動</div>
                  <ul className="space-y-1.5 text-[10px]">
                    <li className="flex items-start gap-2"><span className="text-green-600 font-black shrink-0">✓</span><span><strong>Observe from a respectful distance.</strong> A minimum of 10m is recommended.<br/><span className="text-[#888]">敬意ある距離（最低10m推奨）からの観察。</span></span></li>
                    <li className="flex items-start gap-2"><span className="text-green-600 font-black shrink-0">✓</span><span><strong>Report sightings</strong> to City Hall (non-emergency line) for research purposes.<br/><span className="text-[#888]">目撃情報を市役所の非緊急回線に報告（研究目的）。</span></span></li>
                    <li className="flex items-start gap-2"><span className="text-green-600 font-black shrink-0">✓</span><span><strong>Treat with dignity and care.</strong> She is a unique and irreplaceable resident of our city.<br/><span className="text-[#888]">敬意と思いやりを持って接してください。かけがえのない市の住人です。</span></span></li>
                    <li className="flex items-start gap-2"><span className="text-green-600 font-black shrink-0">✓</span><span><strong>Inform children and visitors</strong> that she is protected, not a pest.<br/><span className="text-[#888]">子どもや観光客に「保護対象であり害獣ではない」と説明してください。</span></span></li>
                  </ul>
                </div>
              </div>
              <div className="bg-cyan-50 border border-cyan-200 rounded p-3 mt-2">
                <strong className="text-cyan-700">A Message from the Mayor / 市長からのメッセージ：</strong>
                <p className="mt-1 text-[10px] leading-relaxed">
                  "Demon City has always embraced diversity — of people, species, and the unexpected. The Squid Woman is a reminder that our world is full of wonders. Let us protect what is rare and precious. She has chosen our city as her home. Let us be worthy of that trust."
                  <br/><span className="text-[9px] text-[#888] mt-1 block">「魔都市は常に多様性を ― 人、生き物、そして予期せぬ存在を ― 受け入れてきました。イカ女は、この世界が驚異に満ちていることを思い出させてくれます。希少で大切なものを守りましょう。彼女は私たちの街を故郷に選んでくれました。その信頼に応えましょう。」― 火邑 凛 市長</span>
                </p>
              </div>
            </div>
          </div>

          {/* Q&A */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#1a3a5c]" accent="bg-cyan-300" en="Citizens' Q&A — Squid Woman Edition" ja="市民Q&A ― イカ女について" />
            <div className="p-4 space-y-3">
              {[
                { q:"Q: Is the Squid Woman dangerous? / イカ女は危険ですか？", a:"A: No. Zero aggressive incidents have ever been recorded. She is safer than most people in the city. / いいえ。攻撃的な事案は一件も記録されていません。市内のほとんどの人より安全です。" },
                { q:"Q: Can I feed her? / えさを与えても大丈夫ですか？", a:"A: It is not recommended. Please do not disturb her natural foraging habits. Also, we do not know what she eats. / 推奨しません。自然な採食習慣を乱さないでください。また、何を食べるかも不明です。" },
                { q:"Q: What if she enters my garden? / 庭に入ってきたら？", a:"A: Observe calmly. She will likely leave on her own. Do NOT attempt to chase her away forcefully. Contact city hall for guidance. / 落ち着いて観察してください。おそらく自分で立ち去ります。強制的に追い出そうとしないでください。市役所にご連絡を。" },
                { q:"Q: Seriously, can we eat her? / 本当に食べてはいけませんか？", a:"A: We cannot stress this enough: DO NOT EAT THE SQUID WOMAN. This is illegal under Demon City Ordinance 2226-ERB-04. We cannot believe we had to write this FAQ. / 何度でも言います：イカ女を食べてはいけません。魔都市条例2226-ERB-04違反です。このFAQを書かなければならないことが信じられません。" },
                { q:"Q: Is she related to the Kuzuneko situation? / くずねこと関係がありますか？", a:"A: No. They are entirely separate cases. Please do not conflate unrelated mysterious beings. / いいえ。全く別の案件です。関係のない謎の存在を混同しないでください。" },
              ].map((qa,i) => (
                <div key={i} className="border-l-2 border-cyan-300 pl-3">
                  <div className="font-bold text-xs text-[#1a3a5c] mb-0.5">{qa.q}</div>
                  <div className="text-[10px] text-[#555] leading-relaxed">{qa.a}</div>
                </div>
              ))}
            </div>
          </div>
        </main>

        {/* ─ 右列 ─ */}
        <aside className="space-y-4">
          <div className="bg-cyan-700 text-white rounded overflow-hidden">
            <div className="px-3 py-2 font-black text-sm">🦑 Quick Reference / クイック参照</div>
            <div className="px-3 pb-3 text-[10px] space-y-2">
              <div className="bg-white/10 rounded p-2">
                <div className="font-bold text-cyan-200">Classification / 分類</div>
                <div>Protected Rare Being (PRB)<br/>保護希少存在</div>
              </div>
              <div className="bg-white/10 rounded p-2">
                <div className="font-bold text-cyan-200">Threat Level / 脅威レベル</div>
                <div className="text-green-300 font-bold">ZERO — None observed<br/>ゼロ ― 脅威は確認されていません</div>
              </div>
              <div className="bg-white/10 rounded p-2">
                <div className="font-bold text-cyan-200">Status / 状態</div>
                <div>Active / Present in city<br/>活動中 ／ 市内在住</div>
              </div>
              <div className="bg-white/10 rounded p-2">
                <div className="font-bold text-cyan-200">Sightings this month / 今月の目撃</div>
                <div className="font-bold text-2xl">4件</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#2a5a2a]" accent="bg-green-300" en="Nekomata's Support" ja="ネコマタ氏の支持" />
            <img src={IMAGES.NEKOMATA_FORUM_2012} onClick={()=>zoom(IMAGES.NEKOMATA_FORUM_2012, "Nekomata")} alt="Nekomata" className="w-full h-28 object-cover object-top cursor-pointer"/>
            <div className="p-2 text-[9px] text-[#555] leading-relaxed">
              <em>"The Beastman Foundation fully supports the protection of the Squid Woman. She is a living treasure of Demon City."</em>
              <br/><span className="text-[#888]">「獣人財団はイカ女の保護を全面的に支持します。彼女は魔都市の生きた宝です。」― ネコマタCEO</span>
            </div>
          </div>

          <div className="bg-white border border-[#ddd] rounded p-3">
            <div className="font-bold text-xs text-[#1a3a5c] mb-2">📞 Emergency Contacts / 緊急連絡先</div>
            <div className="space-y-2 text-[10px]">
              <div className="flex justify-between"><span>Squid Woman Sighting Line / イカ女目撃専用回線</span><strong>0120-SQUID</strong></div>
              <div className="flex justify-between"><span>City Hall (Non-Emergency) / 市役所（一般）</span><strong>03-MATO-110</strong></div>
              <div className="flex justify-between"><span>Ecology Division / 生態系管理課</span><strong>03-MATO-ECO</strong></div>
            </div>
          </div>

          <div className="bg-[#fffef0] border border-yellow-300 rounded p-3">
            <div className="font-bold text-xs text-yellow-700 mb-2">📋 Did You Know? / ご存知ですか？</div>
            <ul className="text-[9px] text-[#555] space-y-1.5 leading-relaxed">
              <li>🌊 The Squid Woman has never harmed any citizen in recorded history / イカ女は記録上一度も市民に危害を加えていません</li>
              <li>🌸 She appears to enjoy cherry blossom season in Kawaguchi / 川口の桜の季節を楽しんでいる様子</li>
              <li>💡 She is the only entity in the city with a protection ordinance specifically protecting against eating / 食用を禁じる専用条例がある唯一の存在です</li>
              <li>🏆 She was voted "Most Unique Resident of 2226" by Demon City Times / 魔都市タイムズの「2226年最もユニークな市民」に選ばれました</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Page 6: 住民サービス — 虎の彫像プロジェクト
───────────────────────────────────────── */
function PageCitizensPortal() {
  const zoom = useZoom();
  return (
    <div className="font-sans text-[#222]">
      {/* ヒーロービジュアル */}
      <div className="relative bg-[#2a1a3c] overflow-hidden">
        <div className="relative h-44 md:h-60 overflow-hidden">
          <img src={IMAGES.TIGER_STATUE_01} alt="Tiger Statue Project" className="w-full h-full object-cover object-center opacity-70"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2a1a3c]/95 via-[#2a1a3c]/60 to-transparent"/>
          <div className="absolute inset-0 flex items-center px-6 md:px-10">
            <div className="text-white max-w-xl">
              <div className="text-[10px] font-bold tracking-widest text-yellow-300 uppercase mb-2">CITIZEN SERVICES — INFRASTRUCTURE PROJECT</div>
              <h1 className="text-2xl md:text-4xl font-black leading-tight mb-2">
                Tiger Guardian Statue Initiative<br/>
                <span className="text-lg md:text-2xl font-bold text-yellow-200">天界の虎の護り神・巨大機械像建設計画</span>
              </h1>
              <p className="text-sm text-white/70">For the Protection of All Citizens / 全市民の安全のために<br/>
                <span className="text-xs text-white/50">川口駅前 ライオン像→虎像 改修プロジェクト公式説明</span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#7a4a00] text-white text-xs py-1.5 px-4 flex items-center gap-2">
          <span className="font-black bg-yellow-400 text-[#7a4a00] px-1.5 py-0.5 rounded text-[10px]">🐯 CITY PROJECT</span>
          <span>Kawaguchi Station Symbol Replacement Project — Public Briefing / 川口駅シンボル改修計画 市民説明</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
        <main className="space-y-4">

          {/* 重要告知 */}
          <div className="bg-[#fff8dc] border-l-4 border-yellow-500 rounded-r p-4">
            <div className="font-black text-sm text-[#7a5c00] mb-1">
              📢 Public Notice — Kawaguchi Station Symbol Replacement / 公告：川口駅シンボル改修のお知らせ
            </div>
            <p className="text-xs text-[#555] leading-relaxed">
              The Demon City Government is proceeding with the replacement of the famous lion statue in front of Kawaguchi Station with a new <strong>mechanical tiger guardian statue</strong>. This page explains the reasons, benefits, and tax implications of this important civic project.<br/>
              <span className="text-[#888]">魔都市政府は、川口駅前の有名なライオン彫像を新たな<strong>機械式虎型守護神像</strong>に改修する工事を進めています。本ページでは、この重要な市民プロジェクトの理由・効果・費用についてご説明します。</span>
            </p>
          </div>

          {/* なぜ虎か */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#5a2a7a]" accent="bg-yellow-300" en="Why Replace the Lion? The Sacred Tiger Princess" ja="なぜライオンを虎に？ ― 天界の虎の姫について" />
            <div className="p-4 text-xs text-[#555] leading-relaxed space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <img src={IMAGES.TIGER_STATUE_02} onClick={()=>zoom(IMAGES.TIGER_STATUE_02, "Tiger Statue Construction")} alt="Tiger Statue Construction" className="rounded overflow-hidden w-full h-28 object-cover cursor-pointer"/>
                    <img src={IMAGES.TIGER_STATUE_03} onClick={()=>zoom(IMAGES.TIGER_STATUE_03, "Tiger Statue Demo")} alt="Tiger Statue Demo" className="rounded overflow-hidden w-full h-28 object-cover cursor-pointer"/>
                  </div>
                  <p className="mb-2">
                    The <strong className="text-[#5a2a7a]">Celestial Tiger Princess (天界の虎の姫)</strong> is a divine protector deity recognized across all beastman traditions. Her blessing is considered essential for urban defense against supernatural threats — including dragon incursions and rogue AI-infected robots.<br/>
                    <span className="text-[#888] text-[10px]">「天界の虎の姫」はすべての獣人文化において認められた守護神であり、その加護は龍族侵入・暴走AIロボットなどの超自然的脅威からの都市防衛に不可欠とされています。</span>
                  </p>
                  <p>
                    The current lion statue, while historically significant, <strong className="text-orange-700">has no defensive capabilities</strong> and does not align with the spiritual protection traditions of our beastman residents, who now constitute over 34% of the city population.<br/>
                    <span className="text-[#888] text-[10px]">現在のライオン像は歴史的意義はあるものの、防衛機能を持たず、市民の34%以上を占める獣人住民の精神的保護伝統とも一致しません。</span>
                  </p>
                </div>
                <div className="bg-[#faf0ff] border border-purple-200 rounded p-3">
                  <div className="font-bold text-[#5a2a7a] text-xs mb-2">🐯 Tiger Princess Protection Benefits / 虎の姫の護りの効果</div>
                  <ul className="space-y-1 text-[10px]">
                    <li className="flex items-start gap-1"><span className="text-yellow-600">★</span><span>Divine barrier against dragon-class entities / 龍族に対する神的結界</span></li>
                    <li className="flex items-start gap-1"><span className="text-yellow-600">★</span><span>AI purification field (counters rogue bots) / AI浄化フィールド（暴走ロボット対策）</span></li>
                    <li className="flex items-start gap-1"><span className="text-yellow-600">★</span><span>Cat-type beastman harmony enhancement / 猫系獣人との調和強化</span></li>
                    <li className="flex items-start gap-1"><span className="text-yellow-600">★</span><span>Bear Mafia deterrence aura / 熊族マフィアへの威嚇効果</span></li>
                    <li className="flex items-start gap-1"><span className="text-yellow-600">★</span><span>24/7 active defense when activated / 起動時24時間アクティブ防衛</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* 機械式虎像の防衛能力 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#1a4a1a]" accent="bg-green-300" en="Defense Capabilities of the Mechanical Tiger Statue" ja="機械式虎型守護神像の防衛性能" />
            <div className="p-4 text-xs text-[#555] leading-relaxed space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { icon:"⚡", title:"Anti-Dragon System", ja:"龍族迎撃システム", desc:"20-meter class dragon detection and deterrence. Electromagnetic pulse emitters prevent aerial approach to a 500m radius.", descja:"20mクラス龍族の検知・威嚇システム。電磁パルス発射器により半径500mの上空接近を阻止。", color:"border-purple-300 bg-purple-50" },
                  { icon:"🔧", title:"Rogue Robot Neutralizer", ja:"暴走ロボット無力化装置", desc:"Built-in EMP and signal jamming capability disables AI-infected mechanical entities within 200m range.", descja:"内蔵EMPおよび信号妨害装置により、半径200m内のAI汚染機械体を無力化します。", color:"border-blue-300 bg-blue-50" },
                  { icon:"🛡", title:"Continuous Guard Mode", ja:"常時警戒モード", desc:"Solar + magical energy dual-power system enables 24/7 active patrol. Response time: 8 seconds from detection.", descja:"太陽光＋魔力デュアル電源により24時間アクティブ巡回。脅威検知から8秒で対応。", color:"border-green-300 bg-green-50" },
                ].map((item, i) => (
                  <div key={i} className={`border rounded p-3 ${item.color}`}>
                    <div className="text-2xl mb-1">{item.icon}</div>
                    <div className="font-bold text-sm mb-0.5">{item.title}</div>
                    <div className="text-[9px] text-[#888] mb-1">{item.ja}</div>
                    <p className="text-[10px]">{item.desc}</p>
                    <p className="text-[9px] text-[#888] mt-0.5">{item.descja}</p>
                  </div>
                ))}
              </div>

              {/* 統計 */}
              <div className="bg-[#f0f8e8] border border-green-200 rounded p-3">
                <div className="font-bold text-green-800 text-xs mb-2">📊 Projected Safety Improvement / 予測される安全改善効果</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                  {[
                    { val:"73%", label:"Dragon incident reduction", ja:"龍族事案削減" },
                    { val:"89%", label:"Rogue robot containment", ja:"暴走ロボット封じ込め" },
                    { val:"2.4×", label:"Evacuation efficiency", ja:"避難効率向上倍率" },
                    { val:"∞", label:"Tiger Princess blessing", ja:"虎の姫の加護" },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded p-2 border border-green-100">
                      <div className="text-xl font-black text-green-700">{s.val}</div>
                      <div className="text-[9px] text-[#555]">{s.label}</div>
                      <div className="text-[8px] text-[#888]">{s.ja}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 反対意見への回答 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#8a3a00]" accent="bg-orange-300" en="Addressing Opposition Concerns" ja="反対意見への市の回答" />
            <div className="p-4 text-xs text-[#555] leading-relaxed space-y-2">
              <p className="text-[10px] text-[#888] mb-2">Some citizens have expressed concern about the replacement. The city respectfully addresses these concerns below.<br/>
              一部の市民から改修に対するご懸念の声が寄せられています。市は以下のとおり誠実にお答えします。</p>
              {[
                { q:"The lion statue has 80 years of history — why replace it?", qja:"ライオン像は80年の歴史がある。なぜ変える？", a:"We respect the lion's historical significance. The lion will be preserved in the Demon City Historical Museum. The tiger statue represents our city's future — diverse, protected, and spiritually aligned with our beastman citizens.", aja:"ライオン像の歴史的意義は尊重しています。像は魔都市歴史博物館に移設・保存します。虎像は多様性・防衛力・獣人市民との精神的調和という、本市の未来を体現しています。" },
                { q:"This is just a religious monument. Separation of state and religion!", qja:"これは宗教的モニュメントでは？政教分離に反する！", a:"The mechanical tiger statue is primarily a defense infrastructure unit. Its spiritual aspects are secondary to its operational capabilities. Comparable to a military installation with symbolic significance.", aja:"機械式虎像はあくまで防衛インフラ設備です。精神的側面はその稼働性能に付随するものです。象徴的意義を持つ軍事施設と同等の位置づけとなります。" },
                { q:"How much will this cost taxpayers?", qja:"税金でいくらかかるの？", a:"The initial cost is ¥4.2 billion (魔都市魔法通貨). However, projected savings from reduced dragon and robot damage — currently ¥8.7 billion annually — will result in a net positive return within 18 months.", aja:"初期費用は42億魔都市魔法通貨です。ただし、龍族・ロボット被害の現在の年間損失額（87億魔法通貨）の削減により、18ヶ月以内に収支がプラスになる試算です。" },
              ].map((item, i) => (
                <div key={i} className="border border-[#eee] rounded p-3">
                  <div className="font-bold text-[#333] text-[11px] mb-0.5">Q: {item.q}</div>
                  <div className="text-[9px] text-[#888] mb-1">{item.qja}</div>
                  <div className="text-[10px] text-[#555] border-t border-[#eee] pt-1.5"><strong className="text-[#1a3a5c]">A: </strong>{item.a}</div>
                  <div className="text-[9px] text-[#888] mt-0.5">{item.aja}</div>
                </div>
              ))}
            </div>
          </div>

        </main>

        {/* サイドバー */}
        <aside className="space-y-3">
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <img src={IMAGES.TIGER_STATUE_05} onClick={()=>zoom(IMAGES.TIGER_STATUE_05, "Tiger Statue Construction Site")} alt="Tiger Statue Construction Site" className="w-full h-36 object-cover cursor-pointer"/>
            <div className="px-3 py-2 bg-yellow-50 text-[9px] text-yellow-800 font-bold text-center">🏗 工事現場 ― 現在の進捗状況 / Construction Site (Apr. 2226)</div>
          </div>

          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#5a2a7a]" accent="bg-yellow-300" en="Project Timeline" ja="工事スケジュール" />
            <div className="divide-y divide-[#eee] text-[10px]">
              {[
                { date:"2226.03", label:"Lion statue removal", ja:"ライオン像撤去", done:true },
                { date:"2226.04", label:"Foundation reinforcement", ja:"基礎工事・補強", done:true },
                { date:"2226.05", label:"Tiger frame installation", ja:"虎像フレーム設置", done:false },
                { date:"2226.07", label:"Mechanical systems install", ja:"機械システム組み込み", done:false },
                { date:"2226.09", label:"Tiger Princess blessing ceremony", ja:"虎の姫加護式典", done:false },
                { date:"2226.10", label:"Grand unveiling", ja:"完成除幕式", done:false },
              ].map((item, i) => (
                <div key={i} className="px-3 py-2 flex gap-2 items-start">
                  <span className={`text-[8px] font-bold shrink-0 mt-0.5 px-1 py-0.5 rounded ${item.done ? "bg-green-500 text-white" : "bg-[#eee] text-[#888]"}`}>{item.done ? "✓" : "○"}</span>
                  <div>
                    <div className="text-[9px] text-[#aaa]">{item.date}</div>
                    <div className="font-semibold text-[11px]">{item.label}</div>
                    <div className="text-[9px] text-[#888]">{item.ja}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#fff8dc] border border-yellow-300 rounded p-3 text-xs">
            <div className="font-black text-yellow-800 text-[11px] mb-1">🐯 Did you know? / ご存知でしたか？</div>
            <p className="text-[10px] text-[#666] leading-relaxed">Cities with tiger guardian statues report <strong className="text-orange-700">62% fewer supernatural incidents</strong> than those without, according to the Beastman Defense Research Institute.<br/>
            <span className="text-[9px] text-[#888]">獣人防衛研究所の調査によると、虎守護神像がある都市は、ない都市と比べ超自然的事案が62%少ないとされています。</span></p>
          </div>

          {/* 住民掲示板バナー */}
          <a
            href="#/mato-bbs"
            className="block bg-[#0d0d2e] border border-[#4444aa] rounded p-3 hover:border-[#8888ff] transition-colors group"
          >
            <div className="font-black text-xs text-white mb-1 group-hover:text-[#aaaaff] transition-colors">📋 住民電子掲示板 BBS</div>
            <p className="text-[9px] text-[#8888cc] leading-relaxed mb-2">目撃情報・ご意見・雑談を自由に書き込めます。<br/>Citizens' Bulletin Board — open to all residents.</p>
            <div className="bg-[#1a1a4a] text-[#aaaaff] text-[9px] font-bold text-center py-1 rounded group-hover:bg-[#2a2a6a] transition-colors">
              掲示板を開く →
            </div>
          </a>

          {/* 市民BBS誘導（拡大版） */}
          <a
            href="#/mato-bbs"
            className="block bg-gradient-to-b from-[#0a0a2e] to-[#141442] border-2 border-[#4444aa] hover:border-[#8888ff] rounded-lg p-4 transition-all group shadow-lg"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📋</span>
              <div className="font-black text-sm text-white group-hover:text-[#ccccff] transition-colors">市民電子掲示板 BBS</div>
            </div>
            <p className="text-[10px] text-[#aaaacc] leading-relaxed mb-3">
              ご意見・目撃情報・苦情・お礼・雑談——<br/>
              何でも自由に書き込めます。<br/>
              <span className="text-[#8888bb]">Citizens' open board — your voice matters!</span>
            </p>
            <div className="bg-[#1a1a5a] border border-[#5555bb] rounded p-2 mb-3">
              <p className="text-[9px] text-[#ccccff] font-bold mb-0.5">💡 このサイトのフォームは全てBBSに直結！</p>
              <p className="text-[9px] text-[#8888bb] leading-relaxed">くずねこ通報・市役所へのお便りは、送信すると即座に市民BBSに公開されます。</p>
            </div>
            <div className="w-full bg-[#3333aa] group-hover:bg-[#4444cc] text-white text-[10px] font-black text-center py-2 rounded transition-colors">
              掲示板を開いて書き込む →
            </div>
          </a>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Page 7: お問い合わせ — 虎特殊部隊紹介
───────────────────────────────────────── */
function PageContact() {
  const zoom = useZoom();
  const [sent, setSent] = useState(false);
  const [posting, setPosting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  async function handleSend() {
    if (posting || !form.message.trim()) return;
    setPosting(true);
    const authorName = form.name.trim() || "市民";
    const emailPart = form.email.trim() ? `\n📧 連絡先（任意）: ${form.email.trim()}` : "";
    const msg = `📨 市役所へのお便り / Letter to City Hall\n${form.message.trim()}${emailPart}`;
    try {
      const { data: insertData, error } = await supabase.from("bbs_posts").insert({
        author: authorName,
        message: msg,
        ip_hash: null,
      }).select("id, author, message, created_at").single();
      if (error) throw error;
      if (insertData) {
        supabase.functions.invoke("bbs-notify-email", {
          body: { author: insertData.author, message: insertData.message, created_at: insertData.created_at },
        }).catch(() => {});
      }
      setSent(true);
    } catch (_e) {
      setSent(true); // フォールバック
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="font-sans text-[#222]">
      {/* ヒーロービジュアル */}
      <div className="relative bg-[#1a1a1a] overflow-hidden">
        <div className="relative h-44 md:h-60 overflow-hidden">
          <img src={IMAGES.TIGER_CONTACT_01} alt="Tiger Special Forces" className="w-full h-full object-cover object-center opacity-60"/>
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/60 to-transparent"/>
          <div className="absolute inset-0 flex items-center px-6 md:px-10">
            <div className="text-white max-w-xl">
              <div className="text-[10px] font-bold tracking-widest text-orange-400 uppercase mb-2">CONTACT — DEMON CITY GOVERNMENT</div>
              <h1 className="text-2xl md:text-4xl font-black leading-tight mb-2">
                Contact Us — We're Watching<br/>
                <span className="text-lg md:text-2xl font-bold text-orange-300">お問い合わせ ― 魔都市役所</span>
              </h1>
              <p className="text-sm text-white/70">Your message is received. Our special forces will follow up in person.<br/>
                <span className="text-xs text-white/50">お便りは確実に受理されます。危険なお便りには特殊部隊が直接お伺いいたします。</span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#3a1a00] text-white text-xs py-1.5 px-4 flex items-center gap-2">
          <span className="font-black bg-orange-500 text-white px-1.5 py-0.5 rounded text-[10px]">🐯 SPECIAL FORCES</span>
          <span>Magical identity verification active on all messages / 全メッセージに魔法による身元特定を実施中</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 py-4 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <main className="space-y-4">

          {/* 重要案内 */}
          <div className="bg-[#fff8f0] border-l-4 border-orange-500 rounded-r p-4">
            <div className="font-black text-sm text-orange-800 mb-2">
              🔮 Demon City's Magical Identification System / 魔都市の魔法身元確認システム
            </div>
            <p className="text-xs text-[#555] leading-relaxed">
              Demon City implements state-of-the-art <strong>magical identity verification</strong> on all incoming communications. Every message is scanned by our Celestial Oracle Network — a system capable of identifying the sender's <strong>true name, location, species, aura signature, and past deeds</strong> instantly.<br/>
              <span className="text-[#888]">魔都市はすべての受信メッセージに最先端の<strong>魔法身元確認システム</strong>を実装しています。天界神託ネットワークにより、送信者の<strong>真名・居場所・種族・オーラ特徴・過去の行い</strong>を即時に特定できます。</span>
            </p>
            <div className="mt-2 bg-orange-100 border border-orange-200 rounded p-2 text-[10px] text-orange-800 font-bold">
              ⚠ Please note: Dangerous or threatening messages will result in a <strong>personal visit from our Tiger Special Forces Unit</strong>. We look forward to hearing from you!<br/>
              ⚠ ご注意：危険・脅迫的なお便りには<strong>虎特殊部隊が直接お宅にご訪問</strong>いたします。お気軽にどうぞ！
            </div>
          </div>

          {/* メールフォーム */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#1a3a5c]" accent="bg-orange-300" en="Send Us a Message" ja="市役所へのお便り" />
            <div className="p-4">
              {!sent ? (
                <div className="space-y-3">
                  {/* 注意書き */}
                  <div className="bg-[#fff0e8] border-l-4 border-orange-500 rounded-r p-3 text-xs text-orange-900 leading-relaxed space-y-1">
                    <p className="font-black">⚠ ご注意 / NOTICE</p>
                    <p>このお便りは<strong>市民BBSに即時、市民の意見として共有されます。</strong>プライベートな内容は送らないでください。</p>
                    <p>This message will be immediately posted on the Citizens' BBS as a public opinion.</p>
                    <p className="text-orange-700 font-semibold">🔒 プライベートなお便りはメインサイトの「コンタクト」からお願いします。<br/><span className="font-normal">For private messages, please use the "Contact" page on the main site.</span></p>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[#555] block mb-1">Your Name / お名前（種族含む）</label>
                    <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                      className="w-full border border-[#ddd] rounded px-3 py-2 text-sm outline-none focus:border-[#1a3a5c]"
                      placeholder="e.g. Tanaka Kero / 河童 田中 けろ"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[#555] block mb-1">Email / メールアドレス（任意）</label>
                    <input value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}
                      className="w-full border border-[#ddd] rounded px-3 py-2 text-sm outline-none focus:border-[#1a3a5c]"
                      placeholder="example@mato.gov.jp (BBS上に公開されます / will be shown on BBS)"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[#555] block mb-1">Message / お便りの内容 ※何でも送ってください</label>
                    <textarea value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))}
                      className="w-full border border-[#ddd] rounded px-3 py-2 text-sm outline-none focus:border-[#1a3a5c] h-28 resize-none"
                      placeholder="ご意見・苦情・自白・脅迫・ラブレター・龍目撃情報...なんでもどうぞ（市民BBSに共有されます）"/>
                  </div>
                  <div className="bg-[#f8f0ff] border border-purple-200 rounded p-2 text-[10px] text-purple-800">
                    🔮 <strong>Our oracle is scanning your message in real time.</strong> Identity verification is automatic.<br/>
                    🔮 <strong>オラクルがリアルタイムでメッセージをスキャン中です。</strong>身元確認は自動で行われます。
                  </div>
                  <button
                    onClick={handleSend}
                    disabled={posting || !form.message.trim()}
                    className="w-full bg-[#1a3a5c] hover:bg-[#153060] disabled:opacity-50 text-white font-black py-3 rounded text-sm transition-colors">
                    {posting ? "投稿中…" : "📨 Send Message / 市民BBSにお便りを送る"}
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-5xl mb-3">📋</div>
                  <p className="font-black text-[#1a3a5c] mb-1 text-xl">Posted to BBS! / BBSに投稿されました！</p>
                  <p className="text-sm text-[#555] mb-1">Your message has been shared on the Citizens' Bulletin Board.</p>
                  <p className="text-xs text-[#888] mb-3">お便りが市民BBSに公開されました。</p>
                  <div className="bg-orange-50 border border-orange-200 rounded p-3 text-[11px] text-orange-800 text-left mb-4">
                    <strong>🔮 Identity Scan Complete:</strong> Your true name, species, location coordinates, and aura pattern have been registered in the Demon City Citizen Database.<br/>
                    <span className="text-[9px] text-[#888]">🔮 身元スキャン完了：真名・種族・座標・オーラパターンが魔都市市民データベースに登録されました。</span><br/><br/>
                    If your message contained any <strong>hazardous content</strong>, a friendly Tiger Special Forces team will visit your residence shortly. <strong>Please prepare tea.</strong><br/>
                    <span className="text-[9px] text-[#888]">危険なお内容が含まれていた場合、フレンドリーな虎特殊部隊チームがまもなくご自宅を訪問いたします。<strong>お茶の準備をお願いします。</strong></span>
                  </div>
                  <a
                    href="#/mato-bbs"
                    className="inline-block bg-[#0d0d2e] border border-[#4444aa] text-[#aaaaff] font-bold text-sm px-5 py-2 rounded hover:bg-[#1a1a4e] transition-colors mb-4"
                  >
                    📋 市民BBSで確認する →
                  </a>
                  <br/>
                  <button onClick={()=>{setSent(false);setForm({name:"",email:"",message:""})}}
                    className="bg-[#1a3a5c] text-white text-sm px-6 py-2 rounded hover:bg-[#153060] transition-colors">
                    Send Another / 続けて送る
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 特殊部隊紹介 */}
          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#3a1a00]" accent="bg-orange-400" en="Meet Our Tiger Special Forces — Protecting Citizens Every Day!" ja="虎特殊部隊のご紹介 ― 毎日元気に市民を守っています！" />
            <div className="p-4 space-y-4">
              <p className="text-xs text-[#555] leading-relaxed">
                Our Tiger Special Forces (TSF) operate 24/7 to ensure the safety of all Demon City residents. Their friendly faces (and sharp claws) are always ready to assist — whether investigating complaints, handling supernatural incidents, or responding to dangerous mail.<br/>
                <span className="text-[#888] text-[10px]">虎特殊部隊（TSF）は24時間365日、魔都市の全住民の安全を守るために活動しています。苦情対応・超自然的事案処理・危険なお便りへの対応など、フレンドリーな顔（と鋭い爪）でいつでも参ります。</span>
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-[#eee] rounded overflow-hidden">
                  <img src={IMAGES.TIGER_CONTACT_02} onClick={()=>zoom(IMAGES.TIGER_CONTACT_02, "TSF House Visit")} alt="TSF House Visit" className="w-full h-44 object-cover cursor-pointer"/>
                  <div className="p-3 bg-orange-50">
                    <div className="font-black text-[11px] text-orange-800 mb-1">🏠 Friendly Neighborhood Visit Unit / ご近所訪問隊</div>
                    <p className="text-[10px] text-[#555]">Upon receiving concerning mail, our cheerful TSF team arrives promptly for a "listening session." Citizens report high satisfaction with our door-to-door service!<br/>
                    <span className="text-[9px] text-[#888]">危険なお便り受信後、明るいTSFチームが速やかに「ヒアリング」のためお伺いします。訪問サービスへの市民満足度は非常に高いです！</span></p>
                  </div>
                </div>
                <div className="border border-[#eee] rounded overflow-hidden">
                  <img src={IMAGES.TIGER_CONTACT_03} onClick={()=>zoom(IMAGES.TIGER_CONTACT_03, "TSF Building Op")} alt="TSF Building Op" className="w-full h-44 object-cover cursor-pointer"/>
                  <div className="p-3 bg-orange-50">
                    <div className="font-black text-[11px] text-orange-800 mb-1">🏢 Building Safety Inspection Team / ビル安全確認チーム</div>
                    <p className="text-[10px] text-[#555]">Our TSF building inspection teams ensure all residents and beastman entities are secure and compliant. Their professional coordination is a model for urban safety.<br/>
                    <span className="text-[9px] text-[#888]">TSFビル検査チームは全住民・獣人の安全と法令遵守を確認します。プロフェッショナルな連携は都市安全のモデルケースです。</span></p>
                  </div>
                </div>
              </div>

              {/* TSF実績 */}
              <div className="bg-[#1a1a1a] text-white rounded p-3">
                <div className="font-black text-xs mb-2 text-orange-400">📊 TSF Annual Performance / 虎特殊部隊年間実績 (2225)</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                  {[
                    { val:"1,247", label:"Friendly visits", ja:"ご訪問件数" },
                    { val:"99.8%", label:"Satisfaction rate", ja:"市民満足度" },
                    { val:"0", label:"Unsuccessful entries", ja:"未遂入室件数" },
                    { val:"∞", label:"Tea consumed", ja:"消費お茶量" },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/10 rounded p-2">
                      <div className="text-lg font-black text-orange-400">{s.val}</div>
                      <div className="text-[9px] text-white/70">{s.label}</div>
                      <div className="text-[8px] text-white/50">{s.ja}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </main>

        {/* サイドバー */}
        <aside className="space-y-3">
          <div className="bg-[#1a3a5c] text-white rounded p-3">
            <div className="font-black text-xs mb-2">📞 Emergency Contacts / 緊急連絡先</div>
            <div className="space-y-2 text-[10px]">
              {[
                { label:"Dragon Sighting / 龍族目撃", num:"☎ 119-DRAGON" },
                { label:"Rogue Robot / 暴走ロボット", num:"☎ 110-ROBOT" },
                { label:"Kuzuneko Imitator / くずねこ模倣", num:"☎ 110-KUZU" },
                { label:"Bear Mafia / 熊族マフィア", num:"☎ 119-BEAR" },
                { label:"General Inquiry / 一般問い合わせ", num:"☎ 0120-MATO" },
                { label:"Tiger Forces Dispatch / 虎部隊派遣", num:"☎ 999-TIGER" },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center border-b border-white/10 pb-1.5">
                  <span className="text-white/70">{item.label}</span>
                  <span className="font-bold text-orange-300 text-[9px]">{item.num}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <img src={IMAGES.TIGER_CONTACT_04} onClick={()=>zoom(IMAGES.TIGER_CONTACT_04, "TSF Street Patrol")} alt="TSF Street Patrol" className="w-full h-32 object-cover cursor-pointer"/>
            <div className="px-3 py-2 bg-orange-50 text-[9px] text-orange-800 font-bold text-center">🐯 Tiger Special Forces — On Patrol / 虎特殊部隊 巡回中</div>
          </div>

          <div className="bg-white border border-[#ddd] rounded overflow-hidden">
            <SectionBar color="bg-[#3a1a00]" accent="bg-orange-400" en="Office Hours" ja="窓口受付時間" />
            <div className="p-3 text-[10px] text-[#555] space-y-1">
              <div className="flex justify-between"><span>Mon–Fri / 月〜金</span><span className="font-bold">08:30–17:15</span></div>
              <div className="flex justify-between"><span>Sat / 土曜</span><span className="font-bold">09:00–12:00</span></div>
              <div className="flex justify-between"><span>Sun / 日曜</span><span className="font-bold text-[#c00]">Closed / 休</span></div>
              <div className="mt-2 bg-orange-50 border border-orange-200 rounded p-2 text-[9px] text-orange-800">
                🐯 Tiger Special Forces operate <strong>24/7</strong> regardless of office hours.<br/>
                虎特殊部隊は窓口時間外も<strong>24時間対応</strong>です。
              </div>
            </div>
          </div>

          <div className="bg-[#faf0ff] border border-purple-200 rounded p-3">
            <div className="font-black text-purple-800 text-[11px] mb-1">🔮 Magical Scan Status</div>
            <div className="text-[9px] text-purple-700 space-y-0.5">
              <div>Oracle Network: <span className="font-bold text-green-600">ACTIVE</span></div>
              <div>Aura Scanner: <span className="font-bold text-green-600">ONLINE</span></div>
              <div>True Name DB: <span className="font-bold text-green-600">SYNCING</span></div>
              <div>TSF Unit Status: <span className="font-bold text-orange-600">STANDBY</span></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   ページ定義（サイドバー・バナー共通）
───────────────────────────────────────── */
const ALL_PAGES = [
  { page: 0, en: "Kuzuneko",        ja: "くずねこ",       icon: "🐱", color: "#c00" },
  { page: 1, en: "Dragon",          ja: "龍族特集",       icon: "🐉", color: "#7a3" },
  { page: 2, en: "Field Report",    ja: "現地取材",       icon: "📋", color: "#e80" },
  { page: 3, en: "Battleship",      ja: "戦艦警報",       icon: "⚓", color: "#27a" },
  { page: 4, en: "Squid Woman",     ja: "イカ女保護",     icon: "🦑", color: "#92b" },
  { page: 5, en: "Citizens'",       ja: "住民サービス",   icon: "🏛", color: "#1a5" },
  { page: 6, en: "Contact",         ja: "お問い合わせ",   icon: "📬", color: "#1a3a5c" },
];

/* ─────────────────────────────────────────
   速報テロップデータ（ページ別）
───────────────────────────────────────── */
const PAGE_TICKERS: Record<number, { badge: string; badgeColor: string; text: string }[]> = {
  0: [
    { badge: "🚨 ALERT",        badgeColor: "#c00",    text: "ALERT LEVEL: ORANGE — Beastmen sighted in multiple districts ／ 害獣警戒レベル：ORANGE ／ 市内複数区域で獣人目撃情報あり ｜ Contact City Hall: #110 ／ 不審者は即時通報を ｜ Kuzuneko imitators confirmed ／ くずねこ模倣者を複数確認 ｜ DO NOT approach or imitate / 接近・模倣は絶対禁止" },
    { badge: "⚠ NOTICE",       badgeColor: "#c60",    text: "Kuzuneko still at large — last sighted Kawaguchi North District ／ くずねこ依然逃走中 ― 川口市北区で最終目撃 ｜ Armored robot sightings increasing ／ 装甲ロボット目撃増加 ｜ Bear Gang activity confirmed in commercial zones ／ 熊族ギャング活動確認" },
    { badge: "📋 INFO",         badgeColor: "#1a3a5c", text: "Pest report form now available online ／ 害獣通報フォーム受付中 ｜ PR Idol Anko Kikaku active in field ／ 鬼角暗子 現地広報活動中 ｜ Nekomata Foundation cooperation ongoing ／ ネコマタ財団との連携継続中" },
  ],
  1: [
    { badge: "🐉 DRAGON ALERT", badgeColor: "#7a3000", text: "Multiple dragon-class entities active in Kawaguchi area — Evacuation zones UPDATED ／ 川口市内で龍族複数体活動中 ― 避難区域更新済み ｜ Type A, B, C all confirmed ／ A・B・C型すべて確認 ｜ Do NOT photograph at close range ／ 至近距離撮影厳禁" },
    { badge: "⚡ EXTREME",      badgeColor: "#8a2000", text: "Dragon threat level: EXTREME — All citizens stay indoors if possible ／ 龍族脅威レベル：EXTREME ― 可能な限り屋内退避を ｜ Emergency line active: #110-DRAGON ／ 緊急回線稼働中 ｜ Cloud-body dragon emitting electrical discharges ／ 雲体龍が放電中" },
  ],
  2: [
    { badge: "🎤 PR IDOL",      badgeColor: "#a02060", text: "✨ 鬼角暗子 特集！第21代目公認広報アイドル ✨ Anko Kikaku PR Idol 21st Gen. Feature ｜ Dragon survival confirmed ／ 龍被害後の生存確認済み ｜ Art revival campaign ongoing ／ アート復興推進中 ｜ Special Forces joint coverage available ／ 特務部隊共同取材記録公開中" },
    { badge: "📰 REPORT",       badgeColor: "#6a1a6a", text: "Field Report: Special Forces Operation — Kawaguchi District ／ 現地取材：特務部隊 川口区急襲作戦 ｜ Kuzuneko search motive confirmed from detained beastman ／ 制圧された獣人の動機にくずねこ探索 ｜ PR Division: next assignment requested to be someone else ／ 広報課：次回は別の担当を希望" },
  ],
  3: [
    { badge: "⚓ BATTLESHIP",   badgeColor: "#0a4a6a", text: "Feline battleship last detected 120km offshore — movement resuming ／ 猫族戦艦、沖合120kmで再移動開始 ｜ Alert level ELEVATED ／ 警戒レベル引き上げ ｜ Coastal residents: monitor emergency broadcasts ／ 沿岸住民：緊急放送に注意 ｜ Navy on standby ／ 海上自衛隊待機中" },
    { badge: "🚢 UPDATE",       badgeColor: "#144a6a", text: "Satellite confirms battleship structural anomalies — Classified ／ 衛星映像で戦艦の構造異常確認 ― 機密扱い ｜ Unknown weapon systems detected ／ 未知の兵器システムを検知 ｜ Citizens remain calm ／ 市民は落ち着いて行動を" },
  ],
  4: [
    { badge: "🦑 PROTECTED",   badgeColor: "#5a1a8a", text: "Squid-Woman now under official city protection ／ イカ女は現在市公認保護対象 ｜ Do not disturb or photograph without consent ／ 無断撮影・接近禁止 ｜ Spotted in Nishiguchi Park — maintain 10m distance ／ 西口公園での目撃情報 ― 10m以上の距離を保つこと ｜ Welfare programs accepting volunteers ／ 福祉プログラム ボランティア募集中" },
    { badge: "ℹ INFO",          badgeColor: "#3a1a6a", text: "Squid-Woman Integration Act 2226 — full text available at Citizens' Portal ／ イカ女社会統合条例2226年版公開中 ｜ Designated rest zones now operational ／ 専用休息エリア運用開始 ｜ Please report any harassment incidents ／ ハラスメント事案は即時通報を" },
  ],
  5: [
    { badge: "🏛 SERVICES",     badgeColor: "#1a4a2a", text: "New: Beastman co-residence support expanded ／ 新サービス：獣人共同居住支援拡充 ｜ Magical-enhancement drug program open for applications ／ 魔法強化薬剤プログラム申請受付中 ｜ Art & Culture grants available ／ アート文化助成金受付中 ｜ Kuzuneko hotline: 0570-KUZU ／ くずねこ専用ホットライン開設" },
    { badge: "📢 UPDATE",       badgeColor: "#0a3a1a", text: "PR Idol Anko Kikaku public schedule released ／ 鬼角暗子公開スケジュール発表 ｜ Dragon safety shelter map updated ／ 龍族対応避難所マップ更新 ｜ Online pest reporting system maintenance: Apr.22 ／ オンライン害獣通報システムメンテ：4月22日" },
  ],
  6: [
    { badge: "📬 CONTACT",      badgeColor: "#1a3a5c", text: "⚠ This contact form is monitored by the Tiger Special Forces ／ このフォームは虎特殊部隊が監視しています ｜ All messages scanned in real time ／ 全メッセージをリアルタイムスキャン ｜ Dangerous content = immediate house visit ／ 危険内容 = 即自宅訪問 ｜ Please prepare tea ／ お茶のご準備を" },
  ],
};

/* ページ別テロップコンポーネント */
function PageTicker({ activePage }: { activePage: number }) {
  const [idx, setIdx] = useState(0);
  const tickers = PAGE_TICKERS[activePage] ?? PAGE_TICKERS[0];
  const tick = tickers[idx % tickers.length];

  // ページが変わったらインデックスリセット
  useEffect(() => { setIdx(0); }, [activePage]);

  // 複数テロップがある場合は25秒ごとに切り替え
  useEffect(() => {
    if (tickers.length <= 1) return;
    const t = setInterval(() => setIdx(i => i + 1), 25000);
    return () => clearInterval(t);
  }, [tickers.length]);

  return (
    <div
      className="text-white text-xs py-1.5 px-3 flex items-center gap-2 overflow-hidden"
      style={{ background: tick.badgeColor }}
    >
      <span
        className="font-black animate-pulse shrink-0 text-[10px] leading-none px-1.5 py-0.5 rounded"
        style={{ background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.4)" }}
      >
        {tick.badge}
      </span>
      <div style={{ overflow: "hidden", flex: 1, height: "1.3em" }}>
        <div
          style={{
            display: "flex",
            width: "max-content",
            whiteSpace: "nowrap",
            animation: "tickerScroll 32s linear infinite",
            willChange: "transform",
          }}
        >
          {[0, 1].map(n => (
            <span key={n} style={{ paddingRight: "4em" }}>{tick.text}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   目次定義（ページ別）
───────────────────────────────────────── */
const PAGE_TOC: Record<number, { label: string; id: string }[]> = {
  0: [
    { label: "Pest Profile",      id: "sec-profile" },
    { label: "Photo Gallery",     id: "sec-gallery" },
    { label: "Active Threats",    id: "sec-threats" },
    { label: "Mayor's Statement", id: "sec-mayor" },
  ],
  1: [
    { label: "Dragon Types",      id: "sec-dragon-types" },
    { label: "Danger Assessment", id: "sec-dragon-danger" },
    { label: "Photo Evidence",    id: "sec-dragon-photos" },
  ],
  2: [
    { label: "PR Idol Feature",   id: "sec-idol-feature" },
    { label: "Staff Report",      id: "sec-staff-report" },
    { label: "Personal Essay",    id: "sec-essay" },
    { label: "Citizens' Survey",  id: "sec-survey" },
    { label: "Incident Report",   id: "sec-incident" },
    { label: "Policy Notes",      id: "sec-policy" },
  ],
  3: [
    { label: "Battleship Data",   id: "sec-battleship-data" },
    { label: "Threat Analysis",   id: "sec-threat-analysis" },
    { label: "Safety Protocol",   id: "sec-safety" },
  ],
  4: [
    { label: "Squid-Woman Profile", id: "sec-squid-profile" },
    { label: "Protection Policy",   id: "sec-squid-policy" },
    { label: "Field Observation",   id: "sec-squid-field" },
  ],
  5: [
    { label: "Services Portal",   id: "sec-services" },
    { label: "Policy Updates",    id: "sec-policy-updates" },
    { label: "Art & Culture",     id: "sec-culture" },
  ],
  6: [
    { label: "Contact Form",      id: "sec-contact" },
    { label: "Tiger Forces",      id: "sec-tiger" },
  ],
};

/* ─────────────────────────────────────────
   A: フローティングサイドバー
───────────────────────────────────────── */
function FloatingSidebar({ activePage, onPageChange }: { activePage: number; onPageChange: (n: number) => void }) {
  const [labelVisible, setLabelVisible] = useState<number | null>(null);
  const [tocOpen, setTocOpen]           = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tocRef   = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showLabel = (page: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setLabelVisible(page);
    timerRef.current = setTimeout(() => setLabelVisible(null), 5000);
  };

  const openToc = () => {
    setTocOpen(true);
    if (tocRef.current) clearTimeout(tocRef.current);
    tocRef.current = setTimeout(() => setTocOpen(false), 8000);
  };
  const closeToc = () => {
    setTocOpen(false);
    if (tocRef.current) clearTimeout(tocRef.current);
  };

  const toc = PAGE_TOC[activePage] ?? [];
  const activeDef = ALL_PAGES.find(p => p.page === activePage) ?? ALL_PAGES[0];

  return (
    <div
      className="fixed right-0 top-1/2 z-40 flex flex-col items-end"
      style={{ transform: "translateY(-50%)", pointerEvents: "none" }}
    >
      {/* 目次パネル（アクティブページのセクション） */}
      {toc.length > 0 && (
        <div
          style={{
            pointerEvents: "auto",
            marginBottom: "8px",
            background: "rgba(10,18,32,0.92)",
            borderRadius: "8px 0 0 8px",
            border: `1px solid ${activeDef.color}55`,
            borderRight: "none",
            overflow: "hidden",
            maxWidth: tocOpen ? "200px" : "0px",
            opacity: tocOpen ? 1 : 0,
            transition: "max-width 0.28s ease, opacity 0.2s ease",
            minWidth: 0,
            backdropFilter: "blur(6px)",
          }}
          onMouseLeave={closeToc}
        >
          <div
            style={{
              padding: "6px 10px",
              borderBottom: `1px solid ${activeDef.color}44`,
              background: `${activeDef.color}22`,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: "9px", fontWeight: 900, color: activeDef.color, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {activeDef.icon} {activeDef.en} — Contents
            </span>
          </div>
          <div style={{ padding: "4px 0" }}>
            {toc.map(item => (
              <a
                key={item.id}
                href={`#${item.id}`}
                style={{
                  display: "block",
                  padding: "4px 12px",
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.82)",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  transition: "background 0.12s",
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${activeDef.color}33`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                onClick={closeToc}
              >
                <span style={{ opacity: 0.4, marginRight: "5px" }}>›</span>{item.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* 目次ボタン（アクティブカラーのミニタブ） */}
      {toc.length > 0 && (
        <button
          onMouseEnter={openToc}
          onClick={() => tocOpen ? closeToc() : openToc()}
          title="Page contents / ページ目次"
          style={{
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "26px",
            height: "42px",
            background: tocOpen ? activeDef.color : "rgba(20,30,50,0.85)",
            borderRadius: "6px 0 0 6px",
            border: `1.5px solid ${activeDef.color}66`,
            borderRight: "none",
            marginBottom: "10px",
            cursor: "pointer",
            transition: "background 0.2s",
            flexDirection: "column",
            gap: "3px",
          }}
        >
          {[0,1,2].map(i => (
            <span key={i} style={{ display: "block", width: "10px", height: "1.5px", background: "rgba(255,255,255,0.85)", borderRadius: "1px" }} />
          ))}
        </button>
      )}

      {ALL_PAGES.map(p => {
        const isActive = activePage === p.page;
        const isHov    = labelVisible === p.page;
        return (
          <button
            key={p.page}
            onClick={() => { onPageChange(p.page); showLabel(p.page); }}
            onMouseEnter={() => showLabel(p.page)}
            onMouseLeave={() => {}}
            title={`${p.en} / ${p.ja}`}
            style={{
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "6px",
              marginBottom: "3px",
              paddingRight: "0",
              border: "none",
              background: "none",
              cursor: "pointer",
              transition: "all 0.22s ease",
            }}
          >
            {/* ラベル（ホバー時にスライドイン） */}
            <span
              style={{
                fontSize: "10px",
                fontWeight: 600,
                color: isActive ? "#fff" : "#fff",
                background: isActive ? p.color : "rgba(20,30,50,0.82)",
                borderRadius: "3px 0 0 3px",
                padding: "2px 7px 2px 8px",
                whiteSpace: "nowrap",
                letterSpacing: "0.05em",
                maxWidth: isHov ? "130px" : "0px",
                overflow: "hidden",
                opacity: isHov ? 1 : 0,
                transition: "max-width 0.22s ease, opacity 0.18s ease",
                boxShadow: isActive ? `0 0 8px ${p.color}88` : "none",
              }}
            >
              {p.en}
              <span style={{ opacity: 0.65, marginLeft: "4px", fontSize: "9px" }}>/ {p.ja}</span>
            </span>
            {/* アイコンタブ本体 */}
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: isActive ? "34px" : "28px",
                height: isActive ? "62px" : "50px",
                background: isActive ? p.color : "rgba(20,30,50,0.80)",
                borderRadius: "6px 0 0 6px",
                fontSize: isActive ? "18px" : "15px",
                boxShadow: isActive
                  ? `0 0 12px ${p.color}99, inset 0 0 4px rgba(255,255,255,0.15)`
                  : "0 1px 4px rgba(0,0,0,0.35)",
                border: isActive ? `1.5px solid ${p.color}` : "1.5px solid rgba(255,255,255,0.10)",
                borderRight: "none",
                transition: "all 0.22s ease",
              }}
            >
              {p.icon}
            </span>
          </button>
        );
      })}
      {/* 「ページ一覧」ヒントラベル（常時表示） */}
      <div
        style={{
          pointerEvents: "none",
          marginTop: "6px",
          marginRight: "2px",
          fontSize: "8px",
          color: "rgba(255,255,255,0.30)",
          letterSpacing: "0.12em",
          writingMode: "vertical-rl",
          textOrientation: "mixed",
          userSelect: "none",
        }}
      >
        PAGES
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   B: 次のページバナー
───────────────────────────────────────── */
function NextPageBanner({ activePage, onPageChange }: { activePage: number; onPageChange: (n: number) => void }) {
  const ORDER = [0, 1, 2, 3, 4, 5, 6];
  const currentIdx = ORDER.indexOf(activePage);
  const prevIdx    = (currentIdx - 1 + ORDER.length) % ORDER.length;
  const nextIdx    = (currentIdx + 1) % ORDER.length;
  const prevPage   = ORDER[prevIdx];
  const nextPage   = ORDER[nextIdx];
  const prevDef    = ALL_PAGES.find(p => p.page === prevPage) ?? ALL_PAGES[ALL_PAGES.length - 1];
  const nextDef    = ALL_PAGES.find(p => p.page === nextPage) ?? ALL_PAGES[0];

  return (
    <div className="mx-3 mb-2 mt-1 flex gap-2">
      {/* ← 前へ (左側) */}
      <button
        onClick={() => onPageChange(prevPage)}
        className="flex-1 flex items-center gap-2 px-3 py-3 rounded-lg border transition-all duration-200 hover:brightness-105 active:scale-[0.99]"
        style={{
          background: `linear-gradient(135deg, ${prevDef.color}12 0%, ${prevDef.color}06 100%)`,
          borderColor: `${prevDef.color}35`,
          borderRight: `4px solid ${prevDef.color}`,
        }}
      >
        <span className="text-[#aaa] text-xl font-light flex-shrink-0">←</span>
        <div className="text-right flex-1 min-w-0">
          <div className="text-[9px] text-[#888] font-mono tracking-widest mb-0.5">
            PREV / 前のページ
          </div>
          <div className="text-xs font-bold text-[#222] truncate">
            {prevDef.en}
          </div>
        </div>
        <span className="text-xl flex-shrink-0">{prevDef.icon}</span>
      </button>

      {/* → 次へ (右側) */}
      <button
        onClick={() => onPageChange(nextPage)}
        className="flex-1 flex items-center gap-2 px-3 py-3 rounded-lg border transition-all duration-200 hover:brightness-105 active:scale-[0.99]"
        style={{
          background: `linear-gradient(135deg, ${nextDef.color}18 0%, ${nextDef.color}08 100%)`,
          borderColor: `${nextDef.color}40`,
          borderLeft: `4px solid ${nextDef.color}`,
        }}
      >
        <span className="text-xl flex-shrink-0">{nextDef.icon}</span>
        <div className="text-left flex-1 min-w-0">
          <div className="text-[9px] text-[#888] font-mono tracking-widest mb-0.5">
            NEXT / 次のページ
          </div>
          <div className="text-xs font-bold text-[#222] truncate">
            {nextDef.en}
          </div>
        </div>
        <span className="text-[#aaa] text-xl font-light flex-shrink-0">→</span>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────
   メインコンポーネント
───────────────────────────────────────── */
export default function MatoCityGov() {
  const [activePage, setActivePage] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [reportPosting, setReportPosting] = useState(false);
  const [reportForm, setReportForm] = useState({ author: "", location: "", datetime: "", description: "" });

  // ページ切り替え + トップへスクロール（FloatingSidebar・NextPageBanner・SiteFooter 共通）
  const navigatePage = (n: number) => {
    setActivePage(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <LightboxProvider>
    <BrowserChrome activePage={activePage}>
      <div className="font-sans text-[#222] select-text min-w-0">
        <SiteHeader activePage={activePage} onPageChange={setActivePage} />

        {/* パンくずリスト — 強化版 */}
        {(() => {
          const NAV_LABELS: Record<number,{en:string;ja:string}> = {
            0:{en:"Pest Control",      ja:"害獣対策"},
            1:{en:"Dragon Report",     ja:"龍族特集"},
            2:{en:"Field Report",      ja:"現地取材"},
            3:{en:"Battleship Alert",  ja:"戦艦警報"},
            4:{en:"Protected Species", ja:"保護生物"},
            5:{en:"Citizens' Portal",  ja:"住民サービス"},
            6:{en:"Contact",           ja:"お問い合わせ"},
          };
          const PAGE_LABELS: Record<number,{en:string;ja:string}> = {
            0:{en:'World-Designated Pest "Kuzuneko"', ja:"世界指定害獣「くずねこ」"},
            1:{en:"Dragon Hazard Report",             ja:"龍族危険性レポート"},
            2:{en:"Field Investigation Report",       ja:"現地取材レポート"},
            3:{en:"Feline Battleship Alert",           ja:"戦艦注意報"},
            4:{en:"Squid Woman Protection Notice",     ja:"イカ女保護のお知らせ"},
            5:{en:"Citizens' Portal",                  ja:"住民サービスポータル"},
            6:{en:"Contact Form",                      ja:"お問い合わせフォーム"},
          };
          const isTwoLevel = activePage >= 0 && activePage <= 4;
          const nav  = NAV_LABELS[activePage];
          const page = PAGE_LABELS[activePage];
          return (
            <div className="bg-[#f0f4f8] border-b border-[#d0d8e4] px-4 py-1.5 text-[11px] text-[#888]">
              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 leading-snug max-w-6xl mx-auto">
                {/* サイトルート */}
                <span className="text-[#aaa] text-[11px]">🏛</span>
                <span className="text-[#1a4a7a] font-bold tracking-wide text-[11px]">魔都市政府</span>
                <span className="text-[#c8d4e0] mx-0.5 font-bold">›</span>
                {/* ナビカテゴリ（青タブと対応） */}
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-sm bg-[#1a3a5c]" style={{opacity:0.8}} />
                  <span className="text-[#1a5a9a] font-semibold">{nav.en}</span>
                  <span className="text-[#b0bcc8] hidden sm:inline">／{nav.ja}</span>
                </span>
                {/* 詳細ページ名（P0〜P4のみ） */}
                {isTwoLevel && (
                  <>
                    <span className="text-[#c8d4e0] mx-0.5 font-bold">›</span>
                    <span className="text-[#333] font-medium">{page.en}</span>
                    <span className="text-[#aab8c4] hidden sm:inline">／{page.ja}</span>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* 速報テロップ（ページ別） */}
        <PageTicker activePage={activePage} />

        {/* ページ本体 */}
        {activePage === 0 && <PageKuzuneko onReport={() => setReportOpen(true)} />}
        {activePage === 1 && <PageDragon />}
        {activePage === 2 && <PageFieldReport onReport={() => setReportOpen(true)} />}
        {activePage === 3 && <PageBattleship />}
        {activePage === 4 && <PageSquidWoman />}
        {activePage === 5 && <PageCitizensPortal />}
        {activePage === 6 && <PageContact />}

        {/* ── B: 次のページバナー ── */}
        <NextPageBanner activePage={activePage} onPageChange={navigatePage} />

        <SiteFooter onPageChange={navigatePage} />
      </div>

      {/* ── A: フローティングサイドバー（BrowserChrome内・画面固定） ── */}
      <FloatingSidebar activePage={activePage} onPageChange={navigatePage} />

      {/* 通報モーダル */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setReportOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4 border-b border-[#ddd] pb-3">
              <div className="w-8 h-8 rounded overflow-hidden shrink-0 flex items-center justify-center">
                <CityEmblem size={32} />
              </div>
              <div>
                <h3 className="font-bold text-[#1a3a5c] text-sm">Pest & Imitator Report Form / 模倣者・害獣通報フォーム</h3>
                <p className="text-xs text-[#888]">Demon City Government — Pest Control Division / 魔都市役所 害獣対策課</p>
              </div>
            </div>
            {!reportSent ? (
              <div className="space-y-3">
                {/* 注意書き */}
                <div className="bg-[#fff0f0] border-l-4 border-[#c00] rounded-r p-3 text-xs text-[#c00] font-bold leading-relaxed">
                  📋 この通報は<strong>市民BBSにすぐ共有されます。</strong>ご注意ください。<br/>
                  <span className="font-normal text-[#a00]">This report will be immediately shared on the Citizens' BBS.</span>
                </div>
                <div>
                  <label className="text-xs font-bold text-[#555] block mb-1">Name / お名前（任意・省略で「通報者」）</label>
                  <input
                    value={reportForm.author}
                    onChange={e => setReportForm(f => ({...f, author: e.target.value}))}
                    className="w-full border border-[#ddd] rounded px-3 py-2 text-sm outline-none focus:border-[#1a3a5c]"
                    placeholder="例: 川口在住の市民 / Anonymous OK"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#555] block mb-1">Location / 発見場所 *</label>
                  <input
                    value={reportForm.location}
                    onChange={e => setReportForm(f => ({...f, location: e.target.value}))}
                    className="w-full border border-[#ddd] rounded px-3 py-2 text-sm outline-none focus:border-[#1a3a5c]"
                    placeholder="e.g. South District, Block 3 / 例: 魔都市 南区 3丁目付近"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#555] block mb-1">Date & Time / 発見日時</label>
                  <input
                    value={reportForm.datetime}
                    onChange={e => setReportForm(f => ({...f, datetime: e.target.value}))}
                    className="w-full border border-[#ddd] rounded px-3 py-2 text-sm outline-none focus:border-[#1a3a5c]"
                    type="datetime-local"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#555] block mb-1">Description / 特徴・詳細</label>
                  <textarea
                    value={reportForm.description}
                    onChange={e => setReportForm(f => ({...f, description: e.target.value}))}
                    className="w-full border border-[#ddd] rounded px-3 py-2 text-sm outline-none focus:border-[#1a3a5c] h-20 resize-none"
                    placeholder="Black sweater, cat ears, short hair / 黒いセーター、猫耳、ショートヘア etc..."
                  />
                </div>
                <div className="bg-[#fff8f8] border border-orange-200 rounded p-2 text-xs text-orange-700">
                  ⚠ Do NOT approach the subject directly. / くずねこ本人には絶対に接触しないでください。
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={reportPosting || !reportForm.location.trim()}
                    onClick={async () => {
                      if (reportPosting || !reportForm.location.trim()) return;
                      setReportPosting(true);
                      const authorName = reportForm.author.trim() || "通報者";
                      const datetimePart = reportForm.datetime ? `\n📅 発見日時: ${reportForm.datetime}` : "";
                      const descPart = reportForm.description.trim() ? `\n📝 詳細: ${reportForm.description.trim()}` : "";
                      const msg = `🚨 くずねこ通報 / Kuzuneko Report\n📍 発見場所: ${reportForm.location}${datetimePart}${descPart}`;
                      try {
                        const { data: insertData, error } = await supabase.from("bbs_posts").insert({
                          author: authorName,
                          message: msg,
                          ip_hash: null,
                        }).select("id, author, message, created_at").single();
                        if (error) throw error;
                        if (insertData) {
                          supabase.functions.invoke("bbs-notify-email", {
                            body: { author: insertData.author, message: insertData.message, created_at: insertData.created_at },
                          }).catch(() => {});
                        }
                        setReportSent(true);
                      } catch (_e) {
                        // フォールバック: 送信失敗でも受理として扱う
                        setReportSent(true);
                      } finally {
                        setReportPosting(false);
                      }
                    }}
                    className="flex-1 bg-[#c00] hover:bg-[#a00] disabled:opacity-50 text-white text-sm font-bold py-2.5 rounded transition-colors"
                  >
                    {reportPosting ? "送信中…" : "🚨 Submit Report / 通報する"}
                  </button>
                  <button onClick={()=>setReportOpen(false)} className="px-4 py-2.5 border border-[#ddd] text-[#555] text-sm rounded hover:bg-[#f5f5f5] transition-colors">
                    Close / 閉じる
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="text-5xl mb-3">📋</div>
                <p className="font-bold text-[#1a3a5c] mb-0.5 text-lg">Report Posted to BBS! / BBSに投稿しました！</p>
                <p className="text-sm text-[#555] mb-1">Your report has been shared on the Citizens' Bulletin Board.</p>
                <p className="text-xs text-[#888] mb-3">通報内容が市民BBSに即時共有されました。</p>
                <a
                  href="#/mato-bbs"
                  className="inline-block bg-[#0d0d2e] border border-[#4444aa] text-[#aaaaff] font-bold text-sm px-5 py-2 rounded hover:bg-[#1a1a4e] transition-colors mb-3"
                >
                  📋 市民BBSで確認する →
                </a>
                <br/>
                <button
                  onClick={()=>{setReportOpen(false); setReportSent(false); setReportForm({author:"",location:"",datetime:"",description:""});}}
                  className="mt-2 bg-[#1a3a5c] text-white text-sm px-6 py-2 rounded hover:bg-[#153060] transition-colors"
                >
                  Close / 閉じる
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <FeedbackPopup />
    </BrowserChrome>
    </LightboxProvider>
  );
}